import { SyncService } from "frontend/services/syncService";
import { useCanvasElements } from "frontend/subscriptions";
import {
  arrowKeysToVector,
  CanvasKeyboardShortcut,
  isMac,
  setKey,
  unsetKey,
  useHoldKeys,
  useKeyboardShortcuts,
  useModifierKeys,
} from "frontend/utils/keyboard-shortcuts";
import Konva from "konva";
import { CSSProperties, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Circle, Layer, Line, Rect, Stage } from "react-konva";
import { CordProvider } from "@cord-sdk/react";
import consts, { CANVAS_STAGE_LAYERS, TypeCanvasElement } from "shared/consts";
import { copyElement, ElementDrawProps, NewElementData } from "shared/datamodel/canvas-element";
import { placeConnector } from "shared/datamodel/connector";
import { placeDrawing } from "shared/datamodel/drawing";
import { placeShape } from "shared/datamodel/shape";
import { placeStickyNote } from "shared/datamodel/sticky-note";
import { placeTextBlock } from "shared/datamodel/textBlock";
import { placeTaskCard } from "shared/datamodel/task-card";
import { placeTask } from "shared/datamodel/task";
import { placeCommentThread } from "shared/datamodel/comment-thread";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { updateFrameAttachments, updateSingleConnectorFrameAttachment } from "frontend/utils/frame-move-utils";
import { CanvasElement as CanvasElementType, Flags, LockType, Point } from "shared/datamodel/schemas/canvas-element";
import { Connector } from "shared/datamodel/schemas/connector";
import { combineColorOpacity, getOpacity, parseColor } from "frontend/utils/color-utils";
import { useAccountReps } from "frontend/hooks/use-account-reps";

import { canvasElementPrefix, canvasMetadataPrefix, dbKey, downloadURI, getUnixTimestampUTC } from "shared/util/utils";
import {
  byZIndexAsc,
  canvasPointToNormalizedPoint,
  getBoundingBox,
  getDerivedGroupId,
  getZIndex,
  isDerivedGroupParent,
  isGantt,
  isNormalizedPointInsideElement,
  isOrgchartNode,
  isTableNode,
  measureNode,
} from "frontend/utils/node-utils";
import { CanvasElement } from "./canvas-element";
import { CursorType } from "./cursor-type";
import {
  DistanceToSelectLines_pixels,
  drawPropsForNodes,
  getConnectedConnectors,
  getElementTypeForId,
  getTransformerConfigByElementType,
  isIdOfType,
  isOfTypes,
  newGroupId,
  replaceGroupsAfterCopy,
  prepareNodeForTransformerConfig,
  drawPropsForNode,
  drawUndoPropsForNode,
  getCopiedFrameTitle,
  getElementTypeForNode,
  isGanttSupportedElement,
  isElementInGantt,
} from "./elements/canvas-elements-utils";
import { GhostCanvasElement, GhostElementId } from "./ghost-canvas-element";
import { useBoardValue } from "frontend/state/board-state";
import TemplateGhostPreview from "frontend/canvas-designer-new/template-ghost-preview/template-ghost-preview";
import tracking from "frontend/tracking";
import { KonvaEventObject, Node, NodeConfig } from "konva/types/Node";
import ContextMenuElement, { ContextMenuOps, ContextMenuProps } from "./context-menu-element";
import NextElementMenu, {
  NextElementMenuItem,
  NextElementMenuProps,
} from "./shape-libraries/next-element-menu/next-element-menu";
import { IRect, Vector2d } from "konva/types/types";
import { ShapeSockets } from "./utility-elements";
import { useRequestAnimation } from "frontend/hooks/use-animation";
import * as MathUtils from "frontend/utils/math-utils";
import * as MouseUtils from "frontend/utils/mouse-utils";
import BoundingBox from "frontend/geometry/bounding-box";
import * as PointUtils from "frontend/utils/point-utils";
import * as ConnectorUtils from "frontend/canvas-designer-new/elements/connector/connector-utils";
import * as AnchorUtils from "frontend/canvas-designer-new/elements/connector/anchor-point-utils";
import * as MindmapUtils from "frontend/utils/mindmap-utils";
import * as TextUtils from "frontend/canvas-designer-new/text-element";
import * as connectorConsts from "frontend/canvas-designer-new/elements/connector/connector-constants";
import { prepareCopyForeignImages } from "frontend/utils/file-utils";
import useStateValue from "frontend/state/value";
import AsyncImage from "frontend/ui-components/async-image";
import { placeFrame } from "shared/datamodel/frame";
import {
  AllSnapCandidates,
  checkForSnaps,
  DistanceGuidelines,
  getDistanceSnapLines,
  getSnapLines,
  getSnappingGuidelines,
  GuideLines,
  renderGuidelinesDirectly,
  clearGuidelines,
} from "frontend/utils/snap-utils";
import SnappingTransformer, { AnchorDblClickCb } from "./transformer/snapping-transformer";
import { Frame } from "shared/datamodel/schemas/frame";
import { calcPixelRatio, getAreaToExport, getSnapshotBasic, getSnapshotOfFrame } from "frontend/utils/export-utils";
import ElementsToolbarStandalone from "./elements-toolbar/elements-toolbar";
import { ElementsToolbarCIM } from "./elements-toolbar-cim-restyle/elements-toolbar";
import { Html } from "react-konva-utils";
import { FileUploader } from "./uploads/file-uploader";
import usePrevious from "frontend/hooks/use-previous";
import { FileMetadataType, TagType } from "shared/datamodel/schemas/metadata";
import { useUppy } from "@uppy/react";
import Uppy from "@uppy/core";
import { copyFiles, getAllUsers, getTemplateData } from "frontend/api";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  connectorTransformingAtom,
  documentIdAtom,
  editingElementIdAtom,
  editingElementLinkAtom,
  internalSelectionAtom,
  isDraggingElementAtom,
  isExportingAtom,
  isPreviewExportModeAtom,
  isStageReadyAtom,
  mouseStateAtom,
  showTemplateCreationLoaderModalAtom,
  submenuDataAtom,
  tableSelectionAtom,
  utilsAtom,
} from "state-atoms/stage-atoms";
import { layerRefAtom, stageRefAtom, transformerRefAtom } from "state-atoms/general-atoms";
import { accountAtom, boardAtom, userAtom, usersAtom, usersStateAtom } from "state-atoms/users-atoms";
import { iterableHasAtLeast, minIndex, MutationInfo, updateObjectInMap } from "frontend/utils/general-utils";
import { useEventListener } from "usehooks-ts";
import OverlayElement from "./overlays/overlay-element";
import AnnotationMarker from "./overlays/annotation-marker";
import CordThreadView from "./overlays/cord-thread-view";
import { NoCordMsg } from "frontend/hooks/use-cord";
import { CanvasTemplate } from "../../shared/datamodel/schemas/canvas-template";
import { focusedElementIdAtom } from "../../state-atoms/url-atoms";
import { RESET } from "jotai/utils";
import ConnectorBuilder from "./elements/connector/connector-builder";
import FrameBackgroundElement from "./elements/frame/frame-background-element";
import { boundsRefreshOperation, PatchAnything } from ".";
import { placeMindMapNode } from "shared/datamodel/mindmap";
import { CanvasUtils } from "frontend/utils/canvas-utils";
import { CanvasProvider } from "./canvas-context";
import { MindmapNodeElement, mindmapNodeSchema } from "shared/datamodel/schemas/mindmap";
import { VotingButtons } from "frontend/canvas-designer-new/voting/voting-buttons";
import { useDrop } from "react-dnd";
import { placeCardStack } from "shared/datamodel/card-stack";
import { difference, equals, reduce } from "rambda";
import { produceWithPatches } from "immer";
import {
  countOrgChartNodesInReflect,
  deleteChildFromOrgChart as deleteNodeFromOrgChart,
  numberOfNodes,
  parseNodeId as parseOrgChartNodeId,
  transformerAnchorId,
  upgradeOrgChartImageUrls,
  useOrgChartPremiumFeatures,
} from "./elements/org-chart/orgchart-utils";
import { KonvaNodeElement } from "./elements/konva-node-element";
import { EVT_ELEMENT_DRAG, EVT_ELEMENT_DRAG_START, EVT_ELEMENT_DROP } from "./elements/card-stack/card-stack-utils";
import { placeLiveIntegrationStack } from "shared/datamodel/live-integration";
import { OrgchartFileUploaderProvider, useOrgChartUploaderAndDashboard } from "frontend/hooks/use-orgchart-uploader";
import usePlanFeatureValue from "frontend/hooks/use-plan-features";
import { OrgChartElement } from "shared/datamodel/schemas/org-chart";
import { CheckoutSources } from "frontend/billing/checkout";
import { useBoardTasks } from "frontend/hooks/use-board-integrations";
import { CreatingTemplateIntegrationModal } from "./creating-integration-template-loader";
import { noop, off } from "frontend/utils/fn-utils";
import { StaticTimelineElement } from "shared/datamodel/schemas/timeline";
import Transform, { Degrees } from "frontend/utils/transform";
import {
  PlanFeatureName,
  GanttElement,
  Shape,
  TaskCard,
  TextBlock,
  User,
  IntegrationItem,
} from "shared/datamodel/schemas";
import { getDefaultSizeForShape } from "frontend/utils/shape-utils";
import ShapesData from "frontend/data/shapes/shapes-visuals";
import { usePlaceTemplate } from "frontend/hooks/place-template-hooks";
import { createTimelineElements } from "frontend/utils/templates-utils";
import { PdfExporter } from "frontend/pdf-export/pdf-export";
import { containsMiroData, pasteFromMiro } from "./miro/miro_copy_paste_migrator";
import { placeTable } from "shared/datamodel/table";
// import { HotkeysProvider } from "frontend/shortcut-context";
import { ExportConfiguration } from "frontend/canvas-controls/export-canvas/export-dialog";
import { HotkeysProvider } from "frontend/shortcut-context";
import { ReadTransaction } from "@workcanvas/reflect";
import { findNewPosition, VectorDirections } from "frontend/canvas-designer-new/elements/position-elements";
import { splitKey as splitTableCellKey, TableIds } from "./elements/table/table-utils";
import { DistanceGuideline } from "./utility-elements/distance-guideline";
import { BoardContext, getElementGraphicsProvider, getElementProvider } from "elements/index";
import ElementComponent from "elements/base/konva-component";
import TaskCardOpen from "./elements/task-card-open-element";
import { placeGanttElement } from "shared/datamodel/gantt";
import UndoRedoStack from "utils/undo-redo/undo-redo-stack";
import {
  countGanttTaskNodes,
  countGanttTaskNodesInReflect,
  getBaseTaskPosition,
  trackGanttEvent,
} from "elements/gantt/utils";
import {
  ActuallyMovingElements,
  MaybeMovingElements,
  MovingElements,
  MovingStatus,
} from "frontend/canvas-designer-new/canvast-stage.types";
import useKonvaPerformanceMetrics from "frontend/hooks/performance/use-konva-performance-metrics";
import { addDays } from "date-fns";
import { cleanDate } from "shared/util/date-utils";
import { useGanttMaxTasks } from "elements/gantt/hooks/gantt-max-tasks";
import { placeIcon } from "shared/datamodel/icon";
import { usePreciseDoubleClick } from "frontend/hooks/use-precision-double-click";
import { useFeatureFlag, useGetAllFeatureFlags } from "frontend/hooks/use-feature-flag/use-feature-flag";
import { placeNounIcon } from "shared/datamodel/noun-icon";
import { putDirectDownloadLink } from "./elements/noun-icon/icon-fetch-and-cache";
import { SupportedGanttElements } from "elements/gantt/types";
import { GuiSystemProvider, propagateEvent } from "frontend/canvas-gui-system";
import { placeSingleBarChart } from "shared/datamodel/chart";
import { ChartType } from "shared/datamodel/schemas/chart";
import { IntegrationsProvider } from "frontend/hooks/use-integrations-provider";
import { IntegrationType } from "shared/integrations/integration";
import { useModal } from "frontend/modal/dynamic-modal";
import ReplaceIntegrationItemModal from "./integrations/replace-integration-item-modal";
import { signUpToContinueModalAtom } from "state-atoms/modal-atoms";
import useElementsLimitPerPlanFeature from "frontend/hooks/use-elements-limit";
import { SelectedElementOutline, SelectedLockedElementOutline } from "./frontend-consts";
import {
  CreateMondayItemProps,
  CreateMondayItemMenu as CreateMondayItemMenuLegacy,
} from "./integrations/convert-to-monday/ui/create-monday-item-menu";
import { CreateMondayItemMenu as CreateMondayItemMenuCIM } from "./integrations/convert-to-monday/ui-cim-restyle/create-monday-item-menu";

import { usePerformUpload, trackUploadFiles } from "./uploads/upload-utils";
import { fitToElement, fitToStage, fitToSelectedElements } from "frontend/canvas-controls/zoom-controls/fit-to-stage";
import { getRotatedRectCenter, PointInCanvas } from "frontend/utils/math-utils";
import { distance } from "frontend/utils/point-utils";
import * as DataLayerMenusRestyle from "./element-dynamic-data-cim-restyle/editor-ui/data-layer-menus";
import { DataLayerMenuProps } from "./element-dynamic-data-cim-restyle/editor-ui/data-layer-menus";
import * as DataLayerMenusLegacy from "./element-dynamic-data/editor-ui/data-layer-menus";
import { useDynamicElementActions } from "./element-dynamic-data/hooks/use-dynamic-element-actions";
import { SubmenuDataUpdate } from "./canvas-submenu";
import { Transformer } from "konva/types/shapes/Transformer";
import { moveConnectors } from "./elements/connector/connector-move-utils";
import {
  clearFrameHighlights,
  highlightDragEnteringFrame,
  moveFrameBackground,
} from "./elements/frame/frame-move-utils";
import { KonvaStage } from "frontend/konva-stage-types";
import { navModeAtom } from "state-atoms/nav-atoms";
import { MondayItemManagerProvider } from "frontend/canvas-designer-new/integrations/components/monday-item-manager-context";
import { showCommentsAtom } from "frontend/app-header/right-header/right-header-atoms";
import { SidepanelType, sidePanelType } from "state-atoms";
import { useIsCIMReorderEnabled } from "utils/url-utils";
import { decryptData, encryptData } from "shared/util/encryption-utils";
import { placeMondayTask } from "shared/datamodel/monday-task";
import { useUnhandledTextInput } from "frontend/hooks/use-unhandled-text-input";
import { useSetDidDropMondayElement } from "frontend/context/footer-context";
import { placeIntegrationElement } from "shared/datamodel/integration-item";
import { MondayTaskElement } from "elements/monday-task/schema";
import { Sizes } from "frontend/data/shapes/sizes";
import { withMondayCache } from "frontend/services/monday/monday-cache";
import { readLocalStorage, writeLocalStorage } from "utils/navigator-utils";

// values taken from MDN documentation. don't change, they fit MouseEvent values
const enum MouseButtons {
  None = 0,
  Left = 1,
  Right = 2,
  Middle = 4,
  FourthButton = 8,
  FifthButton = 16,
}

class DataTransferItems {
  public text: string = "";
  public imageFiles: File[] = [];
  public unknowns: string[] = [];
  public canvasElements: null | { elementsData: any; metadata: [string, any][] } = null;
}

type MouseState = {
  buttons: MouseButtons;
  rightClick: boolean;
  middleClick: boolean;
  curPosition: Point;
  clickPosition: Point | null;
  clickPositionScreen: Point | null;
  clickInfo: MouseClickInfo | null;
  capture: boolean;
};

interface GhostShape {
  kind: "Shape";
  point1: Point;
  point2: Point;
  isCentered: boolean;
  isEquiSided: boolean;
  data?: any;
}

interface GhostSvgShape {
  kind: "SvgShape";
  point1: Point;
  point2: Point;
  isCentered: boolean;
  isEquiSided: boolean;
}

interface GhostMindMap {
  kind: "MindMap";
  point: Point;
}

interface GhostMindMapOrgChart {
  kind: "MindMapOrgChart";
  position: Point;
  template: CanvasTemplate;
}

interface GhostConnector {
  kind: "Connector";
  point1?: Point & { rotation?: number };
  point2?: Point & { rotation?: number };
  start: { pos: Point; shapeId?: string; side?: string; shapeRotation?: number };
  end: { pos: Point; shapeId?: string; side?: string; shapeRotation?: number };
  mouseDownTime: number;
}

interface GhostNote {
  kind: "Note";
  point1: Point;
  point2: Point;
  data?: any;
}

interface GhostTemplate {
  kind: "Template";
  position: Point;
  template: CanvasTemplate;
}

interface GhostTaskCard {
  kind: "TaskCard";
  point1: Point;
}

interface GhostTask {
  kind: "Task";
  point1: Point;
  data?: any;
}

interface GhostFrame {
  kind: "Frame";
  point1: Point;
  point2: Point;
}

interface GhostCardStack {
  kind: "CardStack";
  point1: Point;
}

interface GhostLiveIntegration {
  kind: "LiveIntegration";
  point1: Point;
}

interface GhostTimeline {
  kind: "Timeline";
  point: Point;
}

interface GhostGantt {
  kind: "Gantt";
  point: Point;
}

interface GhostChart {
  kind: "Chart";
  point: Point;
  chartType: ChartType;
}

interface GhostTable {
  kind: "Table";
  point1: Point;
  point2: Point;
  isCentered: boolean;
  isEquiSided: boolean;
}

interface GhostIconOrImage {
  kind: "IconOrImage";
  url: string;
  point1: Point;
  point2: Point;
}

interface GhostIcon {
  kind: "Icon";
  point: Point;
  data: any;
}

interface SelectionRect {
  kind: "SelectionRect";
  point1: Point;
  point2: Point;
  shift: boolean;
}

interface GhostMultipleElements {
  kind: "MultipleGhosts";
  ghosts: GhostElement[];
}

interface GhostKonvaNode {
  kind: "KonvaNode";
  nodes: Konva.Node[];
  x: number;
  y: number;
  opacity: number;
}

export type GhostElement =
  | null
  | GhostNote
  | GhostConnector
  | GhostShape
  | GhostSvgShape
  | GhostMindMap
  | GhostMindMapOrgChart
  | SelectionRect
  | GhostTemplate
  | GhostTaskCard
  | GhostTask
  | GhostFrame
  | GhostCardStack
  | GhostLiveIntegration
  | GhostTimeline
  | GhostTable
  | GhostGantt
  | GhostIconOrImage
  | GhostMultipleElements
  | GhostKonvaNode
  | GhostChart
  | GhostIcon;

type NodesOrIds = Iterable<Konva.Node> | string[];

interface MouseClickInfo {
  target:
    | "transformer-handle"
    | "annotation"
    | "stage"
    | "selection-area"
    | "shape"
    | "frame"
    | "ghost-element"
    | "shape-socket";
  shapeId?: string;
  shapeGroup?: string[];
}

interface MouseClickInfo2 {
  type:
    | "transformer-handle"
    | "annotation"
    | "anchor"
    | "frame"
    | "element"
    | "selection-area"
    | "stage"
    | "ghost-element"
    | "shape-socket";
  node?: Konva.Node;
  frameId?: string;
  mouseOffset?: Point;
}

// This function checks if some buttons (and only them !) are marked in the 'state',
// To check if some buttons are pressed, and maybe others, do: (state & btns) == btns;
function isButtonPressed(state: number, btns: MouseButtons): boolean {
  return (state & btns) == btns;
}

// on Mac, control+click is considered as mouse right click
function isRightClick(e: MouseEvent): boolean {
  return (
    isButtonPressed(e.buttons, MouseButtons.Right) ||
    (isButtonPressed(e.buttons, MouseButtons.Left) && e.ctrlKey && isMac())
  );
}

function isMiddleClick(e: MouseEvent): boolean {
  return isButtonPressed(e.buttons, MouseButtons.Middle);
}

const isTable = isOfTypes(consts.CANVAS_ELEMENTS.TABLE);

const ORG_CHART_MIND_MAP_CONNECTOR_REGEX = /orgChart|mindmap|connector/;

type ExistingCommentThread = {
  kind: "Existing";
  storeId: string; // the id for the comment provider database
  elementId: string; // the canvas-element id
};

interface NewCommentThread {
  kind: "New";
  storeId: string;
  newElementData: NewElementData;
  elementId: string;
}

type ActiveCommentThread = null | ExistingCommentThread | NewCommentThread;

export interface CanvasState {
  ghostElement: GhostElement;
  guidelines: GuideLines | null;
  distanceGuidelines: DistanceGuidelines | null;
  sizeGuidelines: DistanceGuidelines | null;
  connectorAnchorPoints: {
    points: Point[];
    node: Node;
    highlight: number;
  } | null;
  movingElements: MovingElements | null;
  highlightedFrameElementIds: string[];
  mouseState: {
    clickPosition: Point | null;
    clickPositionScreen: Point | null;
    clickInfo: MouseClickInfo | null;
    curPosition: MathUtils.PointInCanvas;
    buttons: MouseButtons;
    rightClick: boolean;
    middleClick: boolean;
    capture: boolean;
  };
  selectedNodes: null | Konva.Node[];
}

/// A stage that handles gestures
export default function CanvasStage({
  customColors,
  cursorType,
  setCursorType,
  posScale,
  onChangePosScale,
  onDragElement,
  onMoveElements,
  onResizeElement,
  onChangeElement,
  patchCanvasElement,
  patchCanvasElements,
  patchAnything,
  onAddElements,
  onDeleteElements,
  onReplaceElements,
  onDraw,
  onElementsSelected,
  onElementsMutationEnded,
  selectedElementIds,
  setSelectedElementsIds,
  syncService,
  onUndo,
  onRedo,
  onError,
  showUpgradeModal,
  cordAuthToken,
  setShowSubmenu,
  setShowSideMenuOverflow,
  isReadOnly,
  onMouseDown,
  undoRedoStack,
  showKonvaPerformanceMetrics,
  needToRefreshBoundsRef,
}: {
  customColors: string[];
  cursorType: CursorType;
  setCursorType: React.Dispatch<React.SetStateAction<CursorType>>;
  posScale: Point & { scale: number };
  onChangePosScale: (posScale: Point & { scale: number }) => void;
  onDragElement: (id: string, position: Point) => void;
  onMoveElements: (ids: string[], delta: Point) => void;
  onResizeElement: (id: string, position: Point, scaleX: number, scaleY: number, rotation: number) => void;
  onChangeElement: (id: string, props: Map<string, any>, previousProps: Map<string, any>, addUndo: boolean) => void;
  patchCanvasElement: (args: { id: string; patch: any; inversePatch?: any; details?: any }) => void;
  patchCanvasElements: (mutations: [string, any, any | undefined][]) => void;
  patchAnything: PatchAnything;
  onAddElements: (
    elements: NewElementData[],
    addUndo?: boolean,
    source?: string,
    sequenceId?: string
  ) => Promise<string[]>;
  onDeleteElements: (ids: string[], addUndo?: boolean, sequenceId?: string) => Promise<void>;
  onReplaceElements: (newIds: string[], oldIds: string[]) => void;
  onDraw: (id: string, point: Point) => void;
  onElementsSelected: (ids: string[]) => void;
  onElementsMutationEnded: (
    info: Map<string, ElementDrawProps>,
    undoInfo: Map<string, ElementDrawProps>,
    subMenuData?: SubmenuDataUpdate,
    addUndo?: boolean
  ) => void;
  selectedElementIds: string[];
  setSelectedElementsIds: (ids: string[]) => void;
  syncService: SyncService<RW> | null;
  onUndo: any;
  onRedo: any;
  onError?: (error: ReactNode | null) => void;
  showUpgradeModal?: (reason: CheckoutSources) => void;
  cordAuthToken?: string | null;
  setShowSubmenu: (show: boolean) => void;
  setShowSideMenuOverflow: (show: boolean) => void;
  isReadOnly: boolean;
  onMouseDown: () => void;
  undoRedoStack: UndoRedoStack;
  showKonvaPerformanceMetrics: boolean;
  needToRefreshBoundsRef: React.MutableRefObject<string | null>;
}) {
  //Atoms - Set
  const setDocumentId = useSetAtom(documentIdAtom);
  const setStageRefAtom = useSetAtom(stageRefAtom);
  const setLayerRefAtom = useSetAtom(layerRefAtom);
  const setTransformerRefAtom = useSetAtom(transformerRefAtom);
  const setSignUpToContinueModal = useSetAtom(signUpToContinueModalAtom);
  const [usersFromAtom, setUsersAtom] = useAtom(usersAtom);
  const setOpenedSidePanel = useSetAtom(sidePanelType);
  const setUsersStateAtom = useSetAtom(usersStateAtom);
  const setUserAtom = useSetAtom(userAtom);
  const setAccountAtom = useSetAtom(accountAtom);
  const setBoardAtom = useSetAtom(boardAtom);
  const [isExportingCanvas, setExportingCanvas] = useAtom(isExportingAtom);
  const setIsStageReady = useSetAtom(isStageReadyAtom);
  const [editingElementLinkId, setEditingElementLink] = useAtom(editingElementLinkAtom);
  const [isDraggingElement, setIsDraggingElement] = useAtom(isDraggingElementAtom);
  const setMouseState = useSetAtom(mouseStateAtom);
  const widgetsMap = useMemo(() => new Map<string, any>(), []);
  const [newElementData, setNewElementData] = useAtom(submenuDataAtom);
  const navMode = useAtomValue(navModeAtom);
  const areCommentsVisableOnCanvas = useAtomValue(showCommentsAtom);
  const isCIM = useIsCIMReorderEnabled();
  const [isPosScaleChangeInProgress, setIsPosScaleChangeInProgress] = useState(false);
  //Atoms - value
  const isPreviewExportMode = useAtomValue(isPreviewExportModeAtom);
  const { canAddElements: canAddElementsWithMondayLimitation } = useElementsLimitPerPlanFeature(
    PlanFeatureName.MONDAY_INTEGRATION_LIMIT
  );
  const premiumExportEnabled = usePlanFeatureValue(PlanFeatureName.PREMIUM_EXPORT);
  const [internalSelectionValue] = useAtom(internalSelectionAtom);
  const [tableSelection, setTableSelection] = useAtom(tableSelectionAtom);
  const maxAllowedTasksInPlan = useGanttMaxTasks();

  // Feauture flags
  const newDraggingExperience = useFeatureFlag("new-dragging-experience");
  const { maxOrgChartNodes } = useOrgChartPremiumFeatures();
  const allFeatureFlags = useGetAllFeatureFlags();
  const newTransformerDesign = useFeatureFlag("new-transformer-design");
  const uploadPdfFeature = useFeatureFlag("upload-pdf");
  const zoomAndPanPerformance = useFeatureFlag("zoom-and-pan-performance");
  const newTaskElementEnabled = useFeatureFlag("new-task-element");
  const newMondayTaskElementEnabled = useFeatureFlag("new-monday-task-element");
  const newSizeSystem = useFeatureFlag("cim-new-default-sizes");
  const useMondaySdk = useFeatureFlag("use-monday-sdk");
  const newFloatersStyle = useFeatureFlag("cim-new-floaters-style");
  Sizes.enableNew(isCIM && newSizeSystem);
  const sizes = Sizes.get();
  const { DataEditorMenu, RulesEditorMenu } = isCIM && newFloatersStyle ? DataLayerMenusRestyle : DataLayerMenusLegacy;
  const CreateMondayItemMenu = isCIM && newFloatersStyle ? CreateMondayItemMenuCIM : CreateMondayItemMenuLegacy;
  const ElementsToolbar = isCIM && newFloatersStyle ? ElementsToolbarCIM : ElementsToolbarStandalone;

  // State
  const [{ board, user, pusherChannel }, dispatch] = useBoardValue();
  const [editingElementId, setEditingElementId] = useAtom(editingElementIdAtom);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const prevCursorType = usePrevious<CursorType>(cursorType);
  const [contextMenuState, setContextMenuState] = useState<Omit<ContextMenuProps, "onItemSelection"> | null>(null);
  const [nextElementMenuState, setNextElementMenuState] = useState<Omit<NextElementMenuProps, "onAddElement"> | null>(
    null
  );
  const [createIntegrationItemMenuState, setCreateIntegrationItemMenuState] = useState<Omit<
    CreateMondayItemProps,
    "onCreatedItems" | "onDismiss"
  > | null>(null);
  const [dataLayerMenuState, setDataLayerMenuState] = useState<Omit<DataLayerMenuProps, "onDismiss"> | null>(null);
  const [rulesEditorState, setRulesEditorState] = useState<Omit<DataLayerMenuProps, "onDismiss"> | null>(null);
  const [stageMoving, setStageMoving] = useState(false);
  const [stageState, setStageState] = useState<Partial<CanvasState>>({
    ghostElement: null,
    guidelines: null,
    distanceGuidelines: null,
    sizeGuidelines: null,
    connectorAnchorPoints: null,
    movingElements: null,
    highlightedFrameElementIds: [],
    mouseState: {
      curPosition: { x: 0, y: 0 } as PointInCanvas,
      clickPosition: null,
      clickPositionScreen: null,
      buttons: MouseButtons.None,
      rightClick: false,
      middleClick: false,
      clickInfo: null,
      capture: false,
    },
    selectedNodes: null,
  });

  const [shapeSocketsHidden, setShapeSocketsHidden] = useState(false);
  const [resizingElements, setResizingElements] = useState(false);
  const modifierKeys = useModifierKeys();
  const [activeCommentThread, setActiveCommentThread] = useState<ActiveCommentThread>(null);
  const [highlightRect, setHighlightRect] = useState<null | IRect>(null);

  // Function to calculate comment attachment for both everlay functions onDrag and onDragEnded
  function calculateCommentAttachment(commentNode: Konva.Node, commentPosition: Point) {
    const nodeUnderMouse = selectElementAtPoint(commentPosition);
    if (!nodeUnderMouse?.attrs.isConnectable) {
      return { canAttach: false, nodeUnderMouse: null, attachedLocation: null };
    }

    const elementType = getElementTypeForId(nodeUnderMouse.attrs.id);
    const element = nodeUnderMouse.attrs.element;

    const attachedLocation = canvasPointToNormalizedPoint(commentNode.getPosition(), elementType, element);

    const legacyShapes = Object.values(consts.SHAPES);
    const shouldOffset = elementType === consts.CANVAS_ELEMENTS.SHAPE && !legacyShapes.includes(element.type);

    const canAttach = isNormalizedPointInsideElement(
      shouldOffset ? { x: attachedLocation.x + 0.5, y: attachedLocation.y + 0.5 } : attachedLocation,
      elementType,
      element
    );

    return { canAttach, nodeUnderMouse, attachedLocation };
  }

  const [focusedElementId, setFocusedElementId] = useAtom(focusedElementIdAtom);
  const setCanvasUtils = useSetAtom(utilsAtom(board?.documentId));
  const isDataLayerEnabled = useFeatureFlag("element-data-layer");
  const { dataSource } = useBoardTasks(board?.documentId);
  const { removeCustomColorForUser } = useAccountReps();
  const { dashboard, openModal } = useOrgChartUploaderAndDashboard(board);

  const showTemplateCreationLoaderModal = useAtomValue(showTemplateCreationLoaderModalAtom);
  const placeTemplate = usePlaceTemplate();
  const connectorTransforming = useAtomValue(connectorTransformingAtom);
  const { showModal } = useModal();

  const performUploadFromHook = usePerformUpload(onAddElements, onDeleteElements, (errorMessage: string) => {
    onError && onError(errorMessage);
  });

  const {
    elements,
    elementsIds,
    commentIds,
    groups,
    tableCells,
    frames,
    isLoaded: elementsFinishedLoading,
    frameIds,
    cardStacks,
    thumbnailId,
    hiddenFrameIds,
    hiddenElementIds,
  } = useCanvasElements(syncService?.getReplicache());

  // Hook used to detect precise double clicks on the stage
  const { createPreciseDblClickHandlers } = usePreciseDoubleClick();
  const stickyNoteOnDoubleClickFeatureFlag = useFeatureFlag("stickynote-on-double-click");

  const elementsInThumbnail = (() => {
    if (thumbnailId && thumbnailId in frames) {
      return [thumbnailId, ...frames[thumbnailId]];
    }
    return [];
  })();

  useEffect(() => {
    // Make sure stage ready is reset when leaving a canvas
    return () => setIsStageReady(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prevent zooming of the page by the browser
  // Browsers usually zoom the entire page when we "pinch" the trackpad
  // This is bad for us because we want to zoom the canvas.
  useEffect(() => {
    const preventBrowserZoom = (e: WheelEvent) => {
      // Browsers generate mouse-wheel event with ctrlKey=true when pinch-zooming
      // and in a real wheel+ctrl event e.wheelDeltaY == e.deltaY * -3 (See mouse-utils.ts)
      // Firefox is an exception for the -3 rule, but this will still work.

      // @ts-expect-error wheelDelta is deprecated but will work fine.
      const isPinchGesture = e.ctrlKey && e.wheelDelta != e.deltaY * -3;
      isPinchGesture && e.preventDefault();
    };
    document.body.addEventListener("wheel", preventBrowserZoom, { passive: false });
    return () => document.body.removeEventListener("wheel", preventBrowserZoom);
  }, []);

  useEffect(() => {
    // Debug hook that should only be used in non-prod environments
    if (process.env.NEXT_ENV == "production") return;
    function toggle(e: KeyboardEvent) {
      const eventTarget = (e.target as any)?.["tagName"] ?? "n/a";
      if (eventTarget != "BODY") return;
      if (e.key == "q" && e.ctrlKey)
        stageRef.current?.getLayers().each((layer) => (layer as unknown as Konva.Layer).toggleHitCanvas());
      if (e.key == "w" && e.ctrlKey) console.log(transformerRef.current?.nodes());
      if (e.key == "t" && e.ctrlKey) setCursorType(CursorType.text);
    }

    window.addEventListener("keydown", toggle);
    return () => window.removeEventListener("keydown", toggle);
  }, []);

  // Handle Gantt task freed from container - canvas stage should take over drag
  useEffect(() => {
    const handleGanttTaskFreed = (event: CustomEvent) => {
      if (!newDraggingExperience) {
        return;
      }

      const { taskId } = event.detail;

      // Small delay to ensure visual node has been updated
      setTimeout(() => {
        // Find the task node
        const taskNode = getNodeById(taskId);
        if (!taskNode) {
          return;
        }

        // Instead of trying to transform coordinates, use task position + offset for centering
        const taskWidth = taskNode.width() || 200;
        const taskHeight = taskNode.height() || 90;

        // The task is already positioned correctly by the Gantt
        // We just need to tell the drag system that the "mouse" is at the task center
        const taskPosition = { x: taskNode.x(), y: taskNode.y() };
        const mousePositionForCentering = {
          x: taskPosition.x + taskWidth / 2,
          y: taskPosition.y + taskHeight / 2,
        };

        // Initialize canvas drag system for this freed task
        const moveContext = initMovingElements(
          { kind: MovingStatus.Beginning, ids: [taskId], elementUnderMouse: taskNode },
          mousePositionForCentering
        );

        if (moveContext) {
          setSelectedIds([taskId], false, false);
          enableTransformer();
          setEditingElementId(null);

          setStageState((previous) => ({ ...previous, movingElements: moveContext }));
        }
      }, 50); // Small delay to ensure visual update
    };

    window.addEventListener("gantt-task-freed", handleGanttTaskFreed as EventListener);
    return () => window.removeEventListener("gantt-task-freed", handleGanttTaskFreed as EventListener);
  }, []);

  const [appState, _] = useStateValue();

  //register effects to set atoms when changed
  useEffect(() => {
    if (board?.documentId) {
      const utils = new CanvasUtils(
        // eslint-disable-next-line react/prop-types
        (props) => createElements(props.position, props.type, props.size, props.frameId),
        onAddElements,
        setSelectedIds,
        setEditingElementId,
        onElementsMutationEnded,
        onDeleteElements,
        onReplaceElements,
        copyElements,
        addMetadata,
        enableTransformer
      );
      setCanvasUtils(utils);
    }
    setDocumentId({ documentId: board?.documentId || "" });
  }, [board?.documentId, canAddElementsWithMondayLimitation]);

  useEffect(() => {
    if (!user || user.isAnonymous) {
      return;
    }

    const fetchAllUsers = async () => {
      const allUsers = useMondaySdk ? await withMondayCache("users-all", "DAY", getAllUsers) : await getAllUsers();
      const allUsersMap = allUsers.reduce(
        (a: Map<number, User>, v: User) => new Map([...a, [Number(v.id), v]]),
        new Map()
      );
      setUsersAtom(allUsersMap);
    };
    if (usersFromAtom.size === 0 && board && board.accountId !== "9743496") {
      fetchAllUsers();
    }
  }, [user, board]);

  useEffect(() => {
    setUsersStateAtom(appState.userState);
  }, [appState.userState]);

  useEffect(() => {
    setUserAtom(appState.user);
  }, [appState.user]);

  useEffect(() => {
    setAccountAtom(appState.account);
  }, [appState.account]);

  useEffect(() => {
    setBoardAtom(board ?? null);
  }, [board]);

  // Snapping states and refs
  const drawingProps = useRef({
    initialPosition: null as Point | null,
    id: null as string | null,
  });
  const mouseState = useRef<MouseState>({
    curPosition: { x: 0, y: 0 },
    clickPosition: null,
    clickPositionScreen: null,
    buttons: MouseButtons.None,
    rightClick: false,
    middleClick: false,
    clickInfo: null,
    capture: false,
  });

  enum DragInfoType {
    KONVA = "konva",
    STATE_CHANGE = "state_change",
  }

  const dragInfo = useRef({
    lastMouseX: 0,
    lastMouseY: 0,
    movementX: 0,
    movementY: 0,
    offset: { x: 0, y: 0 },
    box: { x: 0, y: 0, width: 0, height: 0 },
    snapInfo: null as AllSnapCandidates | null,
    type: DragInfoType.KONVA,
  });

  const allowedFileTypes = uploadPdfFeature ? ["image/*", "application/pdf"] : ["image/*"];
  const uppy = useUppy(() => {
    const uppy = new Uppy({
      debug: false,
      autoProceed: false,
      restrictions: {
        maxFileSize: 52428800, //limit upload size to 50MB
        minNumberOfFiles: 0,
        allowedFileTypes,
      },
      meta: {
        url: "",
        limitedSlot: -1,
      },
    });

    return uppy;
  });

  // save a reference to saved affected nodes so we'll save their "undoProps" only on
  // mutation start and not on every change during a single mutation
  const mutationAffectedNodes = useRef<{ [key: string]: Node }>({});

  // Refs
  const stageRef = useRef<KonvaStage>(null);
  const layerRef = useRef<Konva.Layer>(null);
  const overlayRef = useRef<any>(null);
  const elementBoundsLayerRef = useRef<Konva.Layer | null>(null);
  const highlightRectsRef = useRef<Map<string, Konva.Rect>>(new Map());
  const groupRectsRef = useRef<Map<string, Konva.Rect>>(new Map());
  const selectionRef = useRef<Konva.Rect>(null);
  const transformerRef = useRef<Transformer>(null);
  const ghostElementRef = useRef<GhostElement | null>(null);
  const selectionBoundsRef = useRef<any>(null);
  const selectedIdsRef = useRef<string[]>([]);
  const lastDuplicateDirection = useRef<VectorDirections>(null);
  const lastWheelEvent = useRef<{ time: Date; hardware: MouseUtils.MouseType } | null>(null);
  const mouseClickInfo = useRef<MouseClickInfo2>({ type: "stage" });
  const copiedElements = useRef<any>(null);
  const loaded = useRef(false);
  const moveElementsThrottleRef = useRef<number>(0);
  const onMoveElementsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Throttle refs for performance optimization for zoom and pan
  const transformerUpdateThrottleRef = useRef<number>(0);
  const posScaleThrottleRef = useRef<number>(0);
  const pendingPosScaleRef = useRef<{ x: number; y: number; scale: number } | null>(null);
  // Ref to track if the topmost selected element has a link (for zoom optimization)
  const topElementHasLinkRef = useRef<boolean>(false);
  // Ref to track voting buttons - stores DOM elements and their current positions
  const votingButtonsOffsetRef = useRef<{
    buttonRefs: Map<string, { element: HTMLDivElement; currentX: number; currentY: number }>;
  }>({
    buttonRefs: new Map(),
  });

  useKonvaPerformanceMetrics(showKonvaPerformanceMetrics, stageRef);
  const { addIntegrationToElement, addFieldToElement } = useDynamicElementActions();
  // Effect for undo/redo, copy-paste, group/ungroup, lock/unlock, etc.
  useEffect(() => {
    if (needToRefreshBoundsRef.current) {
      if (needToRefreshBoundsRef.current === boundsRefreshOperation.group) {
        disableGroupsBounds();
        disableElementBounds();
      } else {
        initializeAllGroupsHighlights();
        initializeAllNodesHighlights();
        enableGroupsBounds();
        enableElementBounds();
      }
      refreshTransformer();
      elementBoundsLayerRef.current?.batchDraw();
      setTimeout(() => {
        // undo & redo need to delay the refresh to allow the transformer & bounds to be updated
        if (needToRefreshBoundsRef.current === boundsRefreshOperation.undo) {
          initializeAllNodesHighlights();
          initializeAllGroupsHighlights();
          enableGroupsBounds();
          enableElementBounds();
          refreshTransformer();
          elementBoundsLayerRef.current?.batchDraw();
        } else if (needToRefreshBoundsRef.current === boundsRefreshOperation.redo) {
          // known issue: when redoing a group, the group bounds are not refreshed so currently we are disabling it
          disableGroupsBounds();
          disableElementBounds();
          refreshTransformer();
          elementBoundsLayerRef.current?.batchDraw();
        }
        needToRefreshBoundsRef.current = null;
      }, 50);
    }
  });

  // Effects

  useEffect(() => {
    tracking.trackAnalyticsEvent("canvas_stage_mounted");
  }, []);

  useEffect(() => {
    if (board && board.documentId && board.accountId && user && user.id) {
      if (isCIM) {
        tracking.trackAnalyticsEvent("canvas_monday_page_response_loaded", {
          documentId: board?.documentId,
          accountId: board?.accountId,
          id: user?.id,
        });
      }
      tracking.trackEvent(
        consts.TRACKING_CATEGORY.PAGE_VIEWS,
        "canvas_page_view",
        board?.documentId,
        board?.accountId,
        user?.id
      );
    }
  }, [user]);

  useEffect(() => {
    if (!stageRef.current) return;
    stageRef.current.focusElement = focusOnElement;
    stageRef.current.onChangePosScale = onChangePosScale;
    stageRef.current.exportStage = exportStage;
    stageRef.current.getSnapshotImage = getSnapshotFn;
    stageRef.current.getSelectableNodes = getSelectableNodes;
    stageRef.current.prepareForExport = () => setEditingElementId(null);
    stageRef.current.getCenterPosition = () => {
      if (!stageRef.current) return { x: 0, y: 0 };
      const stage = stageRef.current;
      const scale = stage.scaleX();
      const x = stage.x();
      const y = stage.y();
      const width = stage.width();
      const height = stage.height();
      return { x: (width / 2 - x) / scale, y: (height / 2 - y) / scale };
    };
    dispatch({ type: "set_stage_ref", payload: stageRef.current });
    setStageRefAtom(stageRef);
  }, [stageRef]);

  // When the canvas is loaded, we should set the stage ready.
  // If the canvas takes too long to load, we will set the stage ready after a timeout.
  useEffect(() => {
    const LOAD_TIMEOUT = 5000; //  5 seconds
    if (elementsFinishedLoading && loaded.current) {
      setStageReady();
    } else {
      // set timeout for 0 elements (Or slow canvases, less common though).
      const timeoutId = setTimeout(() => {
        loaded.current = true; // Do not center stage if we hit the timeout.
        setStageReady();
      }, LOAD_TIMEOUT);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementsFinishedLoading, loaded.current]);

  // Center the stage when it loads.
  // If a certain element is focused, center the stage on that element.
  useEffect(() => {
    if (loaded.current) return;

    const isEmptyCanvas = elementsFinishedLoading && elementsIds.length === 0;
    const didDrawElements = isEmptyCanvas ? true : (layerRef?.current?.children.length ?? 0) > 0;

    if (elementsFinishedLoading && didDrawElements) {
      let viewBox;
      const focusedElement = focusedElementId ? getNodeById(focusedElementId) : null;

      if (focusedElementId && focusedElement) {
        viewBox = fitToElement(stageRef, focusedElement);

        // If focusing on a comment, fetch the comment thread and set it as active.
        if (focusedElement.attrs.element.type == consts.CANVAS_ELEMENTS.COMMENT) {
          syncService
            ?.getReplicache()
            ?.query((tx: ReadTransaction) => tx.get(canvasElementPrefix + focusedElementId))
            .then((element) => {
              if (!element) return;
              setActiveCommentThread({
                kind: "Existing",
                storeId: (element as any).storeId,
                elementId: focusedElementId,
              });
            });
        }
      } else {
        viewBox = fitToStage(stageRef);
      }

      if (!viewBox) {
        viewBox = { x: 0, y: 0, scale: 1 };
      }
      onChangePosScale(viewBox);
      loaded.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerRef.current?.children.length, elementsFinishedLoading, elementsIds.length]);

  if (stageRef.current) {
    stageRef.current.getVisibleFramesByOrder = () => {
      const framesIds = Object.keys(frames);
      //TODO: the filtering for visible frames and sorting is duplicated code, I copied it
      // from the hooks in presentation view
      const visibleFramesNodes = framesIds
        .reduce((acc, id) => {
          const node = layerRef?.current?.findOne("." + id) as Konva.Node;
          if (node) {
            acc.push(node);
          }
          return acc;
        }, [] as Konva.Node[])
        .filter((f) => f && (f.attrs.element.visible ?? true));
      // TODO: it's possible Konva didn't render the screen yet, and we don't have frame nodes.
      // only if user clicks on export button before canvas-loaded
      visibleFramesNodes.sort((f1, f2) => {
        const fr1 = f1.attrs.element as Frame;
        const fr2 = f2.attrs.element as Frame;
        const s1: number = fr1.sortIndex ?? framesIds.indexOf(f1.id());
        const s2: number = fr2.sortIndex ?? framesIds.indexOf(f2.id());
        return s1 - s2;
      });
      const ret = visibleFramesNodes.map((f) => ({ id: f.id(), name: f.attrs.element.title as string }));
      return ret;
    };
  }

  useEffect(() => {
    if (transformerRef && transformerRef.current) {
      setTransformerRefAtom(transformerRef);
    }
  }, [transformerRef.current]);

  // This function makes the anchor rects disappear on small scales, so they
  // don't overlap.
  // It still doesn't feel right...
  // function adjustTransformerBySize(tr: Konva.Transformer) {
  //   let newAnchorSize = tr.width() < 30 || tr.height() < 30 ? 0 : 10;
  //   // experiment to make the anchors scale down smoothly
  //   // let factor = MathUtils.clamp(Math.min(tr.width(), tr.height()) / 30, 0, 1);
  //   // newAnchorSize = factor * 10;
  //   if (tr.anchorSize() != newAnchorSize) {
  //     tr.anchorSize(newAnchorSize);
  //     tr.update();
  //   }
  // }

  useEffect(() => {
    setLayerRefAtom(layerRef);
  }, [layerRef.current]);

  useEffect(() => {
    if (stageRef && stageRef.current) {
      const container = stageRef.current.container();

      if (isDraggingElement) {
        container.style.cursor = "grabbing";
        return;
      }

      if (cursorType !== CursorType.draw) {
        // reset drawing
        drawingProps.current.id = null;
      }

      setStageState((previous) => ({ ...previous, connectorAnchorPoints: null }));
      if (cursorType != CursorType.pointer) clearSelected();

      switch (cursorType) {
        case CursorType.connector: {
          container.style.cursor = "crosshair";
          stageState?.ghostElement?.kind != "Connector" &&
            setStageState((previous) => ({ ...previous, ghostElement: null }));
          break;
        }
        case CursorType.draw: {
          container.style.cursor = penCursor(newElementData.pen.color);
          break;
        }
        case CursorType.text: {
          container.style.cursor = "text";
          break;
        }
        case CursorType.move: {
          container.style.cursor = "grab";
          break;
        }
        case CursorType.frame: {
          if (isCIM) {
            setOpenedSidePanel(SidepanelType.frames);
          }
          container.style.cursor = "crosshair";
          break;
        }
        case CursorType.orgChart: {
          container.style.cursor = "crosshair";
          break;
        }
        case CursorType.file: {
          setShowFileUploadModal(true);
          setStageState((previous) => ({ ...previous, ghostElement: null }));
          break;
        }
        case CursorType.comment: {
          if (isCIM) {
            container.style.cursor = cimCommentCursor();
          } else {
            container.style.cursor = commentCursor();
          }
          break;
        }
        case CursorType.pointer: {
          setStageState((previous) => ({ ...previous, ghostElement: null }));
          container.style.cursor = "inherit";
          break;
        }
        case CursorType.cardStack: {
          container.style.cursor = "crosshair";
          break;
        }
        case CursorType.liveIntegration: {
          container.style.cursor = "crosshair";
          break;
        }
        case CursorType.nounIcon: {
          container.style.cursor = "inherit";
          break;
        }
        default: {
          container.style.cursor = "inherit";
        }
      }
    }
  }, [cursorType, newElementData, isDraggingElement]);

  const singleGroupIsSelected = selectedElementIds.length > 1 && doAllElementsBelongToSameGroup(selectedElementIds);
  const allowGrouping = selectedElementIds.length > 1 && !doAllElementsBelongToSameGroup(selectedElementIds);
  const draggingElements = stageState?.movingElements?.kind == MovingStatus.Ongoing;
  const transformingElements = transformerRef.current?.isTransforming();
  const showElementToolbar = !(
    connectorTransforming ||
    draggingElements ||
    transformingElements ||
    isPosScaleChangeInProgress
  );
  const multipleElementsSelected = selectedElementIds.length > 1;

  const showShapeSocketsOnElement = useMemo(() => {
    if (cursorType == CursorType.connector) return null;
    if (draggingElements || transformingElements || resizingElements) return null;
    if (stageState?.ghostElement?.kind === "SelectionRect") return null;
    if (shapeSocketsHidden) return null; // Hide temporarily after drag operations

    // 1. We are selecting a single node
    if (stageState?.selectedNodes?.length === 1 && !stageState?.selectedNodes[0].attrs.isInContainer) {
      if (stageState?.selectedNodes[0].attrs.element.lock) return null; // We cannot edit locked elements

      // BUG: After clicking on the stage and deselecting an element, selectedNodes does not clear itself.
      // We work around this by returning the id based on selectedElementIds prop.
      return selectedElementIds[0] ?? null;
    }

    // 2. We are selecting a derived group
    if (singleGroupIsSelected) {
      const groupId = getGroupForElement(selectedElementIds[0]);
      if (!groupId?.startsWith("derived:")) return null;

      // Get the parent element of the derived group and return its id
      // (The parent is a sticky note or shape, and only one of thes.
      // For now a basic check like this will have to do. Future: Make this smarter)
      const parent = stageState?.selectedNodes?.find((node) =>
        consts.ELEMENTS_SUPPORTING_DATA_LAYER.includes(node.attrs.element.type)
      );

      if (parent) {
        if (parent.attrs.element.lock) return null; // We cannot edit locked elements
        return parent.attrs.id ?? null;
      }
    }

    return null;
    // getGroupForElement as dep will cause this to run on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cursorType,
    draggingElements,
    transformingElements,
    resizingElements,
    stageState?.selectedNodes,
    stageState?.ghostElement?.kind,
    singleGroupIsSelected,
    selectedElementIds,
    shapeSocketsHidden,
  ]);

  // Functions

  function getBoundingBoxWithFallback(node: Konva.Node) {
    if (
      !node.attrs?.element ||
      node.attrs?.id?.includes("tableCell") ||
      node.attrs?.element?.containerId?.includes("gantt")
    ) {
      return null;
    }

    if (node.attrs.id?.includes(consts.CANVAS_ELEMENTS.INTEGRATION) && layerRef?.current?.getStage()) {
      return node.getClientRect({
        relativeTo: layerRef.current.getStage() as any,
        skipShadow: true,
      });
    }
    let bbox = getBoundingBox(node.attrs.type ?? node.attrs.element.type, node.attrs.element);
    let usedClientRect = false;
    if ((bbox?.height === 0 || bbox?.width === 0) && layerRef?.current?.getStage()) {
      // TODO: This is a hack for gantt and timeline - we should calculate the right bbox inside getBoundingBox function
      bbox = node.getClientRect({
        relativeTo: layerRef?.current.getStage() as any,
        skipShadow: true,
      });
      usedClientRect = true;
    }
    if (!bbox) return null;

    // Account for the node's current position during dragging
    // Skip this adjustment for gantt elements when using getClientRect()
    // as it already includes the current position
    if (!usedClientRect) {
      const nodePos = node.getPosition();
      const elementPos = { x: node.attrs.element.x, y: node.attrs.element.y };

      // If the node has been moved from its original position (during dragging),
      // adjust the bbox to reflect the current position
      if (nodePos.x !== elementPos.x || nodePos.y !== elementPos.y) {
        const dx = nodePos.x - elementPos.x;
        const dy = nodePos.y - elementPos.y;
        bbox = {
          ...bbox,
          x: bbox.x + dx,
          y: bbox.y + dy,
        };
      }
    }
    return bbox;
  }

  function getNodesForTransformer(nodes: Konva.Node[]) {
    // middleware - adjust node list
    const { remove, add } = prepareNodeForTransformerConfig(nodes);
    let nodesForTransformer = nodes;
    if (remove.length > 0) {
      nodesForTransformer = nodesForTransformer.filter((node) => !remove.includes(node));
    }
    if (add.length > 0) {
      nodesForTransformer = [
        ...(layerRef.current?.find((node: Konva.Node) => add.includes(node.name())).toArray() ?? []),
        ...nodesForTransformer,
      ];
    }

    // filter out locked elements only if there are locked and unlocked elements in the selection
    const unlockedNodes = nodesForTransformer.filter((x) => (x.attrs.element.lock ?? 0) == 0);
    if (unlockedNodes.length !== nodesForTransformer.length && unlockedNodes.length > 0) {
      nodesForTransformer = unlockedNodes;
    }
    return nodesForTransformer;
  }

  // #region transformer operations
  function disableTransformer() {
    if (transformerRef.current) {
      transformerRef.current.nodes([]);
      transformerRef.current.update();
    }
  }

  function enableTransformer(shouldSetNodes: boolean = true) {
    if (transformerRef && transformerRef.current) {
      if (selectedIdsRef.current.length === 0 || !layerRef.current) {
        transformerRef.current.nodes([]);
        transformerRef.current.update();
        return [];
      }

      // Get the nodes matching the selected-ids
      const nodes: Konva.Node[] =
        layerRef.current
          ?.find(
            (i: any) =>
              selectedIdsRef.current.includes(i.attrs.id) &&
              !i.attrs.isFrameTitle &&
              !i.attrs.isTableTitle &&
              // Exclude integration elements inside cardstack for transformer
              // because they recieve relative position to the stack, not absolute
              ((i.attrs.id?.includes(consts.CANVAS_ELEMENTS.INTEGRATION) ||
                i.attrs.id?.includes(consts.CANVAS_ELEMENTS.MONDAY_TASK)) &&
              i.parent?.parent?.name()?.toLowerCase()?.includes("cardstack")
                ? false
                : true)
          )
          .toArray() ?? [];
      if (shouldSetNodes) {
        setStageState((previous) => ({ ...previous, selectedNodes: nodes.length > 0 ? nodes : null }));
      }

      const nodesForTransformer = getNodesForTransformer(nodes);

      // give it to the transformer
      const transformerConfig = getTransformerConfigByElementType(nodesForTransformer);
      transformerRef.current.nodes(nodesForTransformer);
      transformerRef.current.setAttrs(transformerConfig);
      transformerRef.current.update();
      // adjustTransformerBySize(transformerRef.current);
      return nodes;
    }
    return [];
  }

  // TODO: Maybe we should use enableTransformer instead of this function
  function refreshTransformer() {
    if (transformerRef.current) {
      const nodes =
        layerRef.current
          ?.find(
            (i: any) => selectedIdsRef.current.includes(i.attrs.id) && !i.attrs.isFrameTitle && !i.attrs.isTableTitle
          )
          .toArray() ?? [];
      const transformerConfig = getTransformerConfigByElementType(nodes);
      transformerRef.current.nodes(nodes);
      transformerRef.current.setAttrs(transformerConfig);
      transformerRef.current.forceUpdate();
    }
  }
  // #endregion

  // #region selection bounds operations
  function disableSelectionBounds() {
    const selectionBounds = selectionBoundsRef.current;
    if (!selectionBounds) {
      return;
    }
    selectionBounds.setAttrs({
      visible: false,
    });
  }

  function enableSelectionBounds(box: BoundingBox) {
    const selectionBounds = selectionBoundsRef.current;
    if (!selectionBounds) {
      return;
    }
    selectionBounds.setAttrs({
      visible: true,
      x: box.x,
      y: box.y,
      width: box.width,
      height: box.height,
    });
  }

  function moveSelectionBoundsByDelta(dx: number, dy: number) {
    const selectionBounds = selectionBoundsRef.current;
    if (!selectionBounds) return;

    // Convert to numbers to prevent string concatenation
    const deltaX = Number(dx);
    const deltaY = Number(dy);

    if (!isFinite(deltaX) || !isFinite(deltaY)) {
      console.warn("Invalid deltas in moveSelectionBoundsByDelta:", { dx, dy, deltaX, deltaY });
      return;
    }

    // Get current position and validate it
    const currentX = selectionBounds.x();
    const currentY = selectionBounds.y();

    // Convert current position to numbers in case they're strings
    const validCurrentX = Number(currentX);
    const validCurrentY = Number(currentY);

    if (!isFinite(validCurrentX) || !isFinite(validCurrentY)) {
      console.error("Selection bounds has invalid position:", {
        currentX,
        currentY,
        validCurrentX,
        validCurrentY,
        typeof_currentX: typeof currentX,
        typeof_currentY: typeof currentY,
      });

      // Reset to valid position
      selectionBounds.setAttrs({
        x: 0,
        y: 0,
      });
      return;
    }

    const newX = validCurrentX + deltaX;
    const newY = validCurrentY + deltaY;

    if (!isFinite(newX) || !isFinite(newY)) {
      console.error("Calculated invalid new position for selection bounds:", {
        newX,
        newY,
        validCurrentX,
        validCurrentY,
        deltaX,
        deltaY,
      });
      return;
    }

    selectionBounds.setAttrs({
      x: newX,
      y: newY,
    });
  }
  // #endregion

  // #region element bounds operations
  const updateSelectedNodesHighlights = useCallback(() => {
    selectedIdsRef.current.forEach((id) => {
      const node = layerRef?.current?.findOne((node: Konva.Node) => {
        return (
          node.id() === id &&
          !node.attrs.isFrameTitle &&
          !node.attrs.isTableTitle &&
          !ORG_CHART_MIND_MAP_CONNECTOR_REGEX.test(node.id())
        );
      });
      if (!node) return;

      const rect = highlightRectsRef.current.get(id);
      if (!rect) return;
      if (!node?.attrs?.element) return;

      const bbox = getBoundingBoxWithFallback(node);
      if (!bbox) return;

      rect.setAttrs({
        x: bbox.x,
        y: bbox.y,
        width: bbox.width ?? 0,
        height: bbox.height ?? 0,
      });
    });
  }, []);

  function initializeAllNodesHighlights() {
    const visibilityStates = new Map<string, boolean>();
    const existingGroup = elementBoundsLayerRef.current?.findOne("#element-highlights-group") as
      | Konva.Group
      | undefined;
    if (existingGroup) {
      const elementHighlights = existingGroup.find(".element-highlight").toArray();
      elementHighlights.forEach((rect: Konva.Node) => {
        const elementId = rect.id().replace("highlight-", "");
        visibilityStates.set(elementId, rect.visible());
      });
      existingGroup.destroy();
    }

    const group = new Konva.Group({
      id: "element-highlights-group",
      listening: false,
    });

    const elements =
      layerRef.current
        ?.find((node: Konva.Node) => {
          return (
            node.id() &&
            !node.attrs.isFrameTitle &&
            !node.attrs.isTableTitle &&
            !node.id().includes("orgChart") &&
            !node.id().includes("mindmap") &&
            !node.id().includes("connector")
          );
        })
        .toArray() ?? [];

    elements.forEach((node: Konva.Node) => {
      if (!node?.attrs?.element) return;
      const elementId = node.id();
      const bbox = getBoundingBoxWithFallback(node);
      if (!bbox) return;
      const rect = new Konva.Rect({
        id: `highlight-${elementId}`,
        name: "element-highlight",
        listening: false,
        visible: visibilityStates.get(elementId) ?? false,
        fillEnabled: false,
        stroke: node.attrs.element?.lock ? SelectedLockedElementOutline : SelectedElementOutline,
        strokeWidth: 1, // Fixed width
        strokeScaleEnabled: false, // This handles zoom automatically!
        x: bbox.x,
        y: bbox.y,
        width: bbox.width ?? 0,
        height: bbox.height ?? 0,
      });

      group.add(rect);
      highlightRectsRef.current.set(elementId, rect);
    });

    elementBoundsLayerRef.current?.add(group);
    elementBoundsLayerRef.current?.batchDraw();
  }

  function enableElementBounds() {
    if (isExportingCanvas) return;

    // Hide all rectangles
    highlightRectsRef.current.forEach((rect) => rect.visible(false));

    // Show only selected ones
    selectedIdsRef.current.forEach((id) => {
      const groupId = getGroupForElement(id);
      const rect = highlightRectsRef.current.get(id);
      if (rect) {
        if (groupId || selectedIdsRef.current?.length == 1) {
          rect.visible(false);
        } else {
          rect.visible(true);
        }
      }
    });
  }

  function disableElementBounds() {
    highlightRectsRef.current.forEach((rect: Konva.Node) => {
      rect.visible(false);
    });
  }
  // #endregion

  // #region zoom/pan selection rectangle operations
  // Create a ghost element that matches the transformer's dimensions for performance optimization
  const createGhostTransformerElement = useCallback(() => {
    if (!transformerRef.current || !layerRef.current) return;

    // Get transformer's current bounding box
    const transformerBbox = transformerRef.current._getNodeRect();
    const stage = stageRef.current;
    if (!stage) return;

    const stagePos = stage.position();
    const stageScale = stage.scaleX();

    // Get transformer's rotation directly (in degrees)
    const transformerRotationDeg = transformerRef.current.rotation();
    const transformerRotationRad = transformerRotationDeg * (Math.PI / 180);

    // Transform dimensions from transformer bbox to stage coordinates
    const width = transformerBbox.width / stageScale;
    const height = transformerBbox.height / stageScale;

    // Calculate the actual center point of the rotated transformer
    const centerAbsolute = getRotatedRectCenter(transformerBbox, transformerRotationRad);

    // Convert center from absolute to stage coordinates
    const centerX = (centerAbsolute.x - stagePos.x) / stageScale;
    const centerY = (centerAbsolute.y - stagePos.y) / stageScale;

    // For a rotated rect in Konva, we need to set offsetX/offsetY to rotate around center
    const offsetX = width / 2;
    const offsetY = height / 2;

    // Create a hidden rectangular element with the same dimensions, rotation, and proper element structure
    const ghostElement = new Konva.Rect({
      id: "ghost-transformer-element",
      x: centerX,
      y: centerY,
      width,
      height,
      offsetX,
      offsetY,
      rotation: transformerRotationDeg,
      fill: "transparent",
      stroke: "transparent",
      strokeWidth: 0,
      visible: false, // Hidden from view
      listening: false, // Doesn't interfere with mouse events
      // Add element structure that transformer expects
      element: {
        type: "shape", // Generic shape type for the ghost element
        id: "ghost-transformer-element",
      },
    });

    // Add ghost element to the layer
    layerRef.current.add(ghostElement);

    // Attach transformer to the ghost element
    transformerRef.current.nodes([ghostElement]);
    transformerRef.current.visible(true);
    transformerRef.current.listening(false); // Disable interaction during zoom/pan
  }, []);

  // Remove the ghost transformer element and restore normal transformer
  const removeGhostTransformerElement = useCallback(() => {
    if (!layerRef.current) return;

    // Find and remove the ghost element
    const ghostElement = layerRef.current.findOne("#ghost-transformer-element");
    if (ghostElement) {
      ghostElement.destroy();
    }

    // Restore transformer to original selected elements
    if (transformerRef.current) {
      const selectedNodes = getNodesByIds(selectedIdsRef.current);
      const nodesForTransformer = getNodesForTransformer(selectedNodes);

      transformerRef.current.nodes(nodesForTransformer);
      transformerRef.current.listening(true);
    }
  }, [getNodesByIds]);
  // #endregion

  // #region group bounds operations
  const updateSelectedGroupsHighlights = useCallback(() => {
    // First collect all unique groups that contain selected elements
    const groupsToUpdate = getGroupsForElements(selectedIdsRef.current);

    // Now update each group's highlight
    for (const groupId of groupsToUpdate) {
      const rect = groupRectsRef.current.get(groupId);
      if (!rect) continue;

      const nodes = groups[groupId]
        .map((id) => layerRef.current?.findOne(`#${id}`))
        .filter((node): node is Konva.Node => !!node);

      const stage = layerRef.current?.getStage();
      const measureOptions = { relativeTo: stage as any, skipShadow: true };
      const bbox = new BoundingBox();

      for (const node of nodes) {
        const nodeRect = node.getClientRect(measureOptions);
        bbox.expandRect(nodeRect);
      }
      rect.setAttrs({
        x: bbox.x,
        y: bbox.y,
        width: bbox.width ?? 0,
        height: bbox.height ?? 0,
      });
    }

    elementBoundsLayerRef.current?.batchDraw();
  }, [getGroupsForElements, groups]);

  function initializeAllGroupsHighlights() {
    const visibilityStates = new Map<string, boolean>();
    const existingGroup = elementBoundsLayerRef.current?.findOne("#group-highlights-container") as
      | Konva.Group
      | undefined;
    if (existingGroup) {
      const groupHighlights = existingGroup.find(".group-highlight").toArray();
      groupHighlights.forEach((rect: Konva.Node) => {
        visibilityStates.set(rect.id(), rect.visible());
      });
      existingGroup.destroy();
    }

    const groupContainer = new Konva.Group({
      id: "group-highlights-container",
      listening: false,
    });

    const nodesMap = new Map<string, Konva.Node>();
    layerRef.current?.find((node: Konva.Node) => {
      const nodeId = node.id();
      if (nodeId && !node.attrs.isFrameTitle && !node.attrs.isTableTitle) {
        nodesMap.set(nodeId, node);
      }
      return false;
    });

    Object.entries(groups).forEach(([groupId, groupItems]) => {
      const nodes = groupItems.map((id) => nodesMap.get(id)).filter((node): node is Konva.Node => !!node);

      const stage = layerRef.current?.getStage();
      const measureOptions = { relativeTo: stage as any, skipShadow: true };
      const bbox = new BoundingBox();

      for (const node of nodes) {
        const rect = node.getClientRect(measureOptions);
        bbox.expandRect(rect);
      }

      const isLocked = nodes.every((node: any) => !!node.attrs.element?.lock);

      const rect = new Konva.Rect({
        id: groupId,
        name: "group-highlight",
        listening: false,
        visible: visibilityStates.get(groupId) ?? false,
        fillEnabled: false,
        stroke: isLocked ? SelectedLockedElementOutline : SelectedElementOutline,
        strokeWidth: 1, // Fixed width
        strokeScaleEnabled: false, // This handles zoom automatically!
        x: bbox.x,
        y: bbox.y,
        width: bbox.width ?? 0,
        height: bbox.height ?? 0,
      });

      groupContainer.add(rect);
      groupRectsRef.current.set(groupId, rect);
    });

    elementBoundsLayerRef.current?.add(groupContainer);
    elementBoundsLayerRef.current?.batchDraw();
  }

  // Throttled version of onChangePosScale for zoom and pan performance
  const throttledOnChangePosScale = useCallback(
    (newPosScale: { x: number; y: number; scale: number }) => {
      pendingPosScaleRef.current = newPosScale;

      if (posScaleThrottleRef.current) {
        // Already throttling, just update the pending value
        return;
      }

      // Immediately apply the first change
      onChangePosScale(newPosScale);
      pendingPosScaleRef.current = null;

      // Throttle subsequent changes to 60fps (16ms)
      posScaleThrottleRef.current = window.setTimeout(() => {
        if (pendingPosScaleRef.current) {
          onChangePosScale(pendingPosScaleRef.current);
          pendingPosScaleRef.current = null;
        }
        posScaleThrottleRef.current = 0;
      }, 16);
    },
    [onChangePosScale]
  );

  const hideUIElementsDuringPosScaleChange = useCallback(() => {
    if (transformerRef.current && selectedIdsRef.current.length > 1) {
      if (!isPosScaleChangeInProgress) {
        // Create ghost element and attach transformer for performance optimization
        createGhostTransformerElement();

        // Hide toolbar during operation
        setIsPosScaleChangeInProgress(true);
      }
      // Clear previous timeout and reset it (this happens on every call)
      clearTimeout(transformerUpdateThrottleRef.current);

      // Re-show transformer and toolbar after operation stops
      transformerUpdateThrottleRef.current = window.setTimeout(() => {
        if (transformerRef.current) {
          // Remove ghost element and restore transformer to original elements
          removeGhostTransformerElement();
        }
        // Re-show toolbar
        setIsPosScaleChangeInProgress(false);
      }, 300);
    }
  }, [isPosScaleChangeInProgress, createGhostTransformerElement, removeGhostTransformerElement]);

  useEffect(() => {
    if (!layerRef.current || !elementBoundsLayerRef.current) return;

    if (draggingElements || transformingElements) {
      updateSelectedNodesHighlights();
      updateSelectedGroupsHighlights();
    } else if (selectedIdsRef.current.length > 0 && !isPosScaleChangeInProgress) {
      // Also refresh bounds when elements change (collaborative updates)
      updateSelectedNodesHighlights();
      updateSelectedGroupsHighlights();
    }
  }, [draggingElements, transformingElements, elements, updateSelectedNodesHighlights, updateSelectedGroupsHighlights]); // Add elements as dependency

  function getGroupsContainingSelectedElements() {
    const groupsWithSelected = new Set<string>();

    selectedIdsRef.current.forEach((id) => {
      for (const groupId in groups) {
        if (groups[groupId].includes(id)) {
          groupsWithSelected.add(groupId);
        }
      }
    });

    return groupsWithSelected.size; // Returns the count of groups
  }

  function enableGroupsBounds() {
    if (isExportingCanvas) return;

    // Hide all group rectangles
    groupRectsRef.current.forEach((rect) => rect.visible(false));

    const groupsContainingSelectedElements = getGroupsContainingSelectedElements();
    // Show only rectangles for groups that contain selected elements
    for (const id of selectedIdsRef.current) {
      for (const groupId in groups) {
        const group = groups[groupId];
        if (group.includes(id)) {
          const rect = groupRectsRef.current.get(groupId);
          if (rect) {
            if (
              groupsContainingSelectedElements == 1 &&
              group.length === transformerRef.current?.nodes().length &&
              group.length === selectedIdsRef.current.length
            ) {
              rect.visible(false);
            } else {
              rect.visible(true);
            }
          }
        }
      }
    }
    elementBoundsLayerRef.current?.batchDraw();
  }

  function disableGroupsBounds() {
    groupRectsRef.current.forEach((rect) => rect.visible(false));
  }
  // #endregion

  // #region selection rect operations
  function changeSelectionRect() {
    const node = selectionRef.current;
    if (!node) {
      return;
    }
    const selectionRectActive = ghostElementRef.current && ghostElementRef.current.kind == "SelectionRect";

    if (!selectionRectActive) {
      if (node.visible() == true) {
        node.setAttrs({
          visible: false,
        });
        disableSelectionBounds();
        node.getLayer()?.batchDraw();
      }
      return;
    }
    const { point1, point2 } = ghostElementRef.current as SelectionRect;
    node.setAttrs({
      visible: true,
      x: Math.min(point1.x, point2.x),
      y: Math.min(point1.y, point2.y),
      width: Math.abs(point1.x - point2.x),
      height: Math.abs(point1.y - point2.y),
    });
    handlePointerDragEnd((ghostElementRef.current as any).shift);
    elementBoundsLayerRef.current?.batchDraw();
    requestAnimationFrame(() => {
      node.getLayer()?.batchDraw();
    });
  }
  // #endregion

  function getNodesOrSelection(nodes?: NodesOrIds) {
    if (nodes == undefined) nodes = selectedElementIds;
    if (nodes instanceof Array && typeof nodes[0] == "string") {
      const typescript_hack = nodes;
      return getSelectableNodes((node) => typescript_hack.includes(node.id()));
    }
    return nodes as Iterable<Konva.Node>;
  }

  function mutateMultipleElements({
    newProps,
    prevProps,
  }: {
    newProps: Map<string, any>;
    prevProps: Map<string, any>;
  }) {
    for (const [id, props] of newProps) {
      onChangeElement(id, new Map(Object.entries(props)), new Map(Object.entries(prevProps.get(id)!)), false);
    }
    onElementsMutationEnded(newProps, prevProps);
  }

  function mapMutator(fn: (n: Konva.Node) => { oldProps: any; newProps: any }) {
    return function (nodesOrIds?: NodesOrIds, { newProps, prevProps } = { newProps: new Map(), prevProps: new Map() }) {
      const nodes = getNodesOrSelection(nodesOrIds);
      for (const node of nodes) {
        const id = node.id();
        const change = fn(node);
        updateObjectInMap(prevProps, id, change.oldProps);
        updateObjectInMap(newProps, id, change.newProps);
      }
      return { newProps, prevProps };
    };
  }

  function lockElements(nodesOrIds?: Iterable<Konva.Node> | string[]) {
    // This function is designed to workaround a problem:
    // Suppose we're in text-edit mode on an element, and we lock the element.
    // For correct undo/redo behavior, we should record the text-edit first, and then
    // perform the lock.
    // But right now it's the element itself that records the undo-checkpoint, so we must
    // change the editingElementId, wait for re-render and then perform the lock,
    if (editingElementId == null) {
      lockElements_(nodesOrIds);
    } else {
      setEditingElementId(null);
      setTimeout(() => {
        lockElements_(nodesOrIds);
      }, 10);
    }
  }

  function lockElements_(nodesOrIds?: Iterable<Konva.Node> | string[]) {
    const nodes = getNodesOrSelection(nodesOrIds);
    const mappedLocked = mapMutator((node) => {
      return { oldProps: { lock: node.attrs.element.lock ?? 0 }, newProps: { lock: LockType.Full } };
    });

    let change: any;
    if (iterableHasAtLeast(nodes, 2)) {
      change = mappedLocked(nodes, grouper(nodes));
    } else {
      change = mappedLocked(nodes);
    }
    mutateMultipleElements(change);
    setTimeout(() => {
      refreshTransformer();
      needToRefreshBoundsRef.current = boundsRefreshOperation.lockUnlock;
    }, 10);
  }

  function unlockElements(nodesOrIds?: Iterable<Konva.Node> | string[]) {
    const nodes = getNodesOrSelection(nodesOrIds);
    const mappedUnlock = mapMutator((node) => {
      return { oldProps: { lock: node.attrs.element.lock ?? 0 }, newProps: { lock: LockType.None } };
    });
    let change = mappedUnlock(nodes);
    if (iterableHasAtLeast(nodes, 2)) {
      change = ungrouper(nodes, change);
    }

    // workaround to make sure the lock is set to None for first time render
    for (const node of nodes) {
      node.attrs.element = {
        ...node.attrs.element,
        lock: LockType.None,
      };
    }
    mutateMultipleElements(change);
    setTimeout(() => {
      enableTransformer();
      needToRefreshBoundsRef.current = boundsRefreshOperation.lockUnlock;
    }, 10);
  }

  function penCursor(color: string = "#fff") {
    const encodedColor = color.replace("#", "%23");
    return `url("data:image/svg+xml,%3Csvg width='18' height='17' viewBox='0 0 23 22' fill='${encodedColor}' xmlns='http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg' %3E %3Cpath d='M1.72656 21H6.92497L20.5708 7.29997C21.2601 6.60787 21.6474 5.6692 21.6474 4.69043C21.6474 3.71167 21.2601 2.77299 20.5708 2.0809C19.8814 1.38881 18.9465 1 17.9716 1C16.9967 1 16.0617 1.38881 15.3724 2.0809L1.72656 15.7809V21Z' stroke='%23000' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' %2F%3E %3Cpath d='M14.0723 3.38574L19.2707 8.6048' stroke='%23000' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' %2F%3E %3C%2Fsvg%3E") 0 15, auto`;
  }

  function cimCommentCursor() {
    return `url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22none%22%20viewBox%3D%220%200%2016%2016%22%3E%3Cpath%20stroke%3D%22%231E1E1E%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20d%3D%22M5%206.56h5.901M5%209.445h5.747%22%3E%3C%2Fpath%3E%3Cpath%20fill%3D%22%231E1E1E%22%20d%3D%22M10.241%201.28v-.5zm5%205h.5zm-5%207.599v.5zm-7.407-.007.031-.499-.21-.013-.156.14zm-1.372%201.236.334.372zm-.501-.222h-.5zm5-13.607v-.5zm4.28%200v.5a4.5%204.5%200%200%201%204.5%204.5h1a5.5%205.5%200%200%200-5.5-5.5zm5%205h-.5v2.6h1v-2.6zm0%202.6h-.5a4.5%204.5%200%200%201-4.5%204.5v1a5.5%205.5%200%200%200%205.5-5.5zm-5%205v-.5h-7.28v1h7.28zm-7.28%200v-.5c-.022%200-.03-.002-.096-.006l-.031.5-.031.498c.018.001.094.008.158.008zm-.127-.007-.335-.371-1.372%201.236.335.371.335.372%201.372-1.237zm-1.372%201.236-.335-.371a.2.2%200%200%201%20.334.149h-1a.8.8%200%200%200%201.335.594zm-.501-.222h.5V6.279h-1v8.607zm0-8.607h.5a4.5%204.5%200%200%201%204.5-4.5v-1a5.5%205.5%200%200%200-5.5%205.5zm5-5v.5h4.28v-1h-4.28z%22%3E%3C%2Fpath%3E%3C%2Fsvg%3E") 0 22, auto`;
  }

  function commentCursor() {
    return `url("data:image/svg+xml;utf8,%3Csvg width='22' height='22' viewBox='0 0 22 22' fill='white' xmlns='http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg' %3E%3Cpath d='M1 20.72V4.6975C1 3.71686 1.38956 2.77639 2.08297 2.08297C2.77639 1.38956 3.71686 1 4.6975 1H17.0225C18.0031 1 18.9436 1.38956 19.637 2.08297C20.3304 2.77639 20.72 3.71686 20.72 4.6975V12.0925C20.72 13.0731 20.3304 14.0136 19.637 14.707C18.9436 15.4004 18.0031 15.79 17.0225 15.79H5.93L1 20.72Z' stroke='%230B2642' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'%2F%3E%3Cpath d='M5.92969 5.92993H15.7897' stroke='%230B2642' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'%2F%3E%3Cpath d='M5.92969 10.8601H13.3247' stroke='%230B2642' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'%2F%3E%3C%2Fsvg%3E") 0 22, auto`;
  }

  function focusOnElement(nodeId: string) {
    let node;
    if (getElementTypeForId(nodeId) == consts.CANVAS_ELEMENTS.COMMENT) {
      const overlay = stageRef.current?.findOne(".Overlay") as Konva.Layer | undefined;
      node = overlay?.findOne("." + nodeId);
    } else {
      node = layerRef.current
        ?.find("." + nodeId)
        .toArray()
        .filter((node: any) => !node.attrs.isFrameTitle)[0];
    }
    if (!node) {
      console.warn("can't find node by id", nodeId);
      return false;
    }

    if (node.attrs.type == "marker") {
      const nodePos = node.getAbsolutePosition(node.getStage()),
        x = -nodePos.x + node.getStage()!.width() / 2,
        y = -nodePos.y + node.getStage()!.height() / 2;
      onChangePosScale({ x, y, scale: 1 });
      if (getElementTypeForId(node.attrs.id) == consts.CANVAS_ELEMENTS.COMMENT) {
        setActiveCommentThread({
          kind: "Existing",
          storeId: node.attrs.element.storeId,
          elementId: node.attrs.id,
        });
        setFocusedElementId(node.attrs.id);
      }
    } else {
      const stage = node.getStage()!;
      const box = measureNode(node);
      if (Math.min(box.width, box.height) <= 0) {
        console.warn("failed to calculate initial position");
        return false;
      }
      const padding = 200;
      box.width += padding * 2;
      box.height += padding * 2;
      box.y -= padding * 1.5;
      box.x -= padding;
      const scale = MathUtils.clamp(
        Math.min(stage.width() / box.width, stage.height() / box.height),
        consts.STAGE_ZOOM.MIN,
        consts.STAGE_ZOOM.MAX
      );
      const center = {
        x: stage.width() / 2 - (box.x + box.width / 2) * scale,
        y: stage.height() / 2 - (box.y + box.height / 2) * scale,
      };
      if (isNaN(center.x) || isNaN(center.y) || isNaN(scale)) {
        return false;
      }
      onChangePosScale({ scale, x: center.x, y: center.y });
    }
    return true;
  }

  async function getSnapshotFn({
    withWatermark,
    quality,
    elementId,
    scale,
    position,
    maxSize,
    padding = 40,
    whiteBackground = true,
  }: {
    withWatermark: boolean;
    elementId?: string | null;
    quality: number;
    scale: number;
    position: Point;
    maxSize?: number;
    padding?: number;
    whiteBackground?: boolean;
  }) {
    const canvasLayer = layerRef.current;

    if (!canvasLayer) {
      return;
    }

    // 1. area is the rect in canvas coordinates that contains all elements, or the frame supplied
    let area = getAreaToExport(canvasLayer, elementId);
    // TODO: if area.width or height == 0, abort; don't export empty canvas
    area = MathUtils.padRect(area, padding);

    // 2. Konva requires us to adjust for the stage's current scale and position when exporting
    const adjustedArea = {
      x: area.x * scale + position.x,
      y: area.y * scale + position.y,
      width: area.width * scale,
      height: area.height * scale,
    };

    // 3. And we also have to choose pixel ratio, which really determines final size and quality.
    //    If we choose window.devicePixelRatio we get a match between screen pixels and canvas pixels.
    //    But maximum value allowed is by quality setting and browser limits.
    const minFinalSize = 512;
    const maxFinalSize = maxSize ?? 32767;
    const pixelRatio = calcPixelRatio(adjustedArea, minFinalSize, maxFinalSize, quality);

    const config = {
      ...adjustedArea,
      pixelRatio,
    };
    return getSnapshotBasic(canvasLayer, area, withWatermark, config, whiteBackground, (step) =>
      setExportingCanvas(step == "start")
    );
  }

  function sanitizeExportFilename(name: string) {
    return name.replaceAll(".", "_");
  }

  async function exportStage(config: ExportConfiguration, withWatermark: boolean, position: Point, scale: number) {
    if (!layerRef.current) {
      return;
    }

    tracking.trackEvent(consts.TRACKING_CATEGORY.CANVAS_ACTION, "export_canvas", config.format);
    setExportingCanvas(true);
    const boardName = sanitizeExportFilename(board?.name ?? "WorkCanvas");
    try {
      if (config.format.startsWith("image")) {
        const dataurl = await getSnapshotFn({
          withWatermark,
          quality: { low: 0.04, high: 0.3, ultra: 1 }[config.quality],
          scale,
          position,
          whiteBackground: config.whiteBackground,
        });
        if (!dataurl) return;
        downloadURI(dataurl, boardName + ".png");
      } else {
        const exporter = await PdfExporter.create();
        if (config.singlePage) {
          let area = getAreaToExport(layerRef.current);
          // TODO: if area.width or height == 0, abort; don't export empty canvas
          // padding of 22 is chosen to allow frame and table titles to show (it's their size+margin in pixels)
          area = MathUtils.padRect(area, 22 / (layerRef.current.parent?.scaleX() ?? 1));
          exporter.prepareDocument(
            [area],
            { title: boardName, author: user?.name ?? "", creator: "WorkCanvas.com" },
            layerRef.current.parent?.scaleX()
          );
          exporter.page(layerRef.current, 0, area);
          exporter.save(boardName);
        } else {
          const areas = config.frames.map((frame) => getAreaToExport(layerRef.current as Konva.Layer, frame.id));
          exporter.prepareDocument(
            areas,
            { title: boardName, author: user?.name ?? "", creator: "WorkCanvas.com" },
            layerRef.current.parent?.scaleX()
          );
          for (let i = 0; i < config.frames.length; i++) {
            exporter.page(layerRef.current, i, areas[i], config.frames[i].id);
          }
          exporter.save(boardName);
        }
      }
    } catch (error) {
      console.error("Error exporting canvas", error);
      onError && onError("Error exporting canvas");
    } finally {
      setExportingCanvas(false);
    }
  }

  function setStageReady() {
    dispatch({ type: "set_stage_ready", payload: null });
    setIsStageReady(true);
  }

  const copyDataToStorage = (data: string) => {
    if (appState.user && appState.account) {
      data = encryptData(data, appState.user!.id + appState.account!.id);
      writeLocalStorage("clipboard", data);
    } else {
      copiedElements.current = data;
    }
    // we write empty string to clipboard to clear it
    // thus, on the next paste operation, we can check if clipboard is empty
    // and if so look into localStorage["clipboard"] or copiedElements.current
    navigator.clipboard.writeText("").catch(() => {
      // it's posssible we don't have write-permissions to clipboard,
      // nothing to do about it
    });
  };

  async function handleDuplicate() {
    if (!stageState?.selectedNodes || stageState?.selectedNodes.length == 0) {
      return;
    }
    await handleCopy(stageState?.selectedNodes);
    const { x, y, direction } = findNewPosition({
      previousDirection: lastDuplicateDirection.current,
      selectedNodes: stageState?.selectedNodes,
      allNodes: getSelectableNodes(),
    });
    lastDuplicateDirection.current = direction;
    handlePaste({
      x: x,
      y: y,
    });
  }

  const handleCopy = async function (targetNodes: Iterable<Konva.Node>) {
    const nodes = getClonedSelectedCanvasNodes([...targetNodes]);
    if (!nodes || nodes.length == 0) {
      return;
    }
    const copy = await getCopyPasteData(nodes);
    copyDataToStorage(JSON.stringify(copy));
  };

  const handleCut = async function (targetNodes: Iterable<Konva.Node>) {
    const selectedNodes = [...targetNodes];
    const unlockedNodes = selectedNodes.filter((node: any) => (node.attrs.element.lock ?? 0) == LockType.None);
    let nodes = getClonedSelectedCanvasNodes(unlockedNodes);
    if (!nodes || nodes.length == 0) {
      return;
    }
    nodes = nodes.filter((n) => (n.attrs.element.lock ?? 0) == LockType.None);
    if (!nodes) {
      return;
    }

    const copy = await getCopyPasteData(nodes);
    copyDataToStorage(JSON.stringify(copy));
    handleDeleteElements(nodes!);

    selectedIdsRef.current = [];
    disableTransformer();
  };

  // This is the main entry point for handling copy and cut events.
  // we do these checks here:
  // 1. e.target is not the window - context menu triggers copy&cut events on the window, just because
  //    some elements listen to them. they are not to be handled here
  // 2. e.target contains the canvas - we don't want to handle copy&cut events on input and text fields.
  // 3. selectedNodes is not all org-chart-nodes - org charts handle their own copy/cut/paste
  const handlerCopyCut = (e: Event) => {
    const dontHandle =
      e.target == window ||
      !(e.target as HTMLElement).contains(document.getElementsByTagName("canvas")[0]) ||
      !stageState?.selectedNodes ||
      stageState?.selectedNodes.every(isOrgchartNode) ||
      tableSelection?.cells?.length ||
      internalSelectionValue.length;
    if (dontHandle) return;
    if (e.type == "copy") {
      e.preventDefault();
      handleCopy(stageState?.selectedNodes ?? []);
    } else if (e.type == "cut") {
      e.preventDefault();
      handleCut(stageState?.selectedNodes ?? []);
    } else {
      console.warn("unknown event type");
    }
  };

  async function addImagesToCanvas(files: File[], position?: Point) {
    if (!board?.accountId || !board?.documentId) {
      onError && onError("Can't reach server. Please check your connection and try again");
      return;
    }

    try {
      const pos = position ?? getDistanceFromEvent();
      performUploadFromHook(files, pos);
    } catch (error) {
      console.error(error);
      onError && onError("Something went wrong, please try again");
    }
  }

  // using the "ref to local-function" trick so the paste/drop event handlers
  // will always have access to a live function, not a stale one
  const addImagesToCanvasRef = useRef(addImagesToCanvas);
  addImagesToCanvasRef.current = addImagesToCanvas;

  function parseDataTransfer(eventData: DataTransfer): DataTransferItems {
    const items = new DataTransferItems();
    items.text = eventData.getData("text/plain");
    for (let i = 0; i < eventData.files.length; i++) {
      if (eventData.files[i].type.startsWith("image/") || eventData.files[i].type.startsWith("application/pdf")) {
        items.imageFiles!.push(eventData.files[i]);
      } else {
        items.unknowns!.push(eventData.files[i].type);
      }
    }
    return items;
  }

  async function addMiroMigratedElements(migratedElements: NewElementData[]) {
    const newIds = await onAddElements(migratedElements, true, "miro-migration");
    setSelectedIds(newIds);
  }

  async function addPastedElements(items: DataTransferItems, position?: Point) {
    const { text, imageFiles, canvasElements, unknowns } = items;

    if (text && text.length > 0) {
      const textBlockElement = placeTextBlock({}, getDistanceFromEvent(), text);
      return onAddElements(
        [
          {
            id: textBlockElement.id,
            type: consts.CANVAS_ELEMENTS.TEXT_BLOCK,
            element: textBlockElement.textBlock,
          },
        ],
        true,
        "copy-paste"
      );
    }
    if (imageFiles && imageFiles.length > 0) {
      tracking.trackEvent(consts.TRACKING_CATEGORY.CANVAS_ACTION, "image-dropped", imageFiles.length);
      return addImagesToCanvasRef.current(imageFiles, position);
    }

    if (canvasElements) {
      const { elementsData, metadata } = canvasElements;

      if (elementsData && Object.keys(elementsData).length > 0) {
        const timelineIds = Object.keys(elementsData).filter((id) => {
          return isIdOfType(id, consts.CANVAS_ELEMENTS.TIMELINE);
        });

        if (timelineIds.length > 0) {
          onError && onError("Timeline is no longer supported and cannot be copied or pasted.");
        }

        const filteredElementsData: { [elementId: string]: any } = {};
        for (const id in elementsData) {
          if (!isIdOfType(id, consts.CANVAS_ELEMENTS.TIMELINE)) {
            filteredElementsData[id] = elementsData[id];
          }
        }

        if (Object.keys(filteredElementsData).length === 0) {
          return [];
        }

        const { x, y } = position ?? getDistanceFromEvent();
        const elements = new Map<string, any>();
        for (const id in filteredElementsData) {
          const element = filteredElementsData[id];
          // Some elements (table-cells, mindmap nodes) don't have x,y since they are placed
          // by their container. In such a case we don't want to create x,y.
          // We should clearly seperate such elements since this code is repeated elsewhere
          if (Number.isFinite(element.x) && Number.isFinite(element.y)) {
            element.x += x;
            element.y += y;
          }

          if (element.flags !== undefined) {
            element.flags = off(element.flags, Flags.CreatedBySystem);
          }

          elements.set(id, element);
        }
        // if metadata includes image files, with uploadUrl that's not data url,
        // copy the images to this canvas's folder
        if (metadata.length > 0) {
          const copyReqDescriptor = prepareCopyForeignImages({ metadata, documentId: board!.documentId });
          if (Object.keys(copyReqDescriptor).length > 0) {
            try {
              const copyResults = await copyFiles(copyReqDescriptor);
              for (const metadatum of metadata) {
                if (
                  "url" in metadatum[1] &&
                  metadatum[1].url in copyReqDescriptor &&
                  copyResults[metadatum[1].url] == true
                ) {
                  metadatum[1].url = copyReqDescriptor[metadatum[1].url];
                }
              }
            } catch {
              console.error("copying images in GCS failed");
            }
          }
          await syncService?.mutate.mergeMetadata(metadata);
        }
        return copyElements(elements, { x: 0, y: 0 }, false);
      }
    }

    if (unknowns && unknowns.length > 0) {
      onError && onError("Sorry, we only support images in canvas right now.");
      tracking.trackEvent(consts.TRACKING_CATEGORY.CANVAS_ACTION, "unsupported-file-dropped", JSON.stringify(unknowns));
      return [];
    }
  }

  const handlePaste = async (arg: Event | Point) => {
    if (isReadOnly) return;
    if (
      ((stageState?.selectedNodes?.length ?? 0) > 0 && stageState?.selectedNodes?.every(isOrgchartNode)) ||
      tableSelection?.cells?.length ||
      internalSelectionValue.length > 0
    ) {
      return;
    }

    const gotEvent = arg instanceof Event;
    const e: Event | null = gotEvent ? (arg as Event) : null;

    if (e && e.target == window) {
      // clipboard events that are sent to window are triggered by context-menu,and should be ignored
      // by canvas-stage since they are meant for other elements to capture
      return;
    }

    const pos = e ? getDistanceFromEvent() : (arg as Point);
    let eventData;
    let dataitems = new DataTransferItems();

    if (e && e.type == "paste") {
      if (!(e.target as HTMLElement)?.contains(document.getElementsByTagName("canvas")[0])) return;
      e.preventDefault();
      eventData = (e as ClipboardEvent).clipboardData;
      const eventTextHtml = eventData?.getData("text/html") ?? "";
      if (containsMiroData(eventTextHtml)) {
        onError && onError("Migrating widgets");
        const newCanvasElements = pasteFromMiro(eventTextHtml, pos);
        tracking.trackEvent(
          consts.TRACKING_CATEGORY.MIRO_MIGRATION,
          "paste_from_miro",
          board?.accountId,
          newCanvasElements.length
        );
        await addMiroMigratedElements(newCanvasElements);
        return;
      }
    }
    // We opt not to use the system clipboard for 2 reasons:
    // 1. only chrome supports writing data that isn't text or image to the clipboard
    // 2. it requires the user to give us permission - bad UX
    // instead we...
    // - check the event(in case we got triggered by keyboard shortcut)
    // - use localStorage as a poor man's clipboard
    if (eventData) {
      dataitems = parseDataTransfer(eventData);
    }

    if (!gotEvent && dataitems.text.length == 0 && dataitems.imageFiles.length == 0 && dataitems.unknowns.length == 0) {
      try {
        if (navigator.clipboard?.read) {
          // check the browser has clipboard API (old browsers doesn't have it)
          // get all clipboard items
          const clipboardItems = await navigator.clipboard.read();
          // we can extract text/plain, text/html (from miro) and image/* items
          // I wish I knew which clipboard item was places most recently in the clipboard
          const maybeMiroData = await clipboardItems
            .find((item) => item.types.includes("text/html"))
            ?.getType("text/html")
            .then((blob) => blob.text());
          if (maybeMiroData && containsMiroData(maybeMiroData)) {
            onError && onError("Migrating widgets");
            const newCanvasElements = pasteFromMiro(maybeMiroData, pos);
            tracking.trackEvent(
              consts.TRACKING_CATEGORY.MIRO_MIGRATION,
              "paste_from_miro",
              board?.accountId,
              newCanvasElements.length
            );
            await addMiroMigratedElements(newCanvasElements);
            onError && onError(null);
            return;
          }

          // look for images - I never get images, but I'll still check
          const imageItems = clipboardItems.filter((item) => item.types.some((type) => type.startsWith("image/")));
          const imageFiles = [];
          for (const item of imageItems) {
            const type = item.types[0];
            const fileType = type.split("/")[1];
            const blob = await item.getType(type);
            const file = new File([blob], "clipboard-image" + fileType, { type: type });
            imageFiles.push(file);
          }

          // look for text
          const textItems = clipboardItems.filter((item) => item.types.some((type) => type == "text/plain"));
          const texts = textItems.map((item) => item.getType("text/plain").then((blob) => blob.text()));
          dataitems = {
            text: (await Promise.all(texts)).join("\n"),
            imageFiles,
            unknowns: [],
            canvasElements: null,
          };
        }
      } catch {
        //clipboard could be empty or we don't have read-permissions
      }
    }

    if (dataitems.text.length == 0 && dataitems.imageFiles.length == 0 && dataitems.unknowns.length == 0) {
      let as_string: string | null = null;
      if (appState.user && appState.account) {
        let savedData = await readLocalStorage("clipboard");
        if (savedData) {
          as_string = decryptData(savedData, appState.user!.id + appState.account!.id);
        }
      } else {
        as_string = copiedElements.current;
      }
      if (as_string) dataitems.canvasElements = JSON.parse(as_string);
    }

    //TODO: unify all return types of this function
    const addElementsPromise = addPastedElements(dataitems, pos);
    if (addElementsPromise) {
      addElementsPromise.then((ids) => {
        selectNewElements(ids);
        window.dispatchEvent(new CustomEvent(EVT_ELEMENT_DROP, { detail: { x: pos.x, y: pos.y, ids } }));
        //TODO: sending a single position {x,y} in the event is slightly wrong. Each element is placed relative to
        // where it was when copy started. Each elements is positioned differently now, not exactly at (x,y).
      });
    }
  };

  const handlerDrop = (e: DragEvent) => {
    if ((e.target as HTMLElement).tagName != "CANVAS") return;
    // if (!(e.target as HTMLElement).contains(document.getElementsByTagName("canvas")[0])) return;
    e.preventDefault();

    // should be after `preventDefault` to prevent the browser from opening the file
    if (isReadOnly) return;

    const eventData = (e as DragEvent).dataTransfer;
    if (!eventData) {
      return;
    }

    {
      const d = eventData.getData("application/x-canvas-color");
      if (isFinite(parseInt(d))) {
        // a custom color was just dropped over the stage. we delete it
        tracking.trackAnalyticsEvent("custom_color_deleted", {});
        removeCustomColorForUser(parseInt(d));
        return;
      }
    }

    const mousePosInCanvasElement = { x: e.offsetX, y: e.offsetY };
    const transform = layerRef.current?.getAbsoluteTransform().copy();
    transform?.invert();
    const mousePosInStage = transform?.point(mousePosInCanvasElement);
    const dataitems = parseDataTransfer(eventData);
    const addElementsPromise = addPastedElements(dataitems, mousePosInStage);
    if (addElementsPromise && mousePosInStage) {
      addElementsPromise.then((ids) => {
        //! This won't work for dropped images. addPastedElements starts the upload process
        //! by calling uppy.upload(), and the elements are created only after uppy.complete event
        //! happens. that means the elements are defined, there are no ids!
        selectNewElements(ids);
        window.dispatchEvent(
          new CustomEvent(EVT_ELEMENT_DROP, { detail: { x: mousePosInStage.x, y: mousePosInStage.y, ids } })
        );
        //TODO: sending a single position {x,y} in the event is slightly wrong. Each element is placed in a different position
      });
    }
  };

  // General listeners for copy/cut/paste/drop. Works fine, TS is hallucinating.
  // @ts-expect-error (See above)
  useEventListener("copy", handlerCopyCut, window);
  // @ts-expect-error (See above)
  useEventListener("cut", handlerCopyCut, window);
  // @ts-expect-error (See above)
  useEventListener("paste", handlePaste, window);
  // @ts-expect-error (See above)
  useEventListener("drop", handlerDrop, window);

  //region Keyboard shortcuts

  const arrowKeys = useRef<number>(0); // Tracks which arrow keys are being held down for moving the stage

  // Catch text input (not special keys) that's on canvas-stage, and start editing
  // current selection.
  useUnhandledTextInput((event) => {
    if (editingElementId) {
      return;
    }
    let idToEdit = null;
    if (tableSelection?.cells?.length == 1) {
      const { col, row } = tableSelection.cells[tableSelection.cells.length - 1];
      let idMaker = new TableIds(tableSelection.id);
      idToEdit = idMaker.shortId(col, row);
    } else if (selectedElementIds?.length == 1) {
      idToEdit = selectedElementIds[0];
    }
    if (idToEdit) {
      event.stopPropagation();
      setEditingElementId(idToEdit);
    }
  });

  const handleKeyboardShortcut = useCallback(
    (shortcut: CanvasKeyboardShortcut) => {
      switch (shortcut) {
        case CanvasKeyboardShortcut.delete: {
          !editingElementId && handleDeleteElements();
          break;
        }
        case CanvasKeyboardShortcut.esc: {
          if (isDraggingElement) {
            setIsDraggingElement(false);
          }

          // If elements are being moved, call onMoveElements with the current delta
          if (stageState?.movingElements?.kind === MovingStatus.Ongoing && dragInfo.current) {
            const delta = {
              x: dragInfo.current.movementX,
              y: dragInfo.current.movementY,
            };
            onMoveElements(stageState.movingElements.ids, delta);

            // Reset drag state
            setStageState((previous) => ({ ...previous, movingElements: null }));
            dragInfo.current.movementX = 0;
            dragInfo.current.movementY = 0;
          }

          setStageState((previous) => ({ ...previous, ghostElement: null }));
          setCursorType(CursorType.pointer);
          clearSelected();
          setEditingElementId(null);
          setShowSideMenuOverflow(false);
          drawingProps.current.id = null;
          drawingProps.current.initialPosition = null;
          break;
        }
        case CanvasKeyboardShortcut.enter: {
          if (tableSelection?.cells && tableSelection?.cells.length == 1) {
            const { col, row } = tableSelection.cells[tableSelection.cells.length - 1];
            let idMaker = new TableIds(tableSelection.id);
            setEditingElementId(idMaker.shortId(col, row));
            return;
          }
          if (selectedElementIds.length === 1 && editingElementId !== selectedElementIds[0]) {
            const selectedNode = getNodeById(selectedElementIds[0]);
            if (!selectedNode || !!selectedNode.attrs.element.lock) break; // We cannot edit locked elements
            setEditingElementId(selectedElementIds[0]);
          }
          break;
        }
        case CanvasKeyboardShortcut.undo: {
          onUndo();
          break;
        }
        case CanvasKeyboardShortcut.redo: {
          onRedo();
          break;
        }
        case CanvasKeyboardShortcut.selectAll: {
          if (!editingElementId) {
            selectAllSelectable();
          }
          break;
        }
        case CanvasKeyboardShortcut.zoomIn: {
          const zoomInStep = (stageRef.current?.getStage().scaleX() ?? 1) * -consts.ZOOM_STEP;
          zoomStage(stageRef.current?.getStage(), zoomInStep, "viewport-center");
          break;
        }
        case CanvasKeyboardShortcut.zoomOut: {
          const zoomOutStep = (stageRef.current?.getStage().scaleX() ?? 1) * consts.ZOOM_STEP;
          zoomStage(stageRef.current?.getStage(), zoomOutStep, "viewport-center");
          break;
        }
        case CanvasKeyboardShortcut.resetZoom: {
          const zoomResetStep = (stageRef.current?.getStage().scaleX() ?? 1 - 1) * 100;
          zoomStage(stageRef.current?.getStage(), zoomResetStep, "viewport-center");
          break;
        }
        case CanvasKeyboardShortcut.moveTool: {
          // This shortcut is disabled when you have exactly one element
          // selected, as a temporary workaround for the fact that you
          // need to double click a shape to edit it.
          // TODO: Implement edit on type and then re-enable this
          if (selectedElementIds.length === 1) return;

          setCursorType((previous) => {
            if (previous === CursorType.pointer) {
              return CursorType.move;
            } else {
              return CursorType.pointer;
            }
          });
          break;
        }
        case CanvasKeyboardShortcut.zoomToFit: {
          const viewBox = fitToStage(stageRef);
          if (viewBox) {
            onChangePosScale(viewBox);
          }
          break;
        }
        case CanvasKeyboardShortcut.zoomToFitSelection: {
          const viewBox = fitToSelectedElements(stageRef, selectedElementIds);
          if (viewBox) {
            onChangePosScale(viewBox);
          }
          break;
        }
        case CanvasKeyboardShortcut.duplicate: {
          handleDuplicate();
          break;
        }
        default: {
          break;
        }
      }
    },

    [
      clearSelected,
      editingElementId,
      handleDeleteElements,
      handleDuplicate,
      isDraggingElement,
      onChangePosScale,
      onRedo,
      onUndo,
      selectAllSelectable,
      selectedElementIds,
      setCursorType,
      setEditingElementId,
      setIsDraggingElement,
      setShowSideMenuOverflow,
      zoomStage,
    ]
  );

  useKeyboardShortcuts(handleKeyboardShortcut);

  const handleSpaceDown = useCallback(() => {
    setCursorType(CursorType.move);
  }, [setCursorType]);

  const handleSpaceUp = useCallback(() => {
    if (cursorType === CursorType.move) {
      let newCursorType = prevCursorType;
      if (!newCursorType || newCursorType === CursorType.move) {
        newCursorType = CursorType.pointer;
      }
      setCursorType(newCursorType);
    }
  }, [cursorType, setCursorType, prevCursorType]);

  useHoldKeys(CanvasKeyboardShortcut.space, handleSpaceDown, handleSpaceUp);

  const handleArrowKeyDown = useCallback(
    (shortcut: CanvasKeyboardShortcut, e: KeyboardEvent) => {
      arrowKeys.current = setKey(arrowKeys.current, e.key);

      let moveStep = 5;
      if (e.shiftKey) {
        moveStep *= 10; // Shift key increases the step size
      }

      let dx: number = 0,
        dy: number = 0;

      switch (shortcut) {
        case CanvasKeyboardShortcut.left: {
          dx = -moveStep;
          break;
        }
        case CanvasKeyboardShortcut.right: {
          dx = moveStep;
          break;
        }
        case CanvasKeyboardShortcut.up: {
          dy = -moveStep;
          break;
        }
        case CanvasKeyboardShortcut.down: {
          dy = moveStep;
          break;
        }
      }

      if (selectedElementIds.length === 0) {
        setStageMoving(true);
      } else {
        moveSelectionByKeyboard(dx, dy);
      }
    },
    [moveSelectionByKeyboard, selectedElementIds.length]
  );

  const handleArrowKeyUp = useCallback((_, e: KeyboardEvent) => {
    arrowKeys.current = unsetKey(arrowKeys.current, e.key);
    if (arrowKeys.current == 0) {
      setStageMoving(false);
    }
  }, []);

  useHoldKeys(
    [CanvasKeyboardShortcut.left, CanvasKeyboardShortcut.right, CanvasKeyboardShortcut.up, CanvasKeyboardShortcut.down],
    handleArrowKeyDown,
    handleArrowKeyUp
  );

  const moveStageByKeyboard = (dt: number) => {
    const [dx, dy] = arrowKeysToVector(arrowKeys.current);
    const velocity = Math.max(stageRef.current?.width() ?? 0, stageRef.current?.height() ?? 0) / 5;
    const speedup = modifierKeys.shift ? 3 : 1;
    const x = posScale.x - dx * dt * speedup * velocity;
    const y = posScale.y - dy * dt * speedup * velocity;
    onChangePosScale({ scale: posScale.scale, x, y });
  };
  useRequestAnimation(moveStageByKeyboard, stageMoving);

  //endregion

  function selectAllSelectable() {
    if (isReadOnly) return;
    const allElementsIds = layerRef.current
      ?.find(
        (node: Konva.Node) =>
          node.attrs.isCanvasElement && node.attrs.isSelectable && !node.attrs.isFrameTitle && !node.attrs.isTableTitle
      )
      .toArray()
      .map((node: Konva.Node) => node.attrs.id);
    if (allElementsIds && allElementsIds.length > 0) {
      selectedIdsRef.current = allElementsIds;
      enableTransformer();
      enableGroupsBounds();
      enableElementBounds();
      onElementsSelected(selectedIdsRef.current);
      updateTopElementHasLink();
    }
  }

  function clearSelected() {
    disableTransformer();
    selectedIdsRef.current = [];
    disableElementBounds();
    disableGroupsBounds();
    elementBoundsLayerRef.current?.batchDraw();
    onElementsSelected([]);
  }

  function selectNewElements(ids: string[] | void) {
    if (isReadOnly) return;

    if (ids && ids.length > 0) {
      selectedIdsRef.current = ids;
      onElementsSelected(selectedIdsRef.current);
      enableTransformer();
      needToRefreshBoundsRef.current = boundsRefreshOperation.selectNewElements;
      setTimeout(() => {
        enableTransformer();
      }, 50);
    }
  }

  function addGroupsToSet(nodes: Iterable<Konva.Node>) {
    const result = new Set<Konva.Node>();
    const groupIds = new Array<string>();
    for (const node of nodes) {
      result.add(node);
      const groupid = getGroupForElement(node.id());
      if (groupid) {
        groupIds.push(groupid);
      }
    }
    // Performance issue: getNodesByIds is slow.
    const groupNodesList = getNodesByGroupIds(groupIds);
    for (const groupNode of groupNodesList) {
      result.add(groupNode);
    }
    return result;
  }

  // Update whether the topmost selected element has a link
  // Only checks for specific element types: text, task card, and frame
  // This is used to add padding to ghost transformer for link badges
  function updateTopElementHasLink() {
    if (selectedIdsRef.current.length === 0) {
      topElementHasLinkRef.current = false;
      return;
    }

    // Element types that we check for links (only these types have link badges)
    const relevantTypes = new Set<string>([
      consts.CANVAS_ELEMENTS.TEXT_BLOCK,
      consts.CANVAS_ELEMENTS.TASK_CARD,
      consts.CANVAS_ELEMENTS.FRAME,
    ]);

    // Get all selected nodes with their positions, type, and link status
    const nodesWithPositions = selectedIdsRef.current
      .map((id) => {
        const node = layerRef.current?.findOne(`#${id}`);
        if (!node) return null;
        const elementType = node.attrs.type || node.attrs.element?.type;
        return {
          id,
          y: node.y(),
          type: elementType as string,
          hasLink: node.attrs.element?.link && node.attrs.element.link.length > 0,
        };
      })
      .filter((item): item is { id: string; y: number; type: string; hasLink: boolean } => item !== null);

    if (nodesWithPositions.length === 0) {
      topElementHasLinkRef.current = false;
      return;
    }

    // Find the minimum Y position (topmost element)
    const minY = Math.min(...nodesWithPositions.map((item) => item.y));

    // Check if any element with a link is at or near the top position
    // AND is one of the relevant types (text, task, frame)
    // Using small epsilon for floating point comparison
    const epsilon = 0.1;
    topElementHasLinkRef.current = nodesWithPositions.some(
      (item) => item.hasLink && relevantTypes.has(item.type) && Math.abs(item.y - minY) < epsilon
    );
  }

  function setSelectedIds(
    ids: string[] | ((previous: string[]) => string[]),
    includeGroupsOfElements = false,
    updateBounds = true
  ) {
    if (isReadOnly) return;

    let newSelectedIds;
    if (typeof ids == "function") {
      newSelectedIds = ids(selectedIdsRef.current);
    } else {
      newSelectedIds = ids;
    }

    const byGroups: Record<string, any> = {};
    if (includeGroupsOfElements) {
      for (const id of newSelectedIds) {
        const groupid = getGroupForElement(id);
        if (groupid) {
          byGroups[groupid] = groups[groupid];
        } else {
          byGroups["__not_grouped__"] ??= [];
          byGroups["__not_grouped__"].push(id);
        }
      }
      newSelectedIds = Object.values(byGroups).flat();
    }

    newSelectedIds = newSelectedIds.filter((id) => !id.includes(consts.CANVAS_ELEMENTS.TABLE_CELL));

    selectedIdsRef.current = newSelectedIds;
    enableGroupsBounds();
    enableElementBounds();

    if (updateBounds) {
      needToRefreshBoundsRef.current = boundsRefreshOperation.selectNewElements;
    }
  }

  // I need the frames object inside the getNodesForCopy function, but I keep
  // getting stale data !!! why ???
  const getFrameElements = (id: string) => frames[id];
  const getFrameElementsRef = useRef<any>();
  getFrameElementsRef.current = getFrameElements;

  const getNodesForCopy = (nodes?: Konva.Node[]) => {
    if (!nodes || nodes.length == 0) {
      if (stageState?.selectedNodes == null || stageState?.selectedNodes.length == 0) return;
      nodes = stageState?.selectedNodes;
    }
    if (!nodes || nodes.length == 0) return null;

    nodes = nodes.filter((x: any) => x.attrs.isCanvasElement && !x.attrs.isFrameTitle);
    // 1. first collect all elements from selected frames
    // I collect frames within frames also, though we don't support it yet
    const isFrame = (n: Konva.Node) => n.attrs.isFrame && !n.attrs.isFrameTitle;

    function appendToSet<T>(a: Set<T>, b: Iterable<T>) {
      for (const x of b) a.add(x);
      return a;
    }

    const nodesSet = new Set(nodes);
    const visited: Node<NodeConfig>[] = [];

    function collectFrameContents(nodesToCheck: Iterable<Konva.Node>) {
      for (const node of nodesToCheck) {
        if (isFrame(node) && !visited.includes(node)) {
          visited.push(node);
          const innerNodesIds = getFrameElementsRef.current(node.attrs.id);
          if (innerNodesIds && innerNodesIds.length > 0) {
            const innerNodes =
              layerRef.current
                ?.find((n: any) => innerNodesIds.includes(n.attrs.id) && !n.attrs.isFrameTitle && !n.attrs.isTableTitle)
                .toArray() ?? [];
            appendToSet(nodesSet, innerNodes);
            collectFrameContents(innerNodes);
          }
        }
      }
    }

    collectFrameContents(nodes);

    //collect table-cells (should collect contained elements?)
    //TODO ofirc does this happen?
    {
      const containedElementsIds = new Set();
      for (const node of nodesSet) {
        if (isIdOfType(node.attrs.id, consts.CANVAS_ELEMENTS.TABLE)) {
          const cells =
            layerRef.current?.find((n: any) => n.attrs.isTableCell && n.attrs.tableId == node.attrs.id).toArray() ?? [];
          appendToSet(nodesSet, cells);
          // catch contained elements also
          appendToSet(
            containedElementsIds,
            cells.flatMap((cellNode: Konva.Node) => cellNode.attrs.cell?.containedIds)
          );
        }
      }
      if (containedElementsIds.size) {
        appendToSet(
          nodesSet,
          layerRef.current
            ?.find((n: any) => n.attrs.isCanvasElement && containedElementsIds.has(n.attrs.id))
            .toArray() ?? []
        );
      }
    }

    // 2. now look for connectors within the set that were not in the selection,
    // but they are attached to shapes in the selection (on both ends)
    const nodesAsArr = [...nodesSet],
      nodesIds = nodesAsArr.map((x) => x.id()),
      connectorsIds = nodesAsArr.flatMap(getConnectedConnectors),
      notInCurrentSelection = connectorsIds.filter((id) => !nodesIds.includes(id)),
      connectorNodes =
        notInCurrentSelection.length == 0
          ? []
          : getSelectableNodes(
              (node) =>
                notInCurrentSelection.includes(node.attrs.id) &&
                node.attrs.element.connectedShapes.every((shape: any) => shape != null && nodesIds.includes(shape.id))
            );
    return nodesAsArr.concat(connectorNodes);
  };

  function getClonedSelectedCanvasNodes(nodes?: any[]): Konva.Node[] | null {
    const n = getNodesForCopy(nodes);
    return n ? n.filter((n) => n.getStage()) : null;
  }

  /**
   * Prepares Konva nodes for copy/paste by calculating properties.
   * @param nodes - Konva nodes to prepare for copy/paste.
   * @returns - An object containing metadata and elements data (NOT nodes!)
   */
  async function getCopyPasteData(nodes: Konva.Node[]) {
    const selectableNodes = getSelectableNodes();
    const ids = new Set(nodes.filter((n) => n.attrs.isCanvasElement).map((node) => node.attrs.id));

    const relatedNodes = selectableNodes.filter((node) => {
      return node.attrs.element.containerId && ids.has(node.attrs.element.containerId);
    });

    nodes = nodes.concat(relatedNodes);

    // TODO: I can move this calculation to copyElements, once I have measureElement function
    let elementsData: { [elementId: string]: any } = {};
    let metadata: any = [];
    const searchMetadataItems = new Set<string>();
    const nodesRects = nodes.map((n) => BoundingBox.fromRect(measureNode(n)));
    const area = nodesRects.reduce((acc, rect) => acc.expandRect(rect), new BoundingBox());
    const centerX = area.center.x,
      centerY = area.center.y;
    const framesBeingCopied = new Set(nodes.filter((node) => node.attrs.isFrame).map((node) => node.attrs.id));

    // When copying a dynamic element, we copy all of its derived texts.
    // We track the parents here to know which texts to copy.
    const dynamicElements = new Set<string>(
      nodes
        .filter((node) => {
          return getDerivedGroupId(node) && consts.ELEMENTS_SUPPORTING_DATA_LAYER.includes(getElementTypeForNode(node));
        })
        .map((node) => node.attrs.id)
    );

    // Add all the children of the dynamic elements to the nodes set.
    const childNodes = selectableNodes.filter((node) => {
      return (
        getDerivedGroupId(node) &&
        dynamicElements.has(node.attrs.element.derivedElementIds?.[0]) &&
        !ids.has(node.attrs.id) // Ensure no duplicates
      );
    });
    nodes = nodes.concat(childNodes);

    // Collect element IDs that are referenced in timeline itemsDates
    // These elements should be excluded from the copy
    const elementsInTimelineItemsDates = new Set<string>();
    for (const node of nodes) {
      if (node.attrs.type == consts.CANVAS_ELEMENTS.TIMELINE && node.attrs.element?.itemsDates) {
        const itemsDates = node.attrs.element?.itemsDates as Record<string, number>;
        for (const elementId in itemsDates) {
          elementsInTimelineItemsDates.add(elementId);
        }
      }
    }

    for (const node of nodes) {
      if (isOrgchartNode(node)) {
        continue;
      }

      // Skip elements that are referenced in timeline itemsDates
      if (elementsInTimelineItemsDates.has(node.attrs.id)) {
        continue;
      }

      if ((node.attrs.element.lock ?? 0) != LockType.None) {
        continue;
      }

      //TODO ofirc can this happen?
      if (node.attrs.isTableCell && node.attrs.cell) {
        const element = structuredClone(node.attrs.cell);
        element.lock = LockType.None;
        // save the copied element
        elementsData[node.attrs.id] = element;
        continue;
      }

      let element: CanvasElementType = structuredClone(node.attrs.element);

      // If we are copying a child of a dynamic element without its parent,
      // Remove all dynamic fields and just copy the plain text.
      if (
        getDerivedGroupId(node) &&
        !consts.ELEMENTS_SUPPORTING_DATA_LAYER.includes(getElementTypeForNode(node)) &&
        !dynamicElements.has(node.attrs.element.derivedElementIds?.[0])
      ) {
        delete element.derivedElementIds;
        delete element.groupId;
        delete element.groupHistory;
        (element as TextBlock).text = TextUtils.getTextFromNode(node);
      }

      // remove the element from its frame if the frame isn't part of the copy operation
      if (framesBeingCopied.has(element.frameId) == false) delete element.frameId;

      // get metadata needed for files and task-cards
      if (node.attrs.type == consts.CANVAS_ELEMENTS.FILE) {
        const fileId = (node.attrs.element as any).fileId;
        const isOldStyle = Number.isFinite(Number(fileId));
        if (!isOldStyle) {
          searchMetadataItems.add(dbKey(fileId, FileMetadataType));
        }
      }

      // collect metadata about images embedded in org charts
      if (node.attrs.type == consts.CANVAS_ELEMENTS.ORG_CHART) {
        // org charts contain images also.
        // legacy org charts kept the url in the node's data record.
        // newer org charts use cMetadata-File- entry like images.
        // we need to collect metadata from legacy and new, and upgrade old to new.
        const { newMetadataEntries, existingMetadataEntries } = upgradeOrgChartImageUrls(element as OrgChartElement);
        metadata = metadata.concat(newMetadataEntries);
        existingMetadataEntries.forEach((entry) => searchMetadataItems.add(dbKey(entry, FileMetadataType)));
      }

      if (node.attrs.type == consts.CANVAS_ELEMENTS.CONNECTOR) {
        const p1 = node.attrs.startpoint,
          p2 = node.attrs.endpoint;
        (element as Connector).points = [
          { x: p1.x, y: p1.y },
          { x: p2.x, y: p2.y },
        ];
      }

      if (node.attrs.type == consts.CANVAS_ELEMENTS.TASK_CARD) {
        const tags = (node.attrs.element as any).tags;
        if (tags) {
          for (const tag of tags) {
            if (tag.length > 0)
              // we saw empty strings in the tags (bug); so ignore those
              searchMetadataItems.add(dbKey(tag, TagType));
          }
        }
      }

      if (node.attrs.type === consts.CANVAS_ELEMENTS.MINDMAP) {
        const mindmapNode = node.attrs.element as MindmapUtils.MindmapNodeTree;
        const parsedMindmap = mindmapNodeSchema.safeParse(mindmapNode);
        if (!parsedMindmap.success) {
          console.error("Error parsing mindmap node", parsedMindmap.error);
          continue;
        }
        if (elementsData[mindmapNode.id]) {
          // we already have this node because one of its ancestors is also selected
          continue;
        }

        const mindmap = parsedMindmap.data;
        // make element root
        mindmap.rootId = mindmapNode.id;
        // make sure it's not collapsed
        mindmap.collapsed = false;
        // set the parsed mindmap as the element
        element = mindmap;

        // add all descendants to the elementsData
        const descendants = MindmapUtils.getNodeDescendants(mindmapNode);
        elementsData = descendants.reduce((acc, node) => {
          // we parse it to remove any extra properties we set to render the mindmap
          const parsedNode = mindmapNodeSchema.safeParse(node);
          if (parsedNode.success) {
            const childNode = parsedNode.data;
            childNode.rootId = mindmapNode.id;
            acc[node.id] = childNode;
          }
          return acc;
        }, elementsData);
      }

      // Give the element new position, relative to the center of the group.
      // Mindmap needs special handling here (see absolutePosition attribute)
      if (isIdOfType(node.attrs.id, consts.CANVAS_ELEMENTS.MINDMAP)) {
        const position = node.getClientRect({ relativeTo: stageRef.current as any, skipShadow: true });
        (element as MindmapNodeElement).x = position.x - centerX + (element as MindmapNodeElement).width / 2;
        (element as MindmapNodeElement).y = position.y - centerY + (element as MindmapNodeElement).height / 2;
      } else {
        element.x = element.x - centerX;
        element.y = element.y - centerY;
      }

      // unlock (after copy everything is unlocked)
      element.lock = LockType.None;

      // remove from frame - will be calculated on paste
      delete element.frameId;

      if (
        (isIdOfType(node.attrs.id, consts.CANVAS_ELEMENTS.INTEGRATION) ||
          isIdOfType(node.attrs.id, consts.CANVAS_ELEMENTS.MONDAY_TASK)) &&
        isElementInGantt(node.attrs.element)
      ) {
        onError && onError("Monday items on Gantt can't be copied");
        continue;
      }

      if (!isGanttSupportedElement(getElementTypeForId(node.attrs.id))) {
        delete element.containerId;
      }

      // save the copied element
      elementsData[node.attrs.id] = element;
    }

    if (searchMetadataItems.size > 0) {
      metadata = metadata.concat(
        await syncService?.getReplicache()?.query(async (tx: ReadTransaction) => {
          const promises = [];
          for (const item of searchMetadataItems) {
            promises.push(tx.get(canvasMetadataPrefix + item).then((entry) => [item, entry]));
          }
          return Promise.all(promises);
        })
      );
      // filter out undefined entries (undefined is missing entry from replicache; paste code assumes that can't happen)
      // it happened to us once because of bug elsewhere, so I prefer to be defensive here
      metadata = metadata.filter(([, value]: [any, any]) => value !== undefined);
    }

    return {
      metadata,
      elementsData,
    } as const;
  }

  function addMetadata(metadata: { [id: string]: any }) {
    syncService?.mutate.putManyMetadata(Object.entries(metadata).map(([id, value]) => ({ id, value })));
  }

  async function copyElements(elements: Map<string, any>, minPosition: Point, isTemplate: boolean) {
    // monday limitation
    const allIds = [...elements.keys()];
    if (!canAddElementsWithMondayLimitation(allIds)) {
      showUpgradeModal && showUpgradeModal("monday-integration-limit");
      return;
    }

    const newGanttNodes = countGanttTaskNodes(
      [...elements.entries()].filter(([id, element]) => {
        const elementType = getElementTypeForId(id);
        return isElementInGantt({ ...element, type: elementType }, true);
      }) as unknown as SupportedGanttElements[]
    );

    const numExistingGanttTasksInCanvas = (await countGanttTaskNodesInReflect(syncService?.getReplicache())) ?? 0;
    if (numExistingGanttTasksInCanvas + newGanttNodes > maxAllowedTasksInPlan && newGanttNodes > 0) {
      showUpgradeModal && showUpgradeModal("gantt");
      return;
    }

    let newNodes = 0;
    for (const [id, element] of elements.entries()) {
      if (isIdOfType(id, consts.CANVAS_ELEMENTS.ORG_CHART)) newNodes += numberOfNodes(element);
    }

    if (newNodes && !isTemplate) {
      const numExistingNodesInCanvas = (await countOrgChartNodesInReflect(syncService)) ?? 0;
      if (newNodes + numExistingNodesInCanvas > maxOrgChartNodes) {
        showUpgradeModal && showUpgradeModal("org-chart-nodes");
        return;
      }
    }
    return copyElements_execute(elements, minPosition, isTemplate);
  }

  async function copyElements_execute(elements: Map<string, any>, minPosition: Point, isTemplate: boolean) {
    const newElements = new Array<NewElementData>();
    const newIdsMap = new Map<string, [string, string]>();
    let nextFrameNumber = frameIds?.length > 0 ? frameIds.length + 1 : 1;

    // template creation had a bug, where connectors to hidden shapes are copied into the template
    // such connectors aren't visible, and shouldn't be copied. We fixed the bug, but we still have
    // in the database old flawed data, so a fix is needed here.
    if (isTemplate) {
      elements.forEach((value, key) => {
        if (value.type == "connector") {
          let remove = false;
          for (const connection of value.connectedShapes) {
            if (connection != null && connection.id != "" && !elements.has(connection.id)) {
              remove = true;
            }
          }
          if (remove) elements.delete(key);
        }
      });
    }

    // copying elements involves the following steps:
    // 1. clone objects data, and create new ids
    //    - check if we're cloning orgchart and user is limited in number of nodes.
    // 2. replace references to old ids with references to new ids
    //    - frames
    //    - connectors (shape ids in connectors, connector ids in shapes)
    //    - mindmap nodes
    //    - table cells
    //    - timeline
    //    - derived data textBlocks
    // 3. create new group ids instead of old groups
    // 4. fix zindex so new elements are above everything else, but still maintain original order between them
    // 5. add new elements
    // 6. connect new elements to pre-existing frames
    // 7. finally, select the new elements

    // first run - create all new elements,
    // and collect some data we'll need for when we create new elements
    const sortedByZindex = [];
    const { x: offsetX, y: offsetY } = minPosition;

    const roundTwo: any[] = [];
    for (const [id, element] of elements) {
      // id element is table-cell, we don't need to create new id here, see up ahead
      if (isIdOfType(id, consts.CANVAS_ELEMENTS.TABLE_CELL)) {
        roundTwo.push([id, element]);
        continue;
      }
      const newElement = copyElement(id, element, offsetX, offsetY);
      const newElementId = dbKey(newElement.id, newElement.type);
      newIdsMap.set(id, [newElementId, newElement.type]);
      const zIndex = newElement.element.zIndexLastChangeTime ?? newElement.element.lastModifiedTimestamp;
      sortedByZindex.push({ zIndex, id: newElement.id });
      newElements.push(newElement);
    }

    sortedByZindex.sort((a, b) => a.zIndex - b.zIndex);

    // second run
    const now = getUnixTimestampUTC();
    const groupReplacements: Record<string, string> = {};

    for (const index in newElements) {
      const newElement = newElements[index];

      // give the new elements higher z-index than anything else, but also maintain relative order between them
      const relativeIndex = sortedByZindex.findIndex((i) => i.id == newElement.id);
      newElement.element.zIndexLastChangeTime = now + relativeIndex;

      replaceGroupsAfterCopy(groupReplacements, newElement.element);
      if (newElement.element.frameId) {
        const newFrameElement = newIdsMap.get(newElement.element.frameId);
        if (newFrameElement) {
          newElement.element.frameId = newFrameElement[0];
        }
      }
      if (newElement.element.containerId) {
        const newContainerElement = newIdsMap.get(newElement.element.containerId);
        if (newContainerElement) {
          newElement.element.containerId = newContainerElement[0];
        }
      }

      // Replace the derivedElementIds field on child elements.
      if (newElement.element.derivedElementIds) {
        newElement.element.derivedElementIds = newElement.element.derivedElementIds.map(
          (id: string) => newIdsMap.get(id)?.[0]
        );
      }

      // Replace the elements mapping on parent dynamic elements
      if (
        consts.ELEMENTS_SUPPORTING_DATA_LAYER.includes(newElement.type as TypeCanvasElement) &&
        newElement.element.dataLayer?.elementMapping
      ) {
        for (const [key, mappedElementId] of Object.entries(newElement.element.dataLayer.elementMapping)) {
          const newId = newIdsMap.get(mappedElementId as string)?.[0];
          if (newId) {
            newElement.element.dataLayer.elementMapping[key] = newId;
          } else {
            delete newElement.element.dataLayer.elementMapping[key];
          }
        }
      }

      if (newElement.type === consts.CANVAS_ELEMENTS.FRAME) {
        const frame = newElement.element as Frame;
        if (!isTemplate) {
          frame.title = getCopiedFrameTitle(frame.title, nextFrameNumber);
          nextFrameNumber += 1;
        }
        newElement.element = frame;
        newElements[index] = newElement;
      } else if (newElement.type === consts.CANVAS_ELEMENTS.TIMELINE && newElement.element.itemsDates) {
        const timeline = newElement.element as StaticTimelineElement;
        timeline.itemsDates = Object.entries(timeline.itemsDates).reduce((acc, [id, date]) => {
          const newId = newIdsMap.get(id)?.[0];
          if (newId) {
            acc[newId] = date;
          }
          return acc;
        }, {} as Record<string, number>);
        newElement.element = timeline;
        newElements[index] = newElement;
      } else if (newElement.type === consts.CANVAS_ELEMENTS.CONNECTOR) {
        const connector = newElement.element as Connector;
        connector.connectedShapes = connector.connectedShapes.map((shape) => {
          if (!shape) {
            return null;
          }
          const [newId, type] = newIdsMap.get(shape.id) || [null, consts.CANVAS_ELEMENTS.SHAPE];
          if (!newId) {
            return null;
          }
          return {
            ...shape,
            id: newId,
            type,
          };
        });
        newElement.element = connector;
        newElements[index] = newElement;
      } else if (newElement.type === consts.CANVAS_ELEMENTS.MINDMAP) {
        const mindmap = newElement.element as MindmapNodeElement;
        if (newIdsMap.has(mindmap.rootId)) {
          mindmap.rootId = newIdsMap.get(mindmap.rootId)![0];
        }
        if (mindmap.parentId && newIdsMap.has(mindmap.parentId)) {
          mindmap.parentId = newIdsMap.get(mindmap.parentId)![0];
        } else {
          delete mindmap.parentId;
        }
        newElement.element = mindmap;
        newElements[index] = newElement;
      } else if (newElement.type === consts.CANVAS_ELEMENTS.GANTT) {
        const gantt = newElement.element as GanttElement;

        gantt.connectors = (gantt.connectors ?? [])
          .map((connector) => {
            const newFromId = newIdsMap.get(connector.from);
            const newToId = newIdsMap.get(connector.to);

            if (!newFromId || !newToId) {
              return null;
            }

            return {
              id: connector.id,
              type: connector.type,
              from: newIdsMap.get(connector.from)![0],
              to: newIdsMap.get(connector.to)![0],
            };
          })
          .filter((connector) => connector !== null) as {
          id: string;
          type: "custom" | "monday";
          from: string;
          to: string;
        }[];
      } else if (newElement.type === consts.CANVAS_ELEMENTS.TEXT_BLOCK) {
        // Update dynamic texts to reference the new parent elements
        const derivedTextMatches = /\${(.*?):(.*?)}/g.exec(newElement.element.text);
        if (derivedTextMatches) {
          const elementId = derivedTextMatches[1];
          const derivedId = derivedTextMatches[2];
          const newElementId = newIdsMap.get(elementId)?.[0];
          if (newElementId) {
            newElement.element.text = `\${${newElementId}:${derivedId}}`;
          }
        }
      } else {
        const element = newElement.element;
        const newConnectorsMap: any = {};
        if (typeof element.attachedConnectors == "object") {
          const entries = Object.entries(element.attachedConnectors || {});
          for (const [key, value] of entries) {
            if (!value) {
              continue;
            }
            const [newConnectorId] = newIdsMap.get(key) || [null];
            if (newConnectorId) {
              newConnectorsMap[newConnectorId] = {
                ...(value as any),
                lineId: newConnectorId,
              };
            }
          }
        }
        newElements[index] = {
          ...newElement,
          element: { ...element, attachedConnectors: newConnectorsMap },
        };
      }
    }

    // if copying table: copy its table-cells and add them to newElements
    // remember that deleted contained elements are still registered in the table.
    const newTableCells = roundTwo.reduce((acc, [id, element]) => {
      const { tableId, col, row } = splitTableCellKey(id);
      const newId = newIdsMap.get(consts.CANVAS_ELEMENTS.TABLE + "-" + tableId);
      if (newId) {
        const newTableId = newId[0].slice(consts.CANVAS_ELEMENTS.TABLE.length + 1);
        const newContainedElements = element.containedIds?.map((id: string) => newIdsMap.get(id)?.[0]).filter(Boolean);
        acc.push({
          id: `${newTableId}/${col}-${row}`,
          type: consts.CANVAS_ELEMENTS.TABLE_CELL,
          element: { ...element, containedIds: newContainedElements },
        });
      }
      return acc;
    }, [] as NewElementData[]);

    const continingGanttId = newElements.find((node) => isElementInGantt({ ...node.element, type: node.type }, true))
      ?.element.containerId;
    if (continingGanttId) {
      trackGanttEvent("gantt_task_created", {
        amount: parseInt(sessionStorage.getItem(continingGanttId) || "0") + 1,
        from: "copy_paste",
        type: "taskCard",
      });
    }

    selectedIdsRef.current = [...newIdsMap.keys()];
    const newIds = await onAddElements(newElements.concat(newTableCells), true, isTemplate ? "template" : "copy-paste");

    // delay to let new elements render - this is very fragile. sometimes the elements
    // aren't rendered after 50 msec
    setTimeout(() => {
      try {
        const [mutatingElements] = calculateElementsAttachingToFrames(newIds);
        for (const [frameId, ids] of mutatingElements) {
          syncService?.mutate.attachToFrame({ ids, frameId });
        }
      } catch {
        // Quietly swallow the errors
      }
    }, 50);

    // Selection of new elements.
    setTimeout(() => {
      setSelectedIds(newIds, !isTemplate);
    }, 50);

    return newIds;
  }

  function handleDeleteElements(nodes?: Konva.Node[], forceDelete = false) {
    if (!nodes || nodes.length === 0) {
      if (stageState?.selectedNodes == null || stageState?.selectedNodes.length == 0) {
        return;
      }
      nodes = stageState?.selectedNodes;
    }

    // Close next element menu if the connector is deleted
    // Otherwise it throws an error when trying to connect the shape
    setNextElementMenuState(null);

    // I'm going to hide each element and collect all the patches into a single big list
    // org-chart nodes require some special care
    const mutations: Record<string, MutationInfo> = {};

    // In case elements are already deleted but are somehow included in the delete request,
    // we add them to this array and not to the mutations. (Can happen with table, or elements with no id)
    // Unless you know what you are doing, you should not add elements to this array.
    let idsToDelete: string[] | null = null;

    for (const node of nodes) {
      if (!forceDelete && node.attrs.element.lock) {
        continue;
      }

      if (!node.attrs.element) {
        idsToDelete ??= [];
        idsToDelete.push(node.attrs.id);
      } else {
        const element = node.attrs.element as CanvasElementType;
        if (isTableNode(node)) {
          idsToDelete ??= [];
          idsToDelete.push(node.attrs.id);
          if (node.attrs.containedIds?.length) {
            // what if the contained-element is another table? or an orgchart? I should recurse.
            idsToDelete = idsToDelete.concat(node.attrs.containedIds);
          }
        } else if (isOrgchartNode(node)) {
          const parsedId = parseOrgChartNodeId(node.attrs.id);
          if (!parsedId) {
            console.warn("could not parse id of org-chart node", node.attrs.id);
            continue;
          }
          const id = parsedId.fullElementId;
          mutations[id] ??= new MutationInfo(node.attrs.element);
          const result = deleteNodeFromOrgChart(mutations[id].element, parsedId.nodeId);
          mutations[id].addMutation(result);
        } else if (isGantt(node)) {
          const containerId = node.attrs.id;

          mutations[containerId] ??= new MutationInfo(node.attrs.element);
          const result = produceWithPatches(element, (draft) => {
            draft.hidden = true;
          });
          mutations[containerId].addMutation(result);

          const elements = getSelectableNodes((i) => (i.attrs.element as TaskCard).containerId == containerId);
          elements.forEach((element) => {
            mutations[element.attrs.id] ??= new MutationInfo(element.attrs.element);

            const result = produceWithPatches(element.attrs.element, (draft: any) => {
              draft.hidden = true;
            });

            mutations[element.attrs.id].addMutation(result as any);
          });
        } else if (isDerivedGroupParent(node)) {
          const groupId = getDerivedGroupId(node);
          if (groupId) {
            const groupElements = getSelectableNodes((node) => getDerivedGroupId(node) === groupId);
            for (const groupedElement of groupElements) {
              const result = produceWithPatches(groupedElement.attrs.element, (draft: any) => {
                draft.hidden = true;
              });
              mutations[groupedElement.attrs.id] = MutationInfo.fromFinalPatch(result as any);
            }
          } else {
            // Should never get here if isDerivedGroupParent returns true
            const result = produceWithPatches(element, (draft: any) => {
              draft.hidden = true;
            });
            mutations[node.attrs.id] = MutationInfo.fromFinalPatch(result as any);
          }
        } else {
          const result = produceWithPatches(element, (draft) => {
            draft.hidden = true;
          });
          mutations[node.attrs.id] = MutationInfo.fromFinalPatch(result);
        }
      }
    }

    const mt = Object.entries(mutations).map(([id, mutation]) => {
      return [id, mutation.patches, mutation.inversePatches] as const;
    });

    patchCanvasElements(mt as [string, any, any][]);
    if (idsToDelete) {
      onDeleteElements(idsToDelete);
    }
    selectedIdsRef.current = [];
    disableTransformer();
    setSelectedIds([]);
    setSelectedElementsIds([]);
    // to clear the connectors anchor that are different layer than connector
    setTimeout(() => {
      layerRef.current?.getStage().batchDraw();
    }, 50);
  }

  function handleWheelScroll(e: KonvaEventObject<WheelEvent>) {
    e.evt.preventDefault();
    const stage = e.currentTarget;
    if (!stage) {
      return;
    }

    // If we are clicking the middle mouse button, we should not zoom.
    if (mouseState.current.middleClick) {
      return;
    }

    // Dismiss context menus so they don't float away
    setContextMenuState(null);
    setNextElementMenuState(null);

    // This makes it easy in the destructuring
    // eslint-disable-next-line prefer-const
    let { deltaX, deltaY, ctrlKey, metaKey, shiftKey } = e.evt;
    const zoomLimit = consts.STAGE_ZOOM_VELOCITY_LIMIT;
    const panFactor = consts.STAGE_PAN_FACTOR;

    const eventType = navMode === "auto-detect" ? MouseUtils.classifyHardware(e.evt, lastWheelEvent.current) : navMode;
    lastWheelEvent.current = { time: new Date(), hardware: eventType };

    // Pressing shift will cause a sideways pan on both mouse and trackpad.
    // Mouse events automatically swap their deltas when SHIFT is pressed.
    if (shiftKey) {
      moveStagePosition(deltaX * panFactor, 0);
      return;
    }

    // Pressing CTRL/CMD zooms in on both mouse and trackpad.
    // I add both deltas together before clamping for a smooth trackpad experience.
    if (ctrlKey || metaKey) {
      deltaY = MathUtils.clamp(deltaX + deltaY, -zoomLimit, zoomLimit);
      zoomStage(stage, stage.scaleX() * deltaY, "mouse");
      return;
    }

    // If we are not pressing any modifier keys, we zoom if using a mouse and pan if using a trackpad.
    if (eventType === "mouse") {
      deltaY = MathUtils.clamp(deltaY, -zoomLimit, zoomLimit);
      zoomStage(stage, stage.scaleX() * deltaY, "mouse");
    } else {
      moveStagePosition(deltaX * panFactor, deltaY * panFactor);
    }
  }

  //TODO: rewrite handleGroup/hanleUngroup using these functions
  const grouper = function (
    nodesOrIds: NodesOrIds,
    { newProps, prevProps } = { newProps: new Map(), prevProps: new Map() }
  ) {
    const newGroup = newGroupId(),
      nodes = getNodesOrSelection(nodesOrIds);
    for (const node of nodes) {
      const id = node.attrs.id;
      const { groupId = "", groupHistory = [] } = node.attrs.element;
      prevProps.set(id, { groupId, groupHistory });
      const newHistory = groupId ? [...groupHistory, groupId] : [];
      newProps.set(id, {
        groupId: newGroup,
        groupHistory: newHistory,
      });
    }
    return { newProps, prevProps };
  };
  const ungrouper = function (
    nodesOrIds: NodesOrIds,
    { newProps, prevProps } = { newProps: new Map(), prevProps: new Map() }
  ) {
    const nodes = getNodesOrSelection(nodesOrIds);
    for (const node of nodes) {
      const id = node.attrs.id;
      const { groupId = "", groupHistory = [] } = node.attrs.element;
      updateObjectInMap(prevProps, id, { groupId, groupHistory });
      const newHistory = [...groupHistory];
      const newGroupId = newHistory?.pop() || "";
      updateObjectInMap(newProps, id, {
        groupId: newGroupId,
        groupHistory: newHistory,
      });
    }
    return { newProps, prevProps };
  };

  function handleGroup(nodesCollection?: Iterable<Konva.Node>) {
    let nodes: any = nodesCollection;
    if (!nodes && selectedElementIds.length > 1) {
      nodes = getSelectableNodes((i: any) => selectedElementIds.includes(i.attrs.id));
    }
    if (!nodes) return;
    const newGroup = newGroupId();
    const newProps = new Map<string, ElementDrawProps>();
    const oldProps = new Map<string, ElementDrawProps>();
    for (const node of nodes) {
      const id = node.attrs.id;
      const { groupId = "", groupHistory = [] } = node.attrs.element;
      oldProps.set(id, { groupId, groupHistory });

      const newHistory = groupId ? [...groupHistory, groupId] : [];

      newProps.set(id, {
        groupId: newGroup,
        groupHistory: newHistory,
      });
    }
    disableGroupsBounds();
    disableElementBounds();
    needToRefreshBoundsRef.current = boundsRefreshOperation.group;
    mutateWithWorkaroundForTexts(newProps, oldProps);
    //  onElementsMutationEnded(newProps, oldProps);
  }

  function handleUngroup(nodesCollection?: Iterable<Konva.Node>) {
    let nodes: any = nodesCollection;
    if (!nodes && selectedElementIds.length > 1) {
      nodes = getSelectableNodes((i: any) => selectedElementIds.includes(i.attrs.id));
    }
    if (!nodes) return;
    const newProps = new Map<string, ElementDrawProps>();
    const oldProps = new Map<string, ElementDrawProps>();
    for (const node of nodes) {
      const id = node.attrs.id;
      const { groupId = "", groupHistory = [] } = node.attrs.element;
      oldProps.set(id, { groupId, groupHistory });
      const newHistory = groupHistory?.slice();
      const newGropuId = newHistory?.pop() || "";
      newProps.set(id, {
        groupId: newGropuId,
        groupHistory: newHistory,
      });
    }
    needToRefreshBoundsRef.current = boundsRefreshOperation.ungroup;
    mutateWithWorkaroundForTexts(newProps, oldProps);

    // onElementsMutationEnded(newProps, oldProps);
  }

  function handleLinkElement(node: Konva.Node) {
    setEditingElementLink(node.attrs.id);
  }

  function mutateWithWorkaroundForTexts(
    newProps: Map<string, ElementDrawProps>,
    oldProps: Map<string, ElementDrawProps>,
    submenuData?: SubmenuDataUpdate,
    addUndo: boolean = true
  ) {
    // Note: transformer calls onTransformEnd even if we click (without dragging)
    // on an anchor. We don't want to record this no-change, to avoid creating an
    // undo step for nothing.
    let unchangedElements: string[] | undefined;
    for (const [key, old] of oldProps) {
      if (equals(old, newProps.get(key)!)) {
        unchangedElements ??= [];
        unchangedElements.push(key);
      }
    }
    if (unchangedElements) {
      if (unchangedElements.length == newProps.size) return;
      unchangedElements.forEach((key) => {
        newProps.delete(key);
        oldProps.delete(key);
      });
    }

    onElementsMutationEnded(newProps, oldProps, submenuData, addUndo);
    if (
      selectedElementIds.length > 0 &&
      selectedElementIds.every((x) => getElementTypeForId(x) == consts.CANVAS_ELEMENTS.TEXT_BLOCK)
    ) {
      // transformer should refresh itself, since changes to font-size etc. can affect element size
      setTimeout(() => {
        transformerRef.current?.forceUpdate();
      }, 10);
    }
  }

  function moveManyToFrontOrBack(nodes: Iterable<Konva.Node>, toFront: boolean) {
    const newProps = new Map<string, ElementDrawProps>();
    const oldProps = new Map<string, ElementDrawProps>();
    const nodesAsArr = Array.isArray(nodes) ? nodes : [...nodes];
    const sortedByZIndex = nodesAsArr.sort(byZIndexAsc);
    const newBaseZindex = toFront ? getUnixTimestampUTC() : -getUnixTimestampUTC();
    let i = toFront ? 1 : -sortedByZIndex.length;
    for (const node of sortedByZIndex) {
      const oldZ = getZIndex(node),
        newZ = newBaseZindex + i * 15; // 15 difference for spacing out the zIndexes;
      oldProps.set(node.attrs.id, {
        zIndexLastChangeTime: oldZ,
      });
      newProps.set(node.attrs.id, {
        zIndexLastChangeTime: newZ,
      });
      if (node.attrs.type == consts.CANVAS_ELEMENTS.TABLE && node.attrs.containedIds?.length) {
        for (const id of node.attrs.containedIds) {
          oldProps.set(id, {
            zIndexLastChangeTime: oldZ + 1,
          });
          newProps.set(id, {
            zIndexLastChangeTime: newZ + 1,
          });
        }
      }

      i++;
    }
    mutateWithWorkaroundForTexts(newProps, oldProps);
  }

  function moveForwardOrBackward(node: Konva.Node, forward: boolean) {
    const newProps = new Map<string, ElementDrawProps>();
    const oldProps = new Map<string, ElementDrawProps>();
    const c = node.attrs.containedIds ?? [];
    const nodes = getSelectableNodes(
      (n: Konva.Node) =>
        Konva.Util.haveIntersection(node.getClientRect(), n.getClientRect()) &&
        !n.attrs.element.containerId &&
        !n.attrs.isFrameTitle &&
        !n.attrs.isTableTitle &&
        !c.includes(n.id())
    );
    const sortedByZIndex = [...nodes].sort(byZIndexAsc);

    const nodeIndex = sortedByZIndex.findIndex((n) => n == node);
    const targetIndex = nodeIndex + (forward ? 1 : -1);
    if (targetIndex < 0 || targetIndex >= sortedByZIndex.length) return; //Cannot move element any further
    const targetZindex = getZIndex(sortedByZIndex[targetIndex]);

    let newBaseZindex = targetZindex + (forward ? 10 : -10); // 10 difference for spacing out the zIndexes
    const nextTargetZIndex = getZIndex(sortedByZIndex[targetIndex + (forward ? 1 : -1)]); // Will be null if targetIndex is the last element
    if (nextTargetZIndex) {
      // Make the jump smaller if it will pass more than 1 element
      const avgZIndexBetweenNextTwoElements = (nextTargetZIndex - targetZindex) / 2;
      newBaseZindex =
        targetZindex +
        Math.round(
          forward ? Math.min(avgZIndexBetweenNextTwoElements, 10) : Math.max(avgZIndexBetweenNextTwoElements, -10)
        );
    }

    if (node.attrs.type == consts.CANVAS_ELEMENTS.TABLE && node.attrs.containedIds.length) {
      for (const id of node.attrs.containedIds) {
        oldProps.set(id, {
          zIndexLastChangeTime: getZIndex(node) + 1,
        });
        newProps.set(id, {
          zIndexLastChangeTime: newBaseZindex + 1,
        });
      }
    }
    oldProps.set(node.attrs.id, {
      zIndexLastChangeTime: getZIndex(node),
    });
    newProps.set(node.attrs.id, {
      zIndexLastChangeTime: newBaseZindex,
    });
    mutateWithWorkaroundForTexts(newProps, oldProps);
  }

  function handleToFront(nodes: Iterable<Konva.Node>) {
    moveManyToFrontOrBack(nodes, true);
  }

  function handleToBack(nodes: Iterable<Konva.Node>) {
    moveManyToFrontOrBack(nodes, false);
  }

  function handleMoveForward(nodes: Iterable<Konva.Node>) {
    const node = [...nodes][0];
    moveForwardOrBackward(node, true);
  }

  function handleMoveBackward(nodes: Iterable<Konva.Node>) {
    const node = [...nodes][0];
    moveForwardOrBackward(node, false);
  }

  function getDistanceFromEvent(): MathUtils.PointInCanvas {
    if (layerRef.current == null) return { x: 0, y: 0 } as MathUtils.PointInCanvas;
    const transform = layerRef.current.getAbsoluteTransform().copy();
    transform.invert();
    const pos = stageRef.current?.getPointerPosition();
    return pos ? (transform?.point(pos) as MathUtils.PointInCanvas) : ({ x: 0, y: 0 } as MathUtils.PointInCanvas);
  }

  const SHAPE_MOVE_STEP_LARGE = 10;
  const SHAPE_MOVE_STEP_NORMAL = 5;
  function applyKeyboardSnapping(dx: number, dy: number, horizSnap: any, vertSnap: any) {
    const movementSize = Math.max(Math.abs(dx), Math.abs(dy));
    const isLargeMovement = movementSize >= SHAPE_MOVE_STEP_LARGE;
    const snapThreshold = isLargeMovement ? SHAPE_MOVE_STEP_LARGE - 1 : SHAPE_MOVE_STEP_NORMAL - 1;

    let finalDx = dx,
      finalDy = dy,
      didSnap = false;

    if (horizSnap && Math.abs(horizSnap.distance) <= snapThreshold) {
      finalDy = dy - horizSnap.distance;
      didSnap = true;
    }
    if (vertSnap && Math.abs(vertSnap.distance) <= snapThreshold) {
      finalDx = dx - vertSnap.distance;
      didSnap = true;
    }

    return { finalDx, finalDy, didSnap };
  }

  function moveElementsWithKeyboardSnapping(movingElements: ActuallyMovingElements, dx: number, dy: number) {
    // Clear previous snap state and calculate positioning
    if (dragInfo.current) dragInfo.current.snapInfo = null;
    // Calculate bounding box excluding table titles for consistent snapping behavior
    const nodesForBounds = movingElements.nodes.filter((node) => !node.attrs.isTableTitle);
    const currentBox = MathUtils.unionOfRects(nodesForBounds.map((node) => measureNode(node, { skipShadow: true })));
    const intendedBox = {
      x: currentBox.x + dx,
      y: currentBox.y + dy,
      width: currentBox.width,
      height: currentBox.height,
    };
    const snappingData = getSnappingGuidelines(layerRef.current, movingElements.ids);
    const { horizSnap, vertSnap } = checkForSnaps(intendedBox, snappingData);

    // Apply snapping and move elements
    const { finalDx, finalDy } = applyKeyboardSnapping(dx, dy, horizSnap, vertSnap);
    moveElements(movingElements, finalDx, finalDy);

    // Show guidelines if close enough to snap lines
    const finalBox = {
      x: currentBox.x + finalDx,
      y: currentBox.y + finalDy,
      width: currentBox.width,
      height: currentBox.height,
    };
    const movementSize = Math.max(Math.abs(dx), Math.abs(dy));
    const snapThreshold =
      movementSize >= SHAPE_MOVE_STEP_LARGE ? SHAPE_MOVE_STEP_LARGE - 1 : SHAPE_MOVE_STEP_NORMAL - 1;
    const guidelineThreshold = Math.max(snapThreshold, consts.SNAPPING_DISTANCE + 5);

    if (
      (horizSnap && Math.abs(horizSnap.distance) <= guidelineThreshold) ||
      (vertSnap && Math.abs(vertSnap.distance) <= guidelineThreshold)
    ) {
      updateMoveSnapGuidelines(finalBox, snappingData, horizSnap, vertSnap);
    } else {
      clearMoveGuidelines();
    }
  }

  function moveSelectionByKeyboard(dx: number, dy: number) {
    let moveContext: ActuallyMovingElements | null;
    if (stageState?.movingElements == null || stageState?.movingElements.kind == MovingStatus.Beginning) {
      const transformerDraggableNodes =
        transformerRef.current?.nodes().filter((x: Konva.Node) => x.attrs.isDraggable) ?? [];
      const { ids } = filterNodesThatMightMove(transformerDraggableNodes);
      moveContext = initMovingElements({
        kind: MovingStatus.Beginning,
        ids,
      });
      // need for the first time to set the movingElements
      setStageState((previous) => ({
        ...previous,
        movingElements: moveContext,
      }));
      // Render frame backgrounds immediately when movement begins
      if (moveContext) {
        moveFrameBackground(moveContext, layerRef);
      }
    } else {
      moveContext = stageState?.movingElements as ActuallyMovingElements;
    }
    if (!moveContext) return;

    // For keyboard movement, use a simpler approach with direct snapping
    moveElementsWithKeyboardSnapping(moveContext, dx, dy);

    // Use debounced version for better performance
    onMoveElementsDebounced(moveContext);
  }

  function finalizeShapeCreation(isCentered: boolean, isEquiSided: boolean) {
    if (stageState?.ghostElement?.kind != "Shape" && stageState?.ghostElement?.kind != "SvgShape") return;
    const [pos1, pos2] = MathUtils.computeBoundingBox(
      stageState.ghostElement.point1,
      stageState.ghostElement.point2,
      isCentered,
      isEquiSided
    );

    const center = { x: (pos1.x + pos2.x) / 2, y: (pos1.y + pos2.y) / 2 };
    const width = Math.max(Math.abs(pos2.x - pos1.x), consts.MIN_SIZE_NEW_SHAPES);
    const height = Math.max(Math.abs(pos2.y - pos1.y), consts.MIN_SIZE_NEW_SHAPES);

    if (width == consts.MIN_SIZE_NEW_SHAPES && height == consts.MIN_SIZE_NEW_SHAPES)
      createElementAndRegisterIt(center, cursorType, "side-menu");
    else createElementAndRegisterIt(center, cursorType, "side-menu", { x: width, y: height });
  }

  function createShape(
    data: any,
    position: { x: number; y: number },
    size?: { x: number; y: number },
    frameId?: string
  ) {
    const shapeDefaultSize = getDefaultSizeForShape(data.type, data.subtype);
    const actualSize = size
      ? { width: size.x, height: size.y }
      : {
          width: shapeDefaultSize.width * sizes.shape.scaleModify,
          height: shapeDefaultSize.height * sizes.shape.scaleModify,
        };
    if (data.override?.scaleX) {
      actualSize.width *= data.override.scaleX;
    }
    if (data.override?.scaleY) {
      actualSize.height *= data.override.scaleY;
    }
    const rotate = data.override?.rotate ?? data.rotate;
    const shape = placeShape({
      point: position,
      init: data,
      actualSize,
      defaultShapeSize: shapeDefaultSize,
      rotate,
      sizes: {
        strokeWidth: data.override?.strokeWidth ?? data.strokeWidth ?? Sizes.get().shape.strokeWidth.default,
        fontSize: Sizes.get().shape.fontSize,
        defaultRadius: Sizes.get().shape.defaultRadius,
      },
    });
    // position, data, actualSize, shapeDefaultSize, rotate, strokeWidth);
    return [
      {
        id: shape.id,
        type: consts.CANVAS_ELEMENTS.SHAPE,
        element: { ...shape.shape, frameId },
      },
    ];
  }

  function createIcon(data: any, position: { x: number; y: number }, frameId?: string) {
    const iconInit = { ...data, ...data.override };
    const icon = placeIcon(position, iconInit, sizes.icon.size);
    return [
      {
        id: icon.id,
        type: consts.CANVAS_ELEMENTS.ICON,
        element: { ...icon.icon, frameId },
      },
    ];
  }

  function createStickyNote(data: any, position: { x: number; y: number }, frameId?: string) {
    const note = placeStickyNote(data, position, sizes.stickyNote);
    return [
      {
        id: note.id,
        type: note.stickyNote.type,
        element: { ...note.stickyNote, frameId },
      },
    ];
  }

  function createTextBlock(data: any, position: { x: number; y: number }, frameId?: string) {
    const textBlockElement = placeTextBlock(data, position);
    return [
      {
        id: textBlockElement.id,
        type: consts.CANVAS_ELEMENTS.TEXT_BLOCK,
        element: { ...textBlockElement.textBlock, frameId },
      },
    ];
  }

  function createElements(
    position: { x: number; y: number },
    type: CursorType,
    size?: { x: number; y: number },
    frameId?: string,
    optionalProperties?: any,
    ...args: any[]
  ): NewElementData[] | null {
    try {
      switch (type) {
        case CursorType.connector: {
          const connector = placeConnector(newElementData.connector, position);
          return [
            {
              id: connector.id,
              type: connector.connector.type,
              element: { ...connector.connector, frameId },
            },
          ];
        }

        case CursorType.shape: {
          return createShape(newElementData.shape, position, size, frameId);
        }
        case CursorType.icon: {
          return createIcon(newElementData.icon, position, frameId);
        }
        case CursorType.mindmap: {
          const element = placeMindMapNode(position, {
            rootId: args[0],
            parentId: args[1],
            nodeSize: args[1] ? sizes.mindmap.nodeSize : sizes.mindmap.rootSize,
          });
          return [
            {
              id: element.id,
              type: consts.CANVAS_ELEMENTS.MINDMAP,
              element: { ...element.node, frameId },
            },
          ];
        }
        case CursorType.stickyNote: {
          return createStickyNote(newElementData.stickyNote, position, frameId);
        }
        case CursorType.text: {
          return createTextBlock(newElementData.textBlock, position, frameId);
        }
        case CursorType.frame: {
          const frame = placeFrame(position, {
            width: newElementData.frame.width || size?.x,
            height: newElementData.frame.height || size?.y,
            color: newElementData.frame.color,
            index: Object.keys(frames).length + 1,
          });
          return [
            {
              id: frame.id,
              type: consts.CANVAS_ELEMENTS.FRAME,
              element: frame.frame,
            },
          ];
        }
        case CursorType.cardStack: {
          const cardStack = placeCardStack(position);
          return [
            {
              id: cardStack.id,
              type: consts.CANVAS_ELEMENTS.CARD_STACK,
              element: cardStack.cardStack,
            },
          ];
        }
        case CursorType.liveIntegration: {
          const integrationView = placeLiveIntegrationStack(position);
          return [
            {
              id: integrationView.id,
              type: consts.CANVAS_ELEMENTS.LIVE_INTEGRATION,
              element: {
                ...integrationView.item,
                ...optionalProperties,
              },
            },
          ];
        }
        case CursorType.taskCard: {
          const taskCardElement = placeTaskCard(position);
          return [
            {
              id: taskCardElement.id,
              type: consts.CANVAS_ELEMENTS.TASK_CARD,
              element: {
                ...taskCardElement.card,
                frameId,
                ...optionalProperties,
              },
            },
          ];
        }
        case CursorType.task: {
          const taskElement = placeTask(position, {
            color: newElementData.task.color,
          });
          return [
            {
              id: taskElement.id,
              type: consts.CANVAS_ELEMENTS.TASK,
              element: {
                ...taskElement.task,
                frameId,
                ...optionalProperties,
              },
            },
          ];
        }
        case CursorType.comment: {
          const annotationData = {
            id: user?.id,
            thumbnail: user?.photoURL,
          };
          const commentElement = placeCommentThread(position, annotationData);
          Object.assign(commentElement.thread, optionalProperties);
          return [
            {
              id: commentElement.id,
              type: consts.CANVAS_ELEMENTS.COMMENT,
              element: commentElement.thread,
            },
          ];
        }
        case CursorType.timeline: {
          const start = new Date();
          const end = new Date();
          end.setDate(end.getDate() + 14);

          // create random dates and titles for the items
          const items = Array.from({ length: 5 }, (_, i) => {
            // random date between start and end
            const date = new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
            return {
              title: `Item ${i + 1}`,
              date: date.getTime(),
            };
          });
          return createTimelineElements({ position, items });
        }
        case CursorType.orgChart: {
          console.warn("org charts can only be placed from templates, not as standalone elements");
          return null;
        }
        case CursorType.table: {
          return placeTable({
            point: position,
            description: newElementData.table,
            size,
            defaults: sizes.table,
          });
        }

        case CursorType.gantt: {
          const start = new Date();
          const end = new Date();
          end.setDate(end.getDate() + 14);

          const element = placeGanttElement(position, start.getTime(), end.getTime());
          return [
            {
              id: element.id,
              type: consts.CANVAS_ELEMENTS.GANTT,
              element: element.item,
            },
          ];
        }

        case CursorType.nounIcon: {
          if (!optionalProperties.id) {
            console.error("invalid options for icon", optionalProperties);
            onError && onError("Sorry, an error occurred");
            return [];
          }
          putDirectDownloadLink(
            optionalProperties.id,
            optionalProperties.icon_url || "",
            document.getElementById(optionalProperties.id) as HTMLImageElement
          );
          const element = placeNounIcon(position, frameId, optionalProperties);
          return [
            {
              id: element.id,
              type: consts.CANVAS_ELEMENTS.NOUN_ICON,
              element: element.icon,
            },
          ];
        }

        case CursorType.chart: {
          const element = placeSingleBarChart(position);
          return [
            {
              id: element.id,
              type: consts.CANVAS_ELEMENTS.CHART,
              element: element.item,
            },
          ];
        }
      }
    } catch {
      onError && onError("Sorry, an error occurred. Please report with the feedback button about this bug.");
    }
    return null;
  }

  function getFrameId(position: { x: number; y: number }, size?: { x: number; y: number }) {
    const centerPosition = { ...position };
    if (size) {
      centerPosition.x += size.x / 2;
      centerPosition.y += size.y / 2;
    }
    const frameNode = layerRef.current?.findOne(
      (node: Konva.Node) =>
        node.attrs.isFrame &&
        !node.attrs.isFrameTitle &&
        MathUtils.isPointInRect(centerPosition, getBoundingBox(consts.CANVAS_ELEMENTS.FRAME, node.attrs.element)!)
    );
    return frameNode?.attrs.id;
  }

  async function createElementAndRegisterIt(
    position: { x: number; y: number },
    type: CursorType,
    source?: string,
    size?: { x: number; y: number },
    optionalProperties?: any,
    shouldReport: boolean = true
  ) {
    const frameId = getFrameId(position, size);
    const newElements = createElements(position, type, size, frameId, optionalProperties);
    if (newElements && newElements.length) {
      const newElementType = type;
      let newIds = await onAddElements(newElements, true, source);
      if (newElementType == CursorType.table) {
        // When a table is created, it creates master element and cell elements.
        // I deselect cells here, keep only master selected.
        // This will match the behaviour of all other elements (This will match the behavior of all other elements)
        newIds = newIds.filter((id) => !isIdOfType(id, consts.CANVAS_ELEMENTS.TABLE_CELL));
      }
      setTimeout(() => {
        if (
          newElementType == CursorType.stickyNote ||
          newElementType == CursorType.frame ||
          newElementType == CursorType.shape ||
          newElementType == CursorType.mindmap ||
          newElementType == CursorType.text ||
          newElementType == CursorType.task ||
          newElementType == CursorType.taskCard ||
          newElementType == CursorType.comment ||
          newElementType == CursorType.icon
        )
          setEditingElementId(newIds[0]);
        setSelectedIds(newIds);
        onElementsSelected(newIds);
        enableTransformer();
        enableGroupsBounds();
        enableElementBounds();
        elementBoundsLayerRef.current?.batchDraw();
        const isTaskInGantt =
          type === CursorType.taskCard &&
          optionalProperties &&
          optionalProperties.hasOwnProperty("containerId") &&
          optionalProperties.containerId.includes(consts.CANVAS_ELEMENTS.GANTT);
        shouldReport &&
          window.dispatchEvent(
            new CustomEvent(EVT_ELEMENT_DROP, {
              detail: {
                x: position.x,
                y: position.y,
                ids: newIds,
                ...(isTaskInGantt
                  ? {
                      ganttShouldKeep: true,
                    }
                  : {}),
              },
            })
          );
      }, 50);
      return newIds;
    }
  }

  function addNewCommentFromSubMenu(elementBounding: MathUtils.PointInCanvas, attachedProperties?: any) {
    const frameId = getFrameId(elementBounding);
    const newElements = createElements(elementBounding, CursorType.comment, undefined, frameId, attachedProperties);
    if (newElements && newElements.length === 1) {
      const newElement = newElements[0];
      const elementId = `${newElement.type}-${newElement.id}`;
      setActiveCommentThread({
        kind: "New",
        storeId: newElement.id,
        newElementData: newElement,
        elementId,
      });
    }
  }

  function attachCommentToElement(node: Konva.Node) {
    const elementBounding = measureNode(node);
    const comment: any = {
      x: elementBounding.x + elementBounding.width / 2,
      y: elementBounding.y + elementBounding.height / 2,
    };

    const attachedProperties = {
      attachedTo: node.attrs.id,
      attachedLocation: canvasPointToNormalizedPoint(comment, getElementTypeForId(node.attrs.id), node.attrs.element),
    };

    addNewCommentFromSubMenu(comment, attachedProperties);
  }

  function analyze2(e: KonvaEventObject<MouseEvent>): MouseClickInfo2 {
    if (e.target.name().includes("anchor")) {
      return { type: "anchor" };
    }
    if (e.target.name().includes("annotation")) {
      return { type: "annotation" };
    }
    if (e.target.name().includes("shape-socket")) {
      return { type: "shape-socket" };
    }
    if (e.target.id() == GhostElementId) {
      return { type: "ghost-element" };
    }
    const mouseOffset = { x: e.evt.offsetX, y: e.evt.offsetY };
    const mousePos = stageRef.current?.getPointerPosition();
    let nodeUnderMouse = selectElementAtPoint(mousePos ?? { x: 0, y: 0 });

    // This fixes old images and other nodes which are not detected by selectElementAtPoint
    if (
      (nodeUnderMouse == null && e.target) ||
      e.target.name().includes("connector-line") // If clicked exactly on a certain connector, select it.
    ) {
      let t = e.target;
      while (t && !t.attrs.id && !t.attrs.isSelectable) t = t.getParent();
      if (t && t.attrs.isSelectable) nodeUnderMouse = t;
    }

    const rect = transformerRef.current?.getClientRect();
    const clickInsideSelection = rect && MathUtils.isPointInRect(mousePos ?? { x: 0, y: 0 }, rect);
    if (nodeUnderMouse) {
      if (nodeUnderMouse.attrs.isFrame)
        return { type: "frame", frameId: nodeUnderMouse.attrs.id, mouseOffset, node: nodeUnderMouse };
      if (clickInsideSelection) {
        const shapeRect = nodeUnderMouse.getClientRect();
        if (shapeRect.width * shapeRect.height < rect.width * rect.height) {
          return { type: "element", node: nodeUnderMouse, mouseOffset };
        } else {
          return { type: "selection-area", mouseOffset };
        }
      } else {
        return { type: "element", node: nodeUnderMouse, mouseOffset };
      }
    } else if (clickInsideSelection) {
      return { type: "selection-area", mouseOffset };
    } else {
      return { type: "stage", mouseOffset };
    }
  }

  // This function examines the target under the mouse cursor (used after clicks),
  // so we can determine what to do
  // It will return 3 items:
  //     target - the target of the click (a string, see values in MouseClickInfo)
  //     shapeId - the id of the shape the mouse was over (can be null)
  //     shapeGroup - list of shapes that are part of the click target (selection, group, frame, etc.)
  function analyzeMousePosition(e: KonvaEventObject<MouseEvent>, isRightClick: boolean = false): MouseClickInfo {
    if (e.target.name().includes("anchor")) return { target: "transformer-handle" };
    if (e.target.name().includes("annotation")) return { target: "annotation" };
    if (e.target.name().includes("shape-socket")) return { target: "shape-socket" };
    if (e.target.id() == GhostElementId) return { target: "ghost-element" };

    const mousePos = stageRef.current?.getPointerPosition();
    let nodeUnderMouse = selectElementAtPoint(mousePos ?? { x: 0, y: 0 });
    if (
      (nodeUnderMouse == null && e.target) ||
      e.target.name().includes("connector-line") // If clicked exactly on a certain connector, select it.
    ) {
      let t = e.target;
      while (t && !t.attrs.id) t = t.getParent();
      if (t && t.attrs.isSelectable) nodeUnderMouse = t;
    }

    const rect = transformerRef.current?.getClientRect();
    const clickInsideSelection = rect && MathUtils.isPointInRect(mousePos ?? { x: 0, y: 0 }, rect);

    if (isRightClick && nodeUnderMouse?.attrs.isFrameTitle) {
      return { target: "frame", shapeId: nodeUnderMouse.attrs.id, shapeGroup: [nodeUnderMouse.attrs.id] };
    }

    const elementIds = selectedElementIds.flatMap((id) => {
      if (id.includes("frame-")) {
        const frameElements = frames[id] ?? [];
        return [id, ...frameElements];
      } else {
        return [id];
      }
    });
    if (clickInsideSelection) {
      // clicking inside selection always picks all of the selected elements (and elements inside selected frames)
      return { target: "selection-area", shapeGroup: elementIds };
    } else if (nodeUnderMouse) {
      // clicking on the title of a frame inside the selection rect should behave like the previous case
      if (nodeUnderMouse.attrs.isFrameTitle && selectedElementIds.includes(nodeUnderMouse.attrs.id)) {
        return { target: "selection-area", shapeGroup: elementIds };
      }

      if (nodeUnderMouse.attrs.isFrame) {
        if (selectedElementIds.length == 1 && selectedElementIds[0] == nodeUnderMouse.attrs.id) {
          const frameId = nodeUnderMouse.attrs.id;
          const frameElements = frames[frameId] ?? [];
          return {
            target: "shape",
            shapeId: frameId,
            shapeGroup: [frameId, ...frameElements],
          };
        }

        const frameId = nodeUnderMouse.attrs.id;
        const frameElements = frames[frameId] ?? [];
        return {
          target: "frame",
          shapeId: frameId,
          shapeGroup: [frameId, ...frameElements],
        };
      }

      if (selectedElementIds.includes(nodeUnderMouse.attrs.id) && !nodeUnderMouse.attrs.isFrameTitle) {
        return {
          target: "shape",
          shapeId: nodeUnderMouse.attrs.id,
          shapeGroup: selectedElementIds,
        };
      } else {
        const grp = getElementsOfGroupOrFrame(nodeUnderMouse);
        return {
          target: "shape",
          shapeId: nodeUnderMouse.attrs.id,
          shapeGroup: grp,
        };
      }
    } else if (e.target == e.target.getStage()) {
      return { target: "stage" };
    } else {
      console.error("unhandled case when analysing mouse click", e);
      return { target: "annotation" };
    }
  }

  async function handleStageDblClick(): Promise<void> {
    if (!stickyNoteOnDoubleClickFeatureFlag) return;
    if (cursorType !== CursorType.pointer) return;

    const pointerPosition = getDistanceFromEvent();
    const newElement = createStickyNote(newElementData.stickyNote, pointerPosition)[0];
    const newIds = await onAddElements([newElement]);
    if (newIds.length > 0) {
      setTimeout(() => {
        setEditingElementId(newIds[0]);
        setSelectedIds(newIds);
        enableTransformer();
      }, 10);
    }
  }

  const { handlePreciseDblMouseDown, handlePreciseDblMouseUp } = createPreciseDblClickHandlers(handleStageDblClick);

  function handleMouseDown(e: KonvaEventObject<MouseEvent>) {
    setMouseState("down");
    onMouseDown();
    undoRedoStack?.startSequence();
    const mousePosition = getDistanceFromEvent();

    mouseState.current.clickPosition = mousePosition;
    mouseState.current.clickPositionScreen = stageRef.current?.getPointerPosition() ?? { x: 0, y: 0 };
    mouseState.current.curPosition = mousePosition;
    mouseState.current.buttons = e.evt.buttons;
    mouseState.current.rightClick = isRightClick(e.evt);
    mouseState.current.middleClick = isMiddleClick(e.evt);

    mouseState.current.clickInfo = null;

    mouseState.current.capture =
      !e.target.name().includes("annotation") &&
      !e.target.name().includes("transformer-handle") &&
      !e.target.name().includes("anchor");

    if (!mouseState.current.capture) {
      return;
    }

    const clickInfo = analyzeMousePosition(e);

    // Handle preciseDblMouseDown only for stage clicks
    if (clickInfo.target == "stage") {
      handlePreciseDblMouseDown(e);
    }

    mouseState.current.clickInfo = clickInfo;
    switch (cursorType) {
      case CursorType.pointer: {
        mouseClickInfo.current = analyze2(e);
        // Defer expensive highlight operations to avoid blocking initial drag response
        setTimeout(() => {
          initializeAllNodesHighlights();
          initializeAllGroupsHighlights();
        }, 0);
        break;
      }

      case CursorType.mindmapOrgChart: {
        const template: any = structuredClone(newElementData);
        if (!template || !template.template) {
          setCursorType(CursorType.pointer);
          break;
        }
        template.template.element.x = mouseState.current.clickPosition.x;
        template.template.element.y = mouseState.current.clickPosition.y;
        onAddElements([template.template], true, "place-mindmap-org-chart-template");
        setCursorType(CursorType.pointer);
        break;
      }

      case CursorType.liveIntegration: {
        const integrationData = newElementData.liveIntegration || {};
        createElementAndRegisterIt(getDistanceFromEvent(), cursorType, "side-menu", undefined, integrationData);
        setCursorType(CursorType.pointer);
        break;
      }

      case CursorType.taskCard:
      case CursorType.task:
      case CursorType.stickyNote:
      case CursorType.mindmap:
      case CursorType.text:
      case CursorType.orgChart:
      case CursorType.cardStack:
      case CursorType.timeline:
      case CursorType.gantt:
      case CursorType.chart:
      case CursorType.icon: {
        createElementAndRegisterIt(getDistanceFromEvent(), cursorType, "side-menu");
        setCursorType(CursorType.pointer);
        //BUG: we start selection-rect at this point
        break;
      }
      case CursorType.nounIcon: {
        if (e.evt.buttons == 1) {
          if (newElementData.nounIcon?.id) {
            const pos = getDistanceFromEvent();
            createElementAndRegisterIt(pos, CursorType.nounIcon, "side-menu", undefined, newElementData.nounIcon);
            setNewElementData((prev: any) => ({ ...prev, nounIcon: null }));
          }
          setCursorType(CursorType.pointer);
        }

        break;
      }

      case CursorType.connector: {
        break;
      }

      case CursorType.shape: {
        clearSelected();
        setEditingElementId(null);
        break;
      }

      case CursorType.frame: {
        if (!!newElementData.frame.width && !!newElementData.frame.height) {
          createFrameFromGhost();
          setCursorType(CursorType.pointer);
          setNewElementData({ ...newElementData, frame: { width: null, height: null } }); // Reset frame settings after placement
          //BUG: we start selection-rect at this point
        } else {
          clearSelected();
          setEditingElementId(null);
        }
        break;
      }

      case CursorType.comment: {
        // creating and activating the comment thread here won't work, because it will get
        // this very mouse event and will close itself
        break;
      }

      case CursorType.draw: {
        drawingProps.current.id = null;
        drawingProps.current.initialPosition = mousePosition;
        break;
      }
      case CursorType.move: {
        if (stageRef.current) {
          stageRef.current.container().style.cursor = "grabbing";
        }
        break;
      }
      case CursorType.sideMenuOverflow: {
        setCursorType(CursorType.pointer);
        setShowSideMenuOverflow(false);
        break;
      }
    }
  }

  function filterNodesThatMightMove(nodesThatMightMove: Konva.Node[]) {
    let ids: string[] = [];
    const nodeForIds = {} as Record<string, any>;
    nodesThatMightMove = nodesThatMightMove.filter(
      (node: Konva.Node) => (node.attrs.element.lock ?? 0) === LockType.None
    );
    while (nodesThatMightMove.length > 0) {
      const node = nodesThatMightMove.pop();
      if (node?.attrs.name?.startsWith(transformerAnchorId(""))) {
        const rootId = node.attrs.name.split("/")[1];
        const realId = consts.CANVAS_ELEMENTS.ORG_CHART + "-" + rootId;
        ids.push(realId);
        nodeForIds[realId] = layerRef.current?.findOne("." + realId);
      } else {
        ids.push(node?.attrs.id);
        nodeForIds[node?.attrs.id] = node;
        if (isIdOfType(node?.attrs.id, consts.CANVAS_ELEMENTS.FRAME)) {
          const childrenIds = frames[node?.attrs.id];
          if (childrenIds && childrenIds.length > 0) {
            const children = getSelectableNodes((i) => childrenIds.includes(i.attrs.id) && !i.isFrameTitle);
            nodesThatMightMove = nodesThatMightMove.concat(children);
          }
        }
        if (isIdOfType(node?.attrs.id, consts.CANVAS_ELEMENTS.TABLE)) {
          const contained = node?.attrs.containedIds;
          if (contained && contained.length > 0) {
            const children = getSelectableNodes((i) => contained.includes(i.attrs.id) && !i.isFrameTitle);
            nodesThatMightMove = nodesThatMightMove.concat(children);
          }
        }
      }
    }
    ids = [...new Set(ids)];

    // Remove all cards that belong to stacks that move. the stack will move them.
    // all other cards that move are be disconnected from their stacks later on.
    let cardsThatWillMoveAnyway = [] as any[];
    for (const stackid in cardStacks) {
      if (ids.includes(stackid)) {
        cardsThatWillMoveAnyway = cardsThatWillMoveAnyway.concat(cardStacks[stackid]);
      }
    }
    ids = difference(ids, cardsThatWillMoveAnyway);
    return {
      ids,
      nodeForIds,
    };
  }

  async function handleMouseMove(e: KonvaEventObject<MouseEvent>) {
    const ctrlOrCmd = isMac() ? e.evt.metaKey : e.evt.ctrlKey;

    if (e.evt.buttons == 0 && !!mouseState.current.buttons) {
      handleMouseUp({ ...e, evt: { ...e.evt, type: "mouseup" } });
      return;
    }

    const mousePosition = getDistanceFromEvent();
    mouseState.current.curPosition = mousePosition;
    mouseState.current.buttons = e.evt.buttons;

    //NOTE - Experimental gui system
    {
      const stage = e.currentTarget.getStage();
      if (stage != null) {
        const event = {
          type: "mousemove" as const,
          x: mousePosition.x,
          y: mousePosition.y,
        };
        propagateEvent(widgetsMap.values(), event, stage.scaleX());
      }
    }

    const { buttons, clickPosition, rightClick, middleClick, curPosition } = mouseState.current;
    let mouseDelta = clickPosition ? PointUtils.manhattanDistane(mousePosition, clickPosition) : 0;
    mouseDelta *= posScale.scale; // check movement in pixel units, not stage units
    if (
      mouseDelta > consts.MOUSE_MOVE_THRESHOLD &&
      (rightClick || middleClick) &&
      cursorType !== CursorType.move &&
      cursorType !== CursorType.connector
    ) {
      setCursorType(CursorType.move);
    }

    if (buttons && !mouseState.current.capture && !isDraggingElement) {
      // If dragging element, mouseState will not be available, but we still want
      // to enter the switch statement below and update the ghost element.
      return;
    }

    // Determine what we're going to do on mouse movement + left click:
    // Nothing (Happens when resizing, locked, connector building etc, see comment below)
    // Show selection rect
    // Start moving elements (setMovingElements), skips this logic once the move started.
    // NOTE: If we are dragging an element, we skip this entirely and go to the switch statement below.
    if (
      !stageState?.ghostElement &&
      !stageState?.movingElements &&
      cursorType == CursorType.pointer &&
      isButtonPressed(buttons, MouseButtons.Left) &&
      !isDraggingElement
    ) {
      const shiftKey = e.evt.shiftKey;
      const type = mouseClickInfo.current.type;
      const lock = mouseClickInfo.current.node?.attrs.element.lock ?? 0;
      const sel = selectedElementIds;
      const startMovement =
        mouseDelta > consts.MOUSE_MOVE_THRESHOLD && (Math.abs(e.evt.movementX) || Math.abs(e.evt.movementY));
      if (!startMovement) {
        return;
      }
      if (type == "anchor" || type == "transformer-handle" || type == "annotation") {
        // Anchors use ConnectorBuilder class, not handled by this function
        // Resizing (transformers) is handled separately (TODO find out where)
        return;
      }
      if (type == "stage" || shiftKey || lock != 0) {
        setStageState((previous) => ({
          ...previous,
          ghostElement: {
            kind: "SelectionRect",
            point1: mousePosition,
            point2: mousePosition,
            shift: shiftKey,
          },
        }));
        return;
      }

      let nodesThatMightMove: Konva.Node[] = [];
      let nodeToKeepAfterDrag;

      const transformerDraggableNodes =
        transformerRef.current?.nodes().filter((x: Konva.Node) => x.attrs.isDraggable) ?? [];

      if (type == "element") {
        nodeToKeepAfterDrag = mouseClickInfo.current.node;
        if (sel.includes(mouseClickInfo.current.node!.attrs.id)) {
          nodesThatMightMove = transformerDraggableNodes;
        } else {
          const grouped = getElementsOfGroupOrFrame(mouseClickInfo.current.node!);
          nodesThatMightMove = getDraggableNodes((i) => grouped.includes(i.attrs.id) && !i.attrs.isFrameTitle);
        }
      } else if (type == "frame") {
        if (sel.includes(mouseClickInfo.current.node!.attrs.id)) {
          nodesThatMightMove = transformerDraggableNodes;
        } else {
          nodeToKeepAfterDrag = mouseClickInfo.current.node;
          nodesThatMightMove =
            layerRef.current
              ?.find("." + mouseClickInfo.current.frameId!)
              .toArray()
              .filter((x: Konva.Node) => x.attrs.isFrame && !x.attrs.isFrameTitle) ?? [];
        }
      } else if (type == "selection-area") {
        nodesThatMightMove = transformerDraggableNodes;
      }

      const { ids, nodeForIds } = filterNodesThatMightMove(nodesThatMightMove);

      //TODO: initMovingElements looks for the nodes given the ids, which is just double work since
      // we just looked for them
      const moveContext = initMovingElements(
        { kind: MovingStatus.Beginning, ids, elementUnderMouse: nodeToKeepAfterDrag },
        { x: e.evt.offsetX, y: e.evt.offsetY }
      );

      // Remove elements that are moving from their containers
      // (e.g. if we're moving integration cards, we detach them from their owning stack)
      if (moveContext) {
        ids.forEach((id) => {
          const node = nodeForIds[id];
          if (!node) {
            return;
          }
          const { containerId } = node.attrs.element;
          if (containerId) {
            undoRedoStack.patchElement(id, (draft: CanvasElementType) => {
              delete draft.containerId;
            });
          }
        });
        setSelectedIds(moveContext.ids, false, false);
        enableTransformer();
        setEditingElementId(null);
      }

      setStageState((previous) => ({ ...previous, movingElements: moveContext }));
      if (moveContext && moveContext.ids.length) {
        window.dispatchEvent(
          new CustomEvent(EVT_ELEMENT_DRAG_START, {
            detail: { mousePosition, ids: moveContext.ids },
          })
        );
      }

      return;
    }

    switch (cursorType) {
      case CursorType.pointer: {
        if (isButtonPressed(buttons, MouseButtons.Left)) {
          if (stageState?.movingElements && stageState?.movingElements.kind == MovingStatus.Ongoing) {
            const mousePosInCanvas = getDistanceFromEvent();

            // Snap elements to axis when holding SHIFT
            let lockToAxis = null;
            if (e.evt.shiftKey && clickPosition) {
              const clickDeltaX = Math.abs(mousePosition.x - clickPosition.x) * posScale.scale;
              const clickDeltaY = Math.abs(mousePosition.y - clickPosition.y) * posScale.scale;
              lockToAxis = clickDeltaY > clickDeltaX ? "y" : "x";
              if (lockToAxis == "x") {
                mousePosInCanvas.y = clickPosition.y;
              } else {
                mousePosInCanvas.x = clickPosition.x;
              }
            }

            if (e.evt.altKey && clickPosition) {
              if (!stageState?.ghostElement || stageState.ghostElement.kind !== "KonvaNode") {
                // Init ghost movement - called once when ALT is pressed
                moveElements(
                  stageState?.movingElements,
                  -dragInfo.current.movementX,
                  -dragInfo.current.movementY,
                  false
                );
                setStageState((previous) => ({ ...previous, guidelines: null, distanceGuidelines: null }));

                const { box, offset } = dragInfo.current;
                let copiedNodes = getClonedSelectedCanvasNodes([...(stageState?.movingElements?.nodes ?? [])]);
                copiedNodes = copiedNodes!.map((n) => n.clone()); // getClonedSelectedCanvasNodes does not actually clone the nodes.
                copiedNodes.forEach((n) => {
                  // Patch #1: Mind maps have a separate attribute for position
                  if (n.attrs.type === consts.CANVAS_ELEMENTS.MINDMAP) {
                    const pos = n.attrs.element.absolutePosition;
                    n.setPosition({ x: pos.x - box.x, y: pos.y - box.y });
                    return;
                  }

                  // Patch #2: Card stacks render a transparent rect as their Node (see card-stack-element.tsx)
                  // We replace it here with a gray one that is visible
                  if (n.attrs.type === consts.CANVAS_ELEMENTS.CARD_STACK) {
                    n.children[0]?.setAttr("fill", "#b8c0cb");
                  }

                  // Patch #3: Sticky notes render another rectangle which is responsible for their shadow.
                  // We disable it here since it looks terrible with opacity
                  if (n.attrs.type === consts.CANVAS_ELEMENTS.STICKY_NOTE) {
                    n.children[0]?.setAttr("stroke", "transparent");
                  }

                  n.setPosition({ x: n.position().x - box.x, y: n.position().y - box.y });
                });

                const bbox = BoundingBox.expandOnAll(
                  copiedNodes.map((node) => {
                    return node.getClientRect({
                      relativeTo: layerRef.current as any,
                      skipShadow: true,
                    });
                  })
                );

                copiedNodes.push(
                  new Konva.Rect({
                    width: bbox.width,
                    height: bbox.height,
                    stroke: "#00A1FF",
                    strokeWidth: 2,
                    x: bbox.x,
                    y: bbox.y,
                  })
                );

                setStageState((previous) => {
                  return {
                    ...previous,
                    ghostElement: {
                      kind: "KonvaNode",
                      nodes: copiedNodes ?? [],
                      x: mousePosInCanvas.x - offset.x,
                      y: mousePosInCanvas.y - offset.y,
                      opacity: 0.33,
                    },
                  };
                });
              } else {
                // Ghost movement
                const { box: oldbox, offset, snapInfo } = dragInfo.current;
                const newbox = {
                  width: oldbox.width,
                  height: oldbox.height,
                  x: mousePosInCanvas.x - offset.x,
                  y: mousePosInCanvas.y - offset.y,
                };

                if (!ctrlOrCmd && snapInfo) {
                  // Move elements with snapping
                  const { horizSnap, vertSnap } = checkForSnaps(newbox, snapInfo);
                  if (lockToAxis !== "y" && vertSnap && vertSnap.distance < consts.SNAPPING_DISTANCE) {
                    newbox.x -= vertSnap.signedDistance;
                  }
                  if (lockToAxis !== "x" && horizSnap && horizSnap.distance < consts.SNAPPING_DISTANCE) {
                    newbox.y -= horizSnap.signedDistance;
                  }
                  // Draw guidelines where the element(s) currently are
                  setStageState((previous) => ({
                    ...previous,
                    guidelines: getSnapLines(newbox, snapInfo),
                    distanceGuidelines: getDistanceSnapLines(newbox, snapInfo),
                  }));
                }

                setStageState((previous) => {
                  return {
                    ...previous,
                    ghostElement: {
                      ...previous?.ghostElement,
                      x: newbox.x,
                      y: newbox.y,
                    } as GhostKonvaNode,
                  };
                });
              }
            } else {
              // Clear ghosts that were created when before ALT was released
              if (stageState?.ghostElement?.kind === "KonvaNode") {
                setStageState((previous) => ({ ...previous, ghostElement: null }));
                await moveElements(
                  stageState?.movingElements,
                  dragInfo.current.movementX,
                  dragInfo.current.movementY,
                  false
                );
              }

              const elementInGantt = isElementInGantt(stageState?.movingElements?.elementUnderMouse?.attrs.element);
              if (ctrlOrCmd || !dragInfo.current.snapInfo || elementInGantt) {
                // No snapping - just move elements, in these cases:
                // 1. Manual disabling using CTRL/CMD
                // 2. No snap info available
                // 3. Element is in gantt

                //BUG: if the mouse cursor moves on html element, or out of the window, we lose
                // connection with it, and then when it gets back we don't move by delta, we must snap to
                // exact location
                const dx = mousePosInCanvas.x - dragInfo.current.lastMouseX;
                const dy = mousePosInCanvas.y - dragInfo.current.lastMouseY;
                moveElements(stageState?.movingElements, dx, dy);
                dragInfo.current.box.x += dx;
                dragInfo.current.box.y += dy;
                clearMoveGuidelines();
              } else {
                // Move elements with snapping
                moveElementsWithSnapping(stageState?.movingElements, mousePosInCanvas, lockToAxis);
              }
            }

            dragInfo.current.lastMouseX = mousePosInCanvas.x;
            dragInfo.current.lastMouseY = mousePosInCanvas.y;
            if (stageState?.movingElements) {
              const count = Object.values(stageState?.movingElements.stats).reduce((a, b) => a + b, 0);
              window.dispatchEvent(
                new CustomEvent(EVT_ELEMENT_DRAG, {
                  detail: { mousePosition, count, ids: stageState?.movingElements.ids, target: e.target },
                })
              );
            }

            transformerRef.current?.forceUpdate();
          } else if (stageState?.ghostElement?.kind == "SelectionRect") {
            ghostElementRef.current = {
              kind: "SelectionRect",
              point1: clickPosition ?? mousePosition,
              point2: curPosition,
              shift: e.evt.shiftKey,
            };
            changeSelectionRect();
          }
        }
        break;
      }
      case CursorType.shape: {
        setStageState((previous) => ({
          ...previous,
          ghostElement: {
            kind: "Shape",
            point1: mouseState.current.clickPosition ?? mousePosition,
            point2: mousePosition,
            isCentered: e.evt.altKey,
            isEquiSided: e.evt.shiftKey,
          },
        }));
        break;
      }
      case CursorType.mindmap: {
        setStageState((previous) => ({
          ...previous,
          ghostElement: {
            kind: "MindMap",
            point: mouseState.current.clickPosition ?? mousePosition,
          },
        }));
        break;
      }
      case CursorType.mindmapOrgChart: {
        setStageState((previous) => ({
          ...previous,
          ghostElement: {
            kind: "MindMapOrgChart",
            position: mouseState.current.clickPosition ?? mousePosition,
            template: newElementData.template!, // Needed to add ! due to newElementData typing.
          },
        }));
        break;
      }
      case CursorType.frame: {
        setStageState((previous) => ({
          ...previous,
          ghostElement: {
            kind: "Frame",
            point1: mouseState.current.clickPosition ?? mousePosition,
            point2: mousePosition,
          },
        }));
        break;
      }
      case CursorType.stickyNote: {
        setStageState((previous) => ({
          ...previous,
          ghostElement: {
            kind: "Note",
            point1: mouseState.current.clickPosition ?? mousePosition,
            point2: mousePosition,
          },
        }));
        break;
      }
      case CursorType.text: {
        setStageState((previous) => ({ ...previous, ghostElement: null }));
        break;
      }
      case CursorType.draw: {
        setStageState((previous) => ({ ...previous, ghostElement: null }));
        if (drawingProps.current.initialPosition) {
          if (!drawingProps.current.id) {
            const frameNode = layerRef.current?.findOne(
              (node: Konva.Node) =>
                node.attrs.isFrame &&
                !node.attrs.isFrameTitle &&
                Konva.Util.haveIntersection(
                  { ...(stageRef.current?.getPointerPosition() ?? { x: 0, y: 0 }), width: 0, height: 0 },
                  node.getClientRect()
                )
            );
            const frameId = frameNode?.attrs.id;
            const drawing = placeDrawing(drawingProps.current.initialPosition, newElementData.pen.color, frameId);
            drawingProps.current.id = drawing.id;
            drawing.drawing.points.push(mousePosition.x - drawingProps.current.initialPosition.x);
            drawing.drawing.points.push(mousePosition.y - drawingProps.current.initialPosition.y);
            onAddElements(
              [
                {
                  id: drawing.id,
                  type: consts.CANVAS_ELEMENTS.DRAWING,
                  element: drawing.drawing,
                },
              ],
              true,
              "finish-drawing"
            ).then((newIds) =>
              window.dispatchEvent(
                new CustomEvent(EVT_ELEMENT_DROP, { detail: { x: mousePosition.x, y: mousePosition.y, ids: newIds } })
              )
            );
          } else {
            const point = {
              x: mousePosition.x - drawingProps.current.initialPosition.x,
              y: mousePosition.y - drawingProps.current.initialPosition.y,
            };
            onDraw(drawingProps.current.id, point);
          }
        }
        break;
      }
      case CursorType.move: {
        if (buttons != 0) {
          moveStagePosition(-e.evt.movementX, -e.evt.movementY);
        }
        break;
      }
      case CursorType.file: {
        setStageState((previous) => ({ ...previous, ghostElement: null }));
        break;
      }
      case CursorType.connector: {
        // Connector ghosts are handled by the ConnectorBuilder class
        break;
      }
      case CursorType.template: {
        if (!newElementData.template || !newElementData.template.id) {
          return;
        }
        setStageState((previous) => ({
          ...previous,
          ghostElement: {
            kind: "Template",
            position: mouseState.current.curPosition,
            template: newElementData.template!, // Needed to add ! due to newElementData typing.
          },
        }));
        break;
      }
      case CursorType.taskCard: {
        setStageState((previous) => ({
          ...previous,
          ghostElement: {
            kind: "TaskCard",
            point1: mouseState.current.curPosition,
          },
        }));
        break;
      }
      case CursorType.task: {
        setStageState((previous) => ({
          ...previous,
          ghostElement: {
            kind: "Task",
            point1: mouseState.current.curPosition,
            data: {
              task: {
                type: consts.CANVAS_ELEMENTS.TASK,
                width: 275,
                height: 80,
                text: "",
              },
            },
          },
        }));
        break;
      }
      case CursorType.cardStack: {
        setStageState((previous) => ({
          ...previous,
          ghostElement: {
            kind: "CardStack",
            point1: mouseState.current.curPosition,
          },
        }));
        break;
      }
      case CursorType.liveIntegration: {
        setStageState((previous) => ({
          ...previous,
          ghostElement: {
            kind: "LiveIntegration",
            point1: mouseState.current.curPosition,
          },
        }));
        break;
      }
      case CursorType.timeline: {
        setStageState((previous) => ({
          ...previous,
          ghostElement: {
            kind: "Timeline",
            point: mouseState.current.clickPosition ?? mousePosition,
          },
        }));
        break;
      }
      case CursorType.table: {
        setStageState((previous) => ({
          ...previous,
          ghostElement: {
            kind: "Table",
            point1: mouseState.current.clickPosition ?? mousePosition,
            point2: mouseState.current.curPosition,
            isCentered: e.evt.altKey,
            isEquiSided: e.evt.shiftKey,
          },
        }));
        break;
      }
      case CursorType.gantt: {
        setStageState((previous) => ({
          ...previous,
          ghostElement: {
            kind: "Gantt",
            point: mouseState.current.clickPosition ?? mousePosition,
          },
        }));
        break;
      }
      case CursorType.nounIcon: {
        if (newElementData.nounIcon) {
          setStageState((previous) => ({
            ...previous,
            ghostElement: {
              kind: "IconOrImage",
              point1: mouseState.current.clickPosition ?? mousePosition,
              point2: mouseState.current.curPosition,
              url: newElementData.nounIcon!.thumbnail, // Needed to add ! due to newElementData typing.
            },
          }));
        }
        break;
      }
      case CursorType.chart: {
        setStageState((previous) => ({
          ...previous,
          ghostElement: {
            kind: "Chart",
            point: mouseState.current.clickPosition ?? mousePosition,
            chartType: ChartType.singleBar,
          },
        }));
        break;
      }
      case CursorType.icon: {
        setStageState((previous) => ({
          ...previous,
          ghostElement: {
            kind: "Icon",
            point: mouseState.current.curPosition,
            data: { href: newElementData.icon.href },
          },
        }));
        break;
      }
    }
  }

  function handleMouseUp(e: KonvaEventObject<MouseEvent>) {
    let selectedNodes: Node[] = [];
    disableSelectionBounds();
    clearMoveGuidelines();
    clearFrameHighlights(layerRef);
    if (e.target.attrs.name?.includes("anchor")) {
      return;
    }
    // this is another check for transformer handle
    if (mouseState.current.clickInfo?.target == "transformer-handle") {
      return;
    }
    try {
      handleMouseUp_(e);
      // remove selection rect in mouse up
      const selectionRectActive = ghostElementRef.current && ghostElementRef.current.kind == "SelectionRect";
      if (selectionRectActive) {
        ghostElementRef.current = null;
        changeSelectionRect();
      }
    } finally {
      mouseState.current = {
        clickPosition: null,
        clickPositionScreen: null,
        clickInfo: null,
        curPosition: getDistanceFromEvent(),
        buttons: 0,
        rightClick: false,
        middleClick: false,
        capture: false,
      };
      // enable transformer if there are selected elements, otherwise clear selection
      if (selectedIdsRef.current.length > 0) {
        // Re-enable transformer and refresh it
        if (transformerRef.current) {
          transformerRef.current.listening(true);
          transformerRef.current.forceUpdate();
        }
        selectedNodes = enableTransformer(false);
      } else {
        clearSelected();
      }

      // Update whether top element has a link for zoom performance optimization
      updateTopElementHasLink();

      // update state once
      if (stageState?.ghostElement?.kind == "Connector") {
        setStageState((previous) => ({
          ...previous,
          guidelines: null,
          distanceGuidelines: null,
          sizeGuidelines: null,
          connectorAnchorPoints: null,
          movingElements: null,
          highlightedFrameElementIds: [],
          selectedNodes: selectedNodes,
        }));
      } else {
        setStageState((previous) => ({
          ...previous,
          ghostElement: null,
          guidelines: null,
          distanceGuidelines: null,
          sizeGuidelines: null,
          connectorAnchorPoints: null,
          movingElements: null,
          highlightedFrameElementIds: [],
          selectedNodes: selectedNodes,
        }));
      }
      undoRedoStack?.endSequence();
      // Re-enable shape sockets after a small delay to prevent flickering
      setTimeout(() => {
        setShapeSocketsHidden(false);
      }, 50);
      if (selectedIdsRef.current.length > 0) {
        const elementIds = stageState?.movingElements?.ids ?? [];

        // If no elements moved but there's a single connector selected, update its frame attachment
        if (elementIds.length === 0 && selectedIdsRef.current.length === 1) {
          const selectedId = selectedIdsRef.current[0];
          if (selectedId.startsWith("connector-")) {
            console.log(`🔗 Checking frame attachment for single selected connector: ${selectedId}`);
            updateSingleConnectorFrameAttachment(
              selectedId,
              hiddenFrameIds,
              layerRef,
              stageRef,
              getSelectableNodes,
              syncService
            );
          } else {
            updateFrameAttachments(
              elementIds,
              frames,
              hiddenFrameIds,
              layerRef,
              stageRef,
              getSelectableNodes,
              syncService
            );
          }
        } else {
          updateFrameAttachments(
            elementIds,
            frames,
            hiddenFrameIds,
            layerRef,
            stageRef,
            getSelectableNodes,
            syncService
          );
        }
        onElementsSelected(selectedIdsRef.current);
        const movement = {
          x: dragInfo.current.movementX,
          y: dragInfo.current.movementY,
        };
        if (
          elementIds.length > 0 &&
          (movement.x !== 0 || movement.y !== 0) &&
          dragInfo.current.type === DragInfoType.KONVA
        ) {
          onMoveElements(elementIds, movement);
        }
      }
    }
  }

  function handleMouseUp_(e: KonvaEventObject<MouseEvent>) {
    if (isDraggingElement) {
      if (cursorType == CursorType.nounIcon) {
        createElementAndRegisterIt(
          getDistanceFromEvent(),
          cursorType,
          "drag-elements",
          undefined, //size
          newElementData.nounIcon //optionalProps
        );
      } else {
        createElementAndRegisterIt(getDistanceFromEvent(), cursorType, "drag-elements");
      }
      setCursorType(CursorType.pointer);
      setIsDraggingElement(false);
      return;
    }

    setMouseState("up");

    mouseState.current.buttons = e.evt.buttons;
    const rightClick = mouseState.current.rightClick;
    const middleClick = mouseState.current.middleClick;

    handlePreciseDblMouseUp(e);

    if (e.target.attrs.name?.includes("anchor")) {
      return;
    }
    // this is another check for transformer handle
    if (mouseState.current.clickInfo?.target == "transformer-handle") {
      return;
    }
    // test if we're in the process of dragging something (such as overlap marker)
    if (Object.keys(mutationAffectedNodes.current).length > 0) {
      return;
    }

    switch (cursorType) {
      case CursorType.move: {
        if ((rightClick || middleClick) && prevCursorType) {
          setCursorType(prevCursorType);
        } else {
          if (stageRef.current) {
            stageRef.current.container().style.cursor = "grab";
          }
        }
        break;
      }
      case CursorType.pointer: {
        if (rightClick) {
          // If elements are being moved, call onMoveElements with the current delta
          if (stageState?.movingElements?.kind === MovingStatus.Ongoing && dragInfo.current) {
            const delta = {
              x: dragInfo.current.movementX || 0,
              y: dragInfo.current.movementY || 0,
            };
            undoRedoStack?.endSequence();
            onMoveElements(stageState.movingElements.ids, delta);

            // Reset drag state
            setStageState((previous) => ({ ...previous, movingElements: null }));
            if (dragInfo.current) {
              dragInfo.current.movementX = 0;
              dragInfo.current.movementY = 0;
            }
          }

          // Note to future self: we prefer to open the context menu ourselves on mouse up,
          // (instead of oncontextmenu event) because if the user right-clicks and drags the canvas
          // pans, and we should not open context-menu then. So only for right click without movement...
          handleContextMenu(e);
        } else if (stageState?.movingElements == null || stageState?.movingElements.kind != MovingStatus.Ongoing) {
          if (mouseState.current.capture) handlePointerDragEnd(e);
        } else {
          if (e.evt.altKey && stageState?.ghostElement?.kind === "KonvaNode") {
            // Duplicate elements when ALT is held
            const { box, offset } = dragInfo.current;
            const x = mouseState.current.curPosition.x - offset.x + box.width / 2;
            const y = mouseState.current.curPosition.y - offset.y + box.height / 2;
            handleCopy(stageState?.movingElements?.nodes).then(() => handlePaste({ x, y }));
          } else {
            const mutationInfo = new Map<string, ElementDrawProps>();

            if (stageState?.movingElements) {
              const ev = new CustomEvent(EVT_ELEMENT_DROP, {
                detail: {
                  x: mouseState.current.curPosition.x,
                  y: mouseState.current.curPosition.y,
                  ids: stageState?.movingElements.ids,
                },
              });
              // dispatch the event in delay, so we first finish the move, and only then
              // the stacks take the cards
              setTimeout(() => window.dispatchEvent(ev));
            }

            if (mutationInfo.size > 0) {
              syncService?.mutate.changeElements({
                info: Array.from(mutationInfo.entries()),
              });
            }
          }
          if (stageState?.movingElements?.elementUnderMouse?.attrs.isFrame) {
            setSelectedIds([stageState?.movingElements.elementUnderMouse.attrs.id], false);
            enableTransformer();
          }
        }

        break;
      }
      case CursorType.shape: {
        finalizeShapeCreation(e.evt.altKey, e.evt.shiftKey);
        setCursorType(CursorType.pointer);
        break;
      }
      case CursorType.connector: {
        break;
      }
      case CursorType.frame: {
        createFrameFromGhost();
        setCursorType(CursorType.pointer);
        break;
      }
      case CursorType.table: {
        createTableFromGhost(e.evt.altKey, e.evt.shiftKey);
        setCursorType(CursorType.pointer);
        break;
      }
      case CursorType.taskCard:
      case CursorType.stickyNote:
      case CursorType.mindmap:
      case CursorType.text:
      case CursorType.file:
      case CursorType.timeline:
      case CursorType.gantt:
      case CursorType.nounIcon:
      case CursorType.chart: {
        break;
      }

      case CursorType.draw: {
        drawingProps.current.initialPosition = null;
        drawingProps.current.id = null;
        break;
      }
      case CursorType.template: {
        createSelectedTemplateAtCursorPosition();
        setStageState((previous) => ({
          ...previous,
          ghostElement: null,
        }));
        setCursorType(CursorType.pointer);
        break;
      }
      case CursorType.comment: {
        const pos = getDistanceFromEvent();
        const newElements = createElements(pos, CursorType.comment);
        if (newElements && newElements.length === 1) {
          const newElement = newElements[0];
          const elementId = `${newElement.type}-${newElement.id}`;
          setActiveCommentThread({
            kind: "New",
            storeId: newElement.id,
            newElementData: newElement,
            elementId: elementId,
          });
          setFocusedElementId(elementId);
        }
        setCursorType(CursorType.pointer);
        break;
      }
    }
  }

  function initMovingElements(moving: MaybeMovingElements, mouseOffset?: Point): ActuallyMovingElements | null {
    let nodes = getDraggableNodes((node) => {
      if (!moving.ids.includes(node.attrs.id)) return false;
      if (getElementTypeForId(node.attrs.id) == consts.CANVAS_ELEMENTS.CONNECTOR) {
        const connections = node.attrs.element.connectedShapes;
        const startWillMove = !connections[0]?.id || moving.ids.includes(connections[0]?.id);
        const endWillMove = !connections[1]?.id || moving.ids.includes(connections[1]?.id);
        return startWillMove && endWillMove;
      }
      return true;
    });

    // if we're moving a live stack, we don't want to move the cards, just the stack
    if (nodes.some((node) => node.attrs.type === consts.CANVAS_ELEMENTS.LIVE_INTEGRATION)) {
      nodes = nodes.filter((node) => !node.attrs.isDirty);
    }

    const ids = [...new Set(nodes.map((node) => node.attrs.id))];

    const { x, y } = getDistanceFromEvent();
    dragInfo.current.lastMouseX = x;
    dragInfo.current.lastMouseY = y;
    dragInfo.current.movementX = 0;
    dragInfo.current.movementY = 0;

    // Calculate bounding box excluding table titles (they move with table but don't affect snapping bounds)
    const nodesForBounds = nodes.filter((node) => !node.attrs.isTableTitle);
    const box = MathUtils.unionOfRects(nodesForBounds.map((node) => measureNode(node, { skipShadow: true }))); // TODO: similar code to compound-context-menu
    let offset = { x: 0, y: 0 };
    if (mouseOffset) {
      offset = {
        x: (mouseOffset.x - posScale.x) / posScale.scale - box.x,
        y: (mouseOffset.y - posScale.y) / posScale.scale - box.y,
      };
    }
    dragInfo.current.offset = offset;
    dragInfo.current.box = box;

    const dontSnap = ids.every((id) => consts.SNAPPING_EXCLUDED_ELEMENTS.indexOf(getElementTypeForId(id)) != -1);
    if (dontSnap) {
      dragInfo.current.snapInfo = null;
    } else {
      //Remove org chart nodes from the guidelines if their parent is being moved
      ids.forEach((id) => {
        if (getElementTypeForId(id) === consts.CANVAS_ELEMENTS.ORG_CHART) {
          const uniqueId = id.split("-")[1];
          nodes = nodes.concat(
            getSelectableNodes(
              (node) =>
                getElementTypeForId(node.attrs.id) === consts.CANVAS_ELEMENTS.ORG_CHART_NODE &&
                parseOrgChartNodeId(node.attrs.id).rootId === uniqueId
            )
          );
        }
      });
      // Use the same filtered nodes for snapping guidelines (exclude table titles)
      dragInfo.current.snapInfo = getSnappingGuidelines(layerRef.current, nodesForBounds);
    }

    const stats = ids.reduce((acc, cur) => {
      const type = getElementTypeForId(cur);
      acc[type] ??= 0;
      acc[type]++;
      return acc;
    }, {} as Record<TypeCanvasElement, number>);

    const initialState = drawPropsForNodes(nodes);
    const newMoveContext: ActuallyMovingElements = {
      kind: MovingStatus.Ongoing,
      elementUnderMouse: moving.elementUnderMouse,
      nodes,
      ids,
      stats,
      getInitialState: () => initialState,
      getNewState: () => drawPropsForNodes(getSelectableNodes((n) => n.attrs.isDraggable && ids.includes(n.id()))),
    };

    // Hide shape sockets when dragging starts to prevent flickering
    setShapeSocketsHidden(true);

    return ids.length == 0 ? null : newMoveContext;
  }

  function updateSelectionHighlightsAndBounds(dx: number, dy: number) {
    moveSelectionBoundsByDelta(dx, dy);
    // Instead of using delta movements, recalculate highlights based on actual positions
    updateSelectedNodesHighlights();
    updateSelectedGroupsHighlights();
  }

  function batchDraw() {
    const now = Date.now();
    if (now - moveElementsThrottleRef.current >= 16) {
      // 60 fps
      layerRef.current?.batchDraw();
      elementBoundsLayerRef.current?.batchDraw();
      moveElementsThrottleRef.current = now;
    }
  }

  function onMoveElementsDebounced(elements: ActuallyMovingElements) {
    // Clear any existing timeout
    if (onMoveElementsTimeoutRef.current) {
      clearTimeout(onMoveElementsTimeoutRef.current);
    }

    // Set a new timeout to fire only after inactivity
    onMoveElementsTimeoutRef.current = setTimeout(() => {
      const movement = { x: dragInfo.current.movementX, y: dragInfo.current.movementY };

      if (movement.x !== 0 || movement.y !== 0) {
        if (transformerRef.current) {
          transformerRef.current.listening(true);
          transformerRef.current.forceUpdate();
        }
        disableSelectionBounds();
        const selectedNodes = enableTransformer(false);

        try {
          // in case of state change, we don't want to move the elements again
          if (dragInfo.current.type === DragInfoType.KONVA) {
            onMoveElements(elements.ids, movement);
          }
        } finally {
          // Dispatch event when keyboard movement is complete
          window.dispatchEvent(new CustomEvent("keyboard-movement-complete"));
          setStageState((previous) => ({
            ...previous,
            movingElements: null,
            guidelines: null,
            distanceGuidelines: null,
            sizeGuidelines: null,
            connectorAnchorPoints: null,
            highlightedFrameElementIds: [],
            selectedNodes: selectedNodes,
          }));
          setTimeout(() => {
            setShapeSocketsHidden(false);
          }, 50);
        }
      }
      onMoveElementsTimeoutRef.current = null;
    }, 500);
  }

  function moveElementsWithSnapping(
    movingElements: ActuallyMovingElements | null,
    mousePosInCanvas: any,
    lockToAxis: any
  ) {
    if (!dragInfo.current || !movingElements) return;

    const { box: oldbox, offset, snapInfo } = dragInfo.current;

    const newbox = {
      width: oldbox.width,
      height: oldbox.height,
      x: mousePosInCanvas.x - offset.x,
      y: mousePosInCanvas.y - offset.y,
    };

    let dx = newbox.x - oldbox.x;
    let dy = newbox.y - oldbox.y;

    if (snapInfo) {
      const { horizSnap, vertSnap } = checkForSnaps(newbox, snapInfo);
      if (lockToAxis !== "y" && vertSnap && vertSnap.distance < consts.SNAPPING_DISTANCE) {
        newbox.x -= vertSnap.signedDistance;
      }
      if (lockToAxis !== "x" && horizSnap && horizSnap.distance < consts.SNAPPING_DISTANCE) {
        newbox.y -= horizSnap.signedDistance;
      }
    }

    dx = newbox.x - oldbox.x;
    dy = newbox.y - oldbox.y;
    dragInfo.current.box = newbox;

    const snappingData = getSnappingGuidelines(layerRef.current, movingElements.ids);
    const { horizSnap: newHorizSnap, vertSnap: newVertSnap } = checkForSnaps(newbox, snappingData);

    let finalDx = dx;
    let finalDy = dy;

    if (!snapInfo) {
      if (newHorizSnap && Math.abs(newHorizSnap.distance) <= consts.SNAPPING_DISTANCE) {
        finalDy = dy - newHorizSnap.distance;
      }
      if (newVertSnap && Math.abs(newVertSnap.distance) <= consts.SNAPPING_DISTANCE) {
        finalDx = dx - newVertSnap.distance;
      }
    }

    if (
      (newHorizSnap && Math.abs(newHorizSnap.distance) <= consts.SNAPPING_DISTANCE) ||
      (newVertSnap && Math.abs(newVertSnap.distance) <= consts.SNAPPING_DISTANCE)
    ) {
      updateMoveSnapGuidelines(newbox, snappingData, newHorizSnap, newVertSnap);
    } else {
      clearMoveGuidelines();
    }

    moveElements(movingElements, finalDx, finalDy);
  }

  // Calculate and render snap guidelines for move operations
  function updateMoveSnapGuidelines(box: any, snappingData: AllSnapCandidates, horizSnap: any, vertSnap: any) {
    if (!overlayRef.current) return;

    // Only render if we have snaps
    if (horizSnap || vertSnap) {
      const guidelines = getSnapLines(box, snappingData);
      const distanceGuidelines = getDistanceSnapLines(box, snappingData);

      // Use the shared utility function
      renderGuidelinesDirectly(overlayRef.current, guidelines, distanceGuidelines, posScale.scale, "snap-guide");
    } else {
      clearMoveGuidelines();
    }
  }

  function clearMoveGuidelines() {
    clearGuidelines(overlayRef.current, "snap-guide");
  }

  /**
   * Move mindmap by iterating through parent's children
   */
  function moveMindmapAsUnit(rootId: string, deltaX: number, deltaY: number): void {
    if (!layerRef.current) return;

    // Find the mindmap Group that contains all elements (nodes, connectors, buttons)
    // The Group should be the parent of the root node
    const rootNode = layerRef.current.findOne(`#${rootId}`);
    if (!rootNode) return;

    const mindmapGroup = rootNode.getParent();
    if (!mindmapGroup) return;

    // Move the entire Group - this moves all children (nodes, connectors, buttons) together
    const currentX = mindmapGroup.x();
    const currentY = mindmapGroup.y();
    mindmapGroup.x(currentX + deltaX);
    mindmapGroup.y(currentY + deltaY);
  }

  function moveAttachedComments(movingElementIds: string[], deltaX: number, deltaY: number): void {
    if (!overlayRef.current || commentIds.length === 0) return;

    // Find comments that are attached to any of the moving elements
    const attachedCommentNodes = commentIds
      .map((commentId) => {
        const commentNode = overlayRef.current?.findOne(`#${commentId}`);
        if (!commentNode) return null;

        const element = commentNode.attrs?.element;
        if (!element || !element.attachedTo) return null;

        // Check if this comment is attached to any of the moving elements
        const isAttachedToMovingElement = movingElementIds.includes(element.attachedTo);

        return isAttachedToMovingElement ? commentNode : null;
      })
      .filter((node) => node !== null);

    // Move the attached comments
    for (const commentNode of attachedCommentNodes) {
      const currentX = commentNode.x();
      const currentY = commentNode.y();

      if (Number.isFinite(currentX) && Number.isFinite(currentY)) {
        commentNode.x(currentX + deltaX);
        commentNode.y(currentY + deltaY);
      }
    }
  }

  function moveVotingButtons(movingElementIds: string[], deltaX: number, deltaY: number): void {
    // Move voting buttons just like we move comments - update their current position directly
    const { buttonRefs } = votingButtonsOffsetRef.current;

    for (const elementId of movingElementIds) {
      const buttonInfo = buttonRefs.get(elementId);
      if (buttonInfo) {
        const { element, currentX, currentY } = buttonInfo;

        // Update current position
        const newX = currentX + deltaX;
        const newY = currentY + deltaY;

        // Apply new position to the DOM
        element.style.transform = `translate(${newX}px, ${newY}px) translateX(-50%) translateY(-50%)`;

        // Store updated position for next move
        buttonInfo.currentX = newX;
        buttonInfo.currentY = newY;
      }
    }
  }

  // Update your moveElements function with rate limiting
  function moveElements(elements: ActuallyMovingElements, x: number, y: number, updateTotalMovement = true): void {
    // Always update the total movement tracking immediately
    if (dragInfo.current && updateTotalMovement) {
      dragInfo.current.movementX += x;
      dragInfo.current.movementY += y;
    }

    // Validate and convert movement deltas to numbers to prevent string concatenation
    const deltaX = Number(x);
    const deltaY = Number(y);

    if (!Number.isFinite(deltaX) || !Number.isFinite(deltaY)) {
      console.warn("Invalid movement deltas:", { x, y, deltaX, deltaY });
      return;
    }

    // If the feature flag is enabled, we need to use the state change type to persist the changes
    if (!newDraggingExperience) {
      dragInfo.current.type = DragInfoType.STATE_CHANGE;
      onMoveElements(elements.ids, { x: deltaX, y: deltaY });
      return;
    }

    // disable transformer and enable selection bounding box once
    const transformerNodes = transformerRef.current?._nodes;
    if (transformerNodes && transformerNodes.length > 1) {
      const boundingBoxes = [];
      for (const node of transformerNodes) {
        const boundingBox = getBoundingBoxWithFallback(node);
        if (boundingBox) {
          boundingBoxes.push(boundingBox);
        }
      }
      enableSelectionBounds(BoundingBox.expandOnAll(boundingBoxes));
      if (transformerRef.current) {
        transformerRef.current.listening(false);
      }
      disableTransformer();
    }

    const mindmapRootIds = new Set<string>();

    for (const node of elements.nodes) {
      // Check if we're moving mindmap root elements and handle them specially
      const element = node.attrs?.element;
      if (element?.type === "mindmap" && !element?.parentId) {
        const rootId = node.attrs?.id;
        if (rootId && !mindmapRootIds.has(rootId)) {
          moveMindmapAsUnit(rootId, deltaX, deltaY);
          mindmapRootIds.add(rootId);
        }
        continue;
      }

      if (
        node.attrs?.type === consts.CANVAS_ELEMENTS.CARD_STACK ||
        node.attrs?.type === consts.CANVAS_ELEMENTS.LIVE_INTEGRATION
      ) {
        // Move the main element
        const currentX = node.x();
        const currentY = node.y();

        if (Number.isFinite(currentX) && Number.isFinite(currentY)) {
          node.x(currentX + deltaX);
          node.y(currentY + deltaY);

          // Find and move all related card stack components by name
          const cardStackId = node.attrs?.id;
          if (cardStackId && layerRef.current) {
            const componentNames = [
              `cardstack-${cardStackId}-frame`,
              `cardstack-${cardStackId}-cards`,
              `cardstack-${cardStackId}-footer`,
              `cardstack-${cardStackId}-error`,
            ];

            for (const componentName of componentNames) {
              const component = layerRef.current.findOne(`.${componentName}`);
              if (component) {
                const compX = component.x();
                const compY = component.y();
                if (Number.isFinite(compX) && Number.isFinite(compY)) {
                  component.x(compX + deltaX);
                  component.y(compY + deltaY);
                }
              }
            }
          }
        }
        continue;
      }

      if (node.attrs?.type === consts.CANVAS_ELEMENTS.ORG_CHART) {
        // Check if this is a child node and its parent root is also being moved
        if (node.id().includes("orgChartNode-")) {
          // Extract root ID from child node ID (e.g., 'orgChartNode-rootId-childId' -> 'orgChartRoot-rootId')
          const rootId = node.id().replace(/^orgChartNode-([^-]+)-.*$/, "orgChartRoot-$1");
          const isRootAlsoMoving = elements.ids.includes(rootId);
          if (isRootAlsoMoving) {
            // Skip this child node - let the root handle moving everything
            continue;
          }
        }

        // Handle main org chart root movement and move wrapper with it
        const currentX = node.x();
        const currentY = node.y();

        // Validate current position
        if (!Number.isFinite(currentX) || !Number.isFinite(currentY)) {
          console.warn(`Org chart node ${node.id()} has invalid position:`, { x: currentX, y: currentY });
          continue;
        }

        const newX = currentX + deltaX;
        const newY = currentY + deltaY;

        node.x(newX);
        node.y(newY);

        // Move wrapper group to follow the root (only for main root, not child nodes)
        if (
          node.id().startsWith(consts.CANVAS_ELEMENTS.ORG_CHART) ||
          node.id().startsWith(consts.CANVAS_ELEMENTS.MINDMAP_ORG_CHART)
        ) {
          const wrapperGroupId = `${node.id()}-wrapper`;
          const wrapperGroup = layerRef.current?.findOne(`#${wrapperGroupId}`);
          if (wrapperGroup) {
            wrapperGroup.x(wrapperGroup.x() + deltaX);
            wrapperGroup.y(wrapperGroup.y() + deltaY);
          }
        }
        continue;
      }

      const currentX = node.x();
      const currentY = node.y();

      // Validate current position
      if (!Number.isFinite(currentX) || !Number.isFinite(currentY)) {
        console.warn(`Node ${node.id()} has invalid position:`, { x: currentX, y: currentY });
        continue;
      }

      const newX = currentX + deltaX;
      const newY = currentY + deltaY;

      // Validate new position before setting
      if (!Number.isFinite(newX) || !Number.isFinite(newY)) {
        console.warn(`Calculated invalid position for node ${node.id()}:`, {
          newX,
          newY,
          currentX,
          currentY,
          deltaX,
          deltaY,
        });
        continue;
      }

      node.x(newX);
      node.y(newY);
    }

    const hasPartialConnectorChanges = moveConnectors(
      elements.nodes,
      deltaX,
      deltaY,
      layerRef.current,
      selectedElementIds
    );

    // Check if we're moving tasks with containerId (cards in stacks) or card stacks themselves
    const hasCardStackElements = elements.nodes.some((node) => {
      const element = node.attrs.element;
      return (
        // Tasks/cards that belong to a card stack
        (element?.containerId && element?.type === consts.CANVAS_ELEMENTS.INTEGRATION) ||
        (element?.containerId && element?.type === consts.CANVAS_ELEMENTS.MONDAY_TASK) ||
        // Card stacks themselves
        element?.type === consts.CANVAS_ELEMENTS.CARD_STACK
      );
    });

    if (hasPartialConnectorChanges || hasCardStackElements) {
      dragInfo.current.type = DragInfoType.STATE_CHANGE;
      // TODO: this is a hack to persist the connector changes and also regular card stack elements through the mutation system
      // because of multiplayer updates, there are issues with updating the connectors
      onMoveElements(elements.ids, { x: deltaX, y: deltaY });
    } else {
      // Move voting buttons to follow selected elements
      moveVotingButtons(elements.ids, deltaX, deltaY);
      dragInfo.current.type = DragInfoType.KONVA;
    }

    moveFrameBackground(elements, layerRef);
    highlightDragEnteringFrame(elements, layerRef, overlayRef, frames, hiddenFrameIds);
    updateSelectionHighlightsAndBounds(deltaX, deltaY);

    // Move attached comments with their parent elements
    moveAttachedComments(elements.ids, deltaX, deltaY);

    batchDraw();
  }

  // Called on each mouse move - very expensive operation
  // TODO: find a better way to do this
  function calculateElementsAttachingToFrames(elementIds: string[]): [Map<string, string[]>, string[]] {
    const mutatingElements = new Map<string, string[]>();
    const detachedElements = new Array<string>();

    // If there are no frames, we can return immediately
    if (Object.keys(frames).length === 0) {
      return [mutatingElements, detachedElements];
    }
    const elementsInFrames = elementIds.reduce((acc: string[], id: string) => {
      if (frames[id]) {
        acc.push(...frames[id]);
      }
      return acc;
    }, new Array<string>());

    const draggedNodes = elementIds
      .filter((id) => !elementsInFrames.includes(id)) // filter out elements that are already in dragged frames
      .flatMap((id: any) =>
        layerRef.current?.findOne(
          (node: any) => !node.attrs.isFrame && node.attrs.id === id && !node.attrs.isFrameTitle
        )
      )
      .filter((node) => node && node.attrs.isSelectable); // filter out elements that are not selectable (in hidden frames)

    if (!draggedNodes || draggedNodes.length == 0) {
      return [mutatingElements, detachedElements];
    }
    const frameNodes = getSelectableNodes((node) => node.attrs.isFrame && hiddenFrameIds.indexOf(node.attrs.id) == -1);
    const stage = stageRef.current;
    for (const node of draggedNodes) {
      const { x, y, width, height } = node?.getClientRect({ relativeTo: stage as any }) ?? {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
      };
      const centerRect = {
        x: x + width / 2,
        y: y + height / 2,
        width: 1,
        height: 1,
      };

      const intersectingFrame = frameNodes
        .filter((frame: any) => Konva.Util.haveIntersection(frame.getClientRect({ relativeTo: stage }), centerRect))
        .reduce((smallestFrame: any | null, frame: any) => {
          if (!smallestFrame) {
            return frame;
          }
          const { x: x1, y: y1, width: width1, height: height1 } = frame.getClientRect({ relativeTo: stage });
          const { x: x2, y: y2, width: width2, height: height2 } = smallestFrame.getClientRect({ relativeTo: stage });
          if (x1 > x2 && x1 + width1 < x2 + width2 && y1 > y2 && y1 + height1 < y2 + height2) {
            return frame;
          }
          return smallestFrame;
        }, null);
      if (intersectingFrame) {
        const frameElements = mutatingElements.get(intersectingFrame.attrs.id) || [];
        frameElements.push(node?.attrs.id);
        mutatingElements.set(intersectingFrame.attrs.id, frameElements);
      } else if (node?.attrs.element.frameId) {
        detachedElements.push(node?.attrs.id);
      }
    }
    return [mutatingElements, detachedElements];
  }

  async function createFrameFromGhost() {
    if (stageState?.ghostElement?.kind != "Frame") return;
    const { point1, point2 } = stageState.ghostElement;
    let width = Math.abs(point1.x - point2.x);
    let height = Math.abs(point1.y - point2.y);
    const x = Math.min(point1.x, point2.x);
    const y = Math.min(point1.y, point2.y);
    if (width == 0) width = 1024;
    if (height == 0) height = 768;

    const newIds = await createElementAndRegisterIt({ x, y }, CursorType.frame, "side-menu", { x: width, y: height });
    if (!newIds || newIds.length === 0) {
      return;
    }
    const frameId = newIds[0];
    setTimeout(() => {
      attachElementsToFrame(frameId);
    }, 100);
  }

  async function createTableFromGhost(isCentered: boolean, isEquiSided: boolean) {
    if (stageState?.ghostElement?.kind != "Table") return;
    const { point1, point2 } = stageState.ghostElement;
    if (distance(point1, point2) < 10) {
      // just create a default-sized box
      createElementAndRegisterIt(point1, CursorType.table, "side-menu");
    } else {
      const [pos1, pos2] = MathUtils.computeBoundingBox(point1, point2, isCentered, isEquiSided, {
        x: sizes.table.cellWidth / 2,
        y: sizes.table.cellHeight / 2,
      });
      const width = Math.abs(pos2.x - pos1.x);
      const height = Math.abs(pos2.y - pos1.y);
      const x = Math.min(pos1.x, pos2.x);
      const y = Math.min(pos1.y, pos2.y);
      createElementAndRegisterIt({ x, y }, CursorType.table, "side-menu", { x: width, y: height });
    }
  }

  function attachElementsToFrame(frameId: string) {
    const frame = getSelectableNodes((i) => i.attrs.id === frameId)?.[0];

    if (!frame) {
      return;
    }

    const elementIds = getCanvasElementsInRect(frame.getClientRect())
      .filter((node) => {
        const isFrame = node.attrs.isFrame;
        const attachedToFrame: string | undefined = node.attrs.element.frameId;
        return !isFrame && (!attachedToFrame || !frames[attachedToFrame]);
      })
      .map((node) => node.attrs.id);

    syncService?.mutate.attachToFrame({ frameId, ids: elementIds });
  }

  function handleContextMenu(e: KonvaEventObject<MouseEvent>) {
    const stage = e.target.getStage();
    const pointerPosition = stage!.getPointerPosition() as Vector2d;
    const clickInfo = analyzeMousePosition(e, true);
    const enablePaste = true; //enable paste, though clipboard might be empty (can we check it...)

    if (clickInfo.target == "stage") {
      clearSelected(); // deselect - same as Miro
      setContextMenuState({
        anchor: new DOMRect(pointerPosition.x, pointerPosition.y),
        enablePaste,
        enableGroup: false,
        enableUngroup: false,
        enableRelativeMove: false,
        enableComments: !!cordAuthToken,
        openedFrom: "canvas",
      });
    } else if (clickInfo.target == "selection-area") {
      if (!clickInfo.shapeGroup) {
        console.warn("what??");
        return;
      }
      // clicked on empty space
      // clickInfo.shapeGroup is the selected-elements (=== transfromer.node())
      const singleGroupIsSelected = selectedElementIds.length > 1 && doAllElementsBelongToSameGroup(selectedElementIds);
      const nodes = getSelectableNodes(
        (i) => clickInfo.shapeGroup!.includes(i.attrs.id) && !i.attrs.isFrameTitle && !i.attrs.isTableTitle
      );
      setContextMenuState({
        anchor: new DOMRect(pointerPosition.x, pointerPosition.y),
        nodes,
        enablePaste,
        enableGroup: clickInfo.shapeGroup.length > 1 && !singleGroupIsSelected,
        enableUngroup: singleGroupIsSelected,
        enableComments: !!cordAuthToken,
        enableRelativeMove: nodes.length === 1 && hasIntersects(nodes[0]),
        openedFrom: "canvas",
      });
    } else if (clickInfo.target == "shape") {
      // we click on a shape
      const the_group = clickInfo.shapeGroup ?? [clickInfo.shapeId!];
      const singleGroupIsSelected = doAllElementsBelongToSameGroup(selectedElementIds);
      const nodes = getSelectableNodes(
        (i) => the_group.includes(i.attrs.id) && !i.attrs.isFrameTitle && !i.attrs.isTableTitle
      );
      setSelectedIds(the_group);
      elementBoundsLayerRef.current?.batchDraw();
      enableTransformer();
      setContextMenuState({
        anchor: new DOMRect(pointerPosition.x, pointerPosition.y),
        nodes,
        enablePaste,
        enableGroup: the_group.length > 1 && !singleGroupIsSelected,
        enableUngroup: the_group.length > 1,
        enableComments: !!cordAuthToken,
        enableRelativeMove: nodes.length === 1 && hasIntersects(nodes[0]),
        openedFrom: "canvas",
      });
    } else if (clickInfo.target == "frame") {
      // we click on a frame
      setSelectedIds([clickInfo.shapeId!]);
      enableTransformer();
      const nodes = getSelectableNodes(
        (i) => i.attrs.id === clickInfo.shapeId! && !i.attrs.isFrameTitle && !i.attrs.isTableTitle
      );
      setContextMenuState({
        anchor: new DOMRect(pointerPosition.x, pointerPosition.y),
        nodes,
        enablePaste,
        enableGroup: false,
        enableUngroup: false,
        enableComments: !!cordAuthToken,
        enableRelativeMove: nodes.length === 1 && hasIntersects(nodes[0]),
        openedFrom: "canvas",
      });
    }
  }

  async function createSelectedTemplateAtCursorPosition() {
    if (!stageState?.ghostElement || !newElementData.template || !newElementData.template.id) {
      return;
    }
    try {
      onError && onError("Preparing template...");

      const { position } = stageState.ghostElement as GhostTemplate;

      await placeTemplate(newElementData.template, getTemplateData, position, "canvas-template-library");
    } catch (err) {
      console.error("Error creating template", newElementData.template.id, err);
    } finally {
      onError && onError(null);
    }
  }

  function getElementsOfGroupOrFrame(node: Konva.Node): string[] {
    if (!node.attrs.element) {
      console.warn("got empty node", node);
      return [];
    }
    if (!node.attrs.isSelectable) {
      return [];
    }
    if (node.attrs.isFrame) {
      const elements = frames[node.attrs.id] ?? [];
      return [node.attrs.id, ...elements];
    }
    if (node.attrs.element.groupId) {
      const groupId = node.attrs.element.groupId;
      const elements = groups[groupId] ?? [];
      return [node.attrs.id, ...elements];
    }
    return [node.attrs.id];
  }

  function getGroupsForElements(elementIds: string[]): Set<string> {
    const groupsFound = new Set<string>();
    for (const groupId in groups) {
      // Check if any of the elements in this group are in our elementIds
      if (groups[groupId].some((id) => elementIds.includes(id))) {
        groupsFound.add(groupId);
      }
    }
    return groupsFound;
  }

  function getGroupForElement(elementId: string) {
    for (const groupId in groups) {
      if (groups[groupId].includes(elementId)) return groupId;
    }
    return null;
  }

  function doAllElementsBelongToSameGroup(elementIds: string[]) {
    if (elementIds.length <= 1) {
      return true;
    }
    let id1 = null;
    for (const id of elementIds) {
      const grp = getGroupForElement(id);
      if (grp == null) {
        // this element is ungrouped. we know we have 2 elements at least, so they can't
        // belong to a single group
        return false;
      }
      if (id1 == null) {
        id1 = grp;
      } else if (id1 != grp) {
        return false;
      }
    }
    return true;
  }

  function handleContextMenuOperation(e: ContextMenuOps | null, requiresUpgrade: boolean) {
    switch (e) {
      case "cut": {
        handleCut(contextMenuState!.nodes!);
        break;
      }
      case "copy": {
        handleCopy(contextMenuState!.nodes!);
        break;
      }
      case "delete": {
        handleDeleteElements(contextMenuState?.nodes ? [...contextMenuState.nodes] : [], true);
        break;
      }
      case "link": {
        if (contextMenuState?.nodes) {
          const elementsArray = [...contextMenuState.nodes];
          if (elementsArray.length === 1) {
            handleLinkElement(elementsArray[0]);
          }
        }
        break;
      }
      case "group": {
        handleGroup(contextMenuState?.nodes);
        break;
      }
      case "ungroup": {
        handleUngroup(contextMenuState?.nodes);
        break;
      }
      case "tofront": {
        if (contextMenuState?.nodes) handleToFront(contextMenuState.nodes);
        break;
      }
      case "toback": {
        if (contextMenuState?.nodes) handleToBack(contextMenuState.nodes);
        break;
      }
      case "forward": {
        if (contextMenuState?.nodes) handleMoveForward(contextMenuState.nodes);
        break;
      }
      case "backward": {
        if (contextMenuState?.nodes) handleMoveBackward(contextMenuState.nodes);
        break;
      }
      case "lock": {
        lockElements(contextMenuState?.nodes);
        break;
      }
      case "unlock": {
        unlockElements(contextMenuState?.nodes);
        break;
      }
      case "paste": {
        handlePaste(getDistanceFromEvent());
        break;
      }
      case "zoom-in": {
        const zoomInStep = (stageRef.current?.getStage().scaleX() ?? 1) * -consts.ZOOM_STEP;
        zoomStage(stageRef.current?.getStage(), zoomInStep, "mouse");
        break;
      }
      case "zoom-out": {
        const zoomOutStep = (stageRef.current?.getStage().scaleX() ?? 1) * consts.ZOOM_STEP;
        zoomStage(stageRef.current?.getStage(), zoomOutStep, "mouse");
        break;
      }
      case "add-text": {
        createElementAndRegisterIt(getDistanceFromEvent(), CursorType.text, "context-menu");
        break;
      }
      case "add-note": {
        createElementAndRegisterIt(getDistanceFromEvent(), CursorType.stickyNote, "context-menu");
        break;
      }
      case "add-shape": {
        createElementAndRegisterIt(getDistanceFromEvent(), CursorType.shape, "context-menu");
        break;
      }
      case "add-mindmap": {
        createElementAndRegisterIt(getDistanceFromEvent(), CursorType.mindmap, "context-menu");
        break;
      }
      case "add-comment": {
        const elementBounding = getDistanceFromEvent();
        addNewCommentFromSubMenu(elementBounding);
        break;
      }
      case "set-as-thumbnail": {
        if (contextMenuState?.nodes) {
          const elements = Array.from(contextMenuState.nodes);
          if (elements.length === 1) {
            const element = elements[0];
            if (element.attrs.isFrame) {
              syncService?.mutate.setThumbnailId(element.attrs.id);
            }
          }
        }
        break;
      }
      case "export-frame": {
        break;
      }
      case "export-frame-image": {
        if (contextMenuState?.nodes) {
          const frameNodes: any[] = [];
          for (const node of contextMenuState.nodes) {
            if (node.attrs.isFrame && !node.attrs.isFrameTitle) {
              frameNodes.push(node);
            }
          }
          setExportingCanvas(true);
          Promise.allSettled(
            frameNodes.map((node) =>
              getSnapshotOfFrame({
                canvasLayer: layerRef.current,
                frameId: node.attrs.id,
                posScale,
                withWatermark: !premiumExportEnabled,
                onProgress: noop,
                quality: 0.04, // low quality, even if user is paying
              })
            )
          )
            .then((results) => {
              let hadFailures = false;
              results.forEach((result, index) => {
                if (result.status === "fulfilled") {
                  downloadURI(
                    result.value ?? "",
                    sanitizeExportFilename(frameNodes[index].attrs.element.title) + ".png"
                  );
                } else {
                  hadFailures = true;
                }
              });
              if (hadFailures) {
                console.error("failed to export frame as image");
                onError && onError("We're sorry, the export process had an error..");
              }
            })
            .finally(() => {
              tracking.trackEvent(consts.TRACKING_CATEGORY.CANVAS_ACTION, "export_frame", "as_image");
              setExportingCanvas(false);
            });
        }
        break;
      }
      case "export-frame-pdf": {
        if (!user || user.isAnonymous) {
          setSignUpToContinueModal(true);
          break;
        }
        if (requiresUpgrade) {
          // code duplicated from index.tsx, export-dialog:onUpgrade
          tracking.reportGoogleAnalyticsConversion(consts.GA_CONVERSION_TYPES.UPGRADE_CTA);
          tracking.trackEvent(consts.TRACKING_CATEGORY.BILLING, "upgrade-cta-click", "export-frame-as-pdf");
          showUpgradeModal && showUpgradeModal("general");
          break;
        }

        // if we're here, we know that only frame elements were selected.
        // I still verify it here, just to be sure.
        if (contextMenuState?.nodes) {
          const frameNodes: any[] = [];
          for (const node of contextMenuState.nodes) {
            if (node.attrs.isFrame && !node.attrs.isFrameTitle) {
              frameNodes.push(node);
            }
          }
          // TODO: show modal that blocks interaction while exporting
          // TODO: this code is repeated in multiple places, should be refactored
          (async function () {
            try {
              setExportingCanvas(true);
              const exporter = await PdfExporter.create();
              const areas = layerRef.current
                ? frameNodes.map((frame) => getAreaToExport(layerRef.current as Konva.Layer, frame))
                : [];
              exporter.prepareDocument(
                areas,
                {
                  title: board?.name ?? "",
                  author: user?.name ?? "",
                  creator: "WorkCanvas.com",
                },
                layerRef.current?.parent?.scaleX()
              );
              for (let i = 0; i < frameNodes.length; i++) {
                exporter.page(layerRef.current, i, areas[i], frameNodes[i].id());
              }
              const boardName = sanitizeExportFilename(board?.name ?? "WorkCanvas");
              exporter.save(boardName);
            } catch (error) {
              console.error("failed to export frame as pdf", error);
              onError && onError("We're sorry, the export process had an error..");
            } finally {
              tracking.trackEvent(consts.TRACKING_CATEGORY.CANVAS_ACTION, "export_frame", "as_pdf");
              setExportingCanvas(false);
            }
          })();
        }
        break;
      }
      case "clear-thumbnail": {
        syncService?.mutate.clearThumbnailId();
        break;
      }
      case "add-gantt-task": {
        if (!contextMenuState) {
          return;
        }

        const elementsArray = [...(contextMenuState.nodes ?? [])];
        const gantt = elementsArray.find((element) => element.attrs.type === consts.CANVAS_ELEMENTS.GANTT);
        if (!gantt) {
          return;
        }

        const ganttId = gantt?.attrs.id;
        const element = gantt.attrs.element;

        const baseTaskPosition = getBaseTaskPosition(element);
        if (!baseTaskPosition) {
          return;
        }
        const { rowId, color, splitId } = baseTaskPosition;

        const fromDate = cleanDate(element.startDate).getTime();
        const toDate = cleanDate(addDays(fromDate, 1)).getTime();
        const title = "New Task";
        const fieldValues = {
          [splitId]: rowId,
        };

        const currentAmount = parseInt(sessionStorage.getItem(ganttId) || "0");
        if (currentAmount >= maxAllowedTasksInPlan) {
          showUpgradeModal?.("gantt");
          return;
        }

        const cursorType = newTaskElementEnabled ? CursorType.task : CursorType.taskCard;
        const newTaskType = newTaskElementEnabled ? "task" : "taskCard";

        const params = {
          containerId: ganttId,
          title,
          fromDate,
          toDate,
        };

        if (contextMenuState.openedFrom === "toolbar") {
          createElementAndRegisterIt(
            getDistanceFromEvent(),
            cursorType,
            "context-menu",
            undefined,
            {
              ...params,
              fieldValues,
              [newTaskElementEnabled ? "fill" : "color"]: color,
            },
            false
          );
        } else {
          createElementAndRegisterIt(getDistanceFromEvent(), cursorType, "context-menu", undefined, params, true);
        }
        trackGanttEvent("gantt_task_created", {
          from: "context-menu",
          amount: parseInt(sessionStorage.getItem(ganttId) || "0") + 1,
          type: newTaskType,
        });
        break;
      }
      default: {
        break;
      }
    }
    setContextMenuState(null);
  }

  function getNodesByGroupIds(groupIds: string[]): Konva.Node[] {
    return getSelectableNodes((node) => groupIds.includes(node.attrs.element?.groupId ?? ""));
  }

  function getNodesByIds(ids: string[]): Konva.Node[] {
    return getSelectableNodes((node) => ids.includes(node.attrs.id));
  }

  function getNodeById(id: string): Konva.Node | null {
    return getSelectableNodes((node) => node.attrs.id === id)[0] ?? null;
  }

  // Get all selectable nodes in the layer
  function getSelectableNodes(filterFn?: (i: any) => boolean): Konva.Node[] {
    if (!layerRef.current) return [];
    if (filterFn) {
      return layerRef.current.find((i: any) => i.attrs?.isCanvasElement && !i.hidden && filterFn(i)).toArray();
    } else {
      return layerRef.current.find((i: any) => i.attrs?.isCanvasElement && !i.hidden).toArray();
    }
  }

  function getConnectableNodes(filterFn?: (i: any) => boolean): Konva.Node[] {
    if (filterFn) {
      const newFilterFn = (i: any) => i.attrs?.isConnectable && filterFn(i);
      return getSelectableNodes(newFilterFn);
    }
    return getSelectableNodes((i: any) => i.attrs?.isConnectable);
  }

  function getDraggableNodes(filterFn?: (i: any) => boolean): Konva.Node[] {
    if (filterFn) {
      const newFilterFn = (i: any) => i.attrs?.isDraggable && filterFn(i);
      return getSelectableNodes(newFilterFn);
    }
    return getSelectableNodes((i: any) => i.attrs?.isDraggable);
  }

  // Return filter function to filter elements just by bouding box intersection.
  function bboxIntersection(rectViewportSpace: IRect) {
    return (i: Konva.Node) => Konva.Util.haveIntersection(rectViewportSpace, i.getClientRect());
  }

  function hasIntersects(element: Konva.Node) {
    return (
      getSelectableNodes((n: Konva.Node) =>
        Konva.Util.haveIntersection(element.getClientRect(), n.getClientRect())
      ).filter((n) => n.id() !== element.id()).length > 0
    );
  }

  // Return filter function to filter elements by precise shape calculations, not just bbox
  function refineRectSelection(rectViewportSpace: IRect) {
    const { x, y, width, height } = rectViewportSpace;
    const selectionRectCorners = [
      { x, y },
      { x: x + width, y },
      { x: x + width, y: y + height },
      { x, y: y + height },
      { x, y },
    ];
    const checkSelectionRectCover = MathUtils.doesBoxCoversAnother(MathUtils.padRect(rectViewportSpace, 1));

    return (i: any) => {
      // For all elements except drawing and connectors we do bounding-box intersection only
      if (
        i.attrs.element.type != consts.CANVAS_ELEMENTS.CONNECTOR &&
        i.attrs.element.type != consts.CANVAS_ELEMENTS.DRAWING &&
        i.attrs.element.type != consts.CANVAS_ELEMENTS.FRAME
      )
        return true;

      if (i.attrs.element.type == consts.CANVAS_ELEMENTS.DRAWING) {
        const line = i.findOne("Line");
        if (!line) {
          console.warn("Can't find Line node inside drawing element");
          return false;
        }
        // fast and simple test - check if the drawing is entirely inside
        // the selection rect.
        if (checkSelectionRectCover(i.getClientRect())) {
          return true;
        }

        // more detailed test - check if any of the points are inside
        // the selection rect (remember to transform them to viewport coordinates)
        const points = line?.getTensionPoints() ?? i.attrs.element.points;
        const elementTr = i.getAbsoluteTransform();
        for (let j = 0; j <= points.length - 2; j += 2) {
          const x = points[j + 0];
          const y = points[j + 1];
          const p = elementTr.point({ x, y });
          if (MathUtils.isPointInRect(p, rectViewportSpace)) return true;
        }
        return false;
      }
      if (i.attrs.element.type == consts.CANVAS_ELEMENTS.FRAME) {
        const borderThreshold = 10;
        const frameRect = i.getClientRect();
        const topBorderRect = {
          x: frameRect.x,
          y: frameRect.y,
          width: frameRect.width,
          height: borderThreshold,
        };
        const rightBorderRect = {
          x: frameRect.x + frameRect.width - borderThreshold,
          y: frameRect.y,
          width: borderThreshold,
          height: frameRect.height,
        };
        const bottomBorderRect = {
          x: frameRect.x,
          y: frameRect.y + frameRect.height - borderThreshold,
          width: frameRect.width,
          height: borderThreshold,
        };
        const leftBorderRect = {
          x: frameRect.x,
          y: frameRect.y,
          width: borderThreshold,
          height: frameRect.height,
        };
        return (
          Konva.Util.haveIntersection(rectViewportSpace, topBorderRect) ||
          Konva.Util.haveIntersection(rectViewportSpace, rightBorderRect) ||
          Konva.Util.haveIntersection(rectViewportSpace, bottomBorderRect) ||
          Konva.Util.haveIntersection(rectViewportSpace, leftBorderRect)
        );
      }

      const textElement = i.findOne(".connector-text-label");
      if (textElement) {
        const touchedText = Konva.Util.haveIntersection(rectViewportSpace, textElement.getClientRect());
        if (touchedText) return true;
      }
      if (checkSelectionRectCover(i.getClientRect())) {
        return true;
      }
      const konvaNode = i.findOne("Shape");
      if (!konvaNode) return false;

      if (konvaNode.attrs.connectorData) {
        const invTr = i.getAbsoluteTransform().copy().invert();
        const corners = selectionRectCorners.map((p) => invTr.point(p));
        return konvaNode.attrs.connectorData.doesIntersectBox(corners);
      }

      console.warn("Found connector with no data attribute");
      return true;
    };
  }

  function refinePointSelection(point: MathUtils.PointInViewport) {
    const pointInStage = MathUtils.viewportToStage(stageRef.current?.getStage().getAbsoluteTransform() as any, point);
    return (node: Konva.Node) => {
      let type: TypeCanvasElement = node.attrs.type;
      if (!type) {
        type = getElementTypeForId(node.id());
      }
      const provider = getElementGraphicsProvider(type);
      if (provider) {
        return provider.isPointInNode(point, node);
      }
      switch (type) {
        case consts.CANVAS_ELEMENTS.COMMENT: {
          return false;
        }

        case consts.CANVAS_ELEMENTS.SHAPE: {
          if (node.attrs.element.type == consts.CANVAS_ELEMENTS.SHAPE) {
            if (MathUtils.isPointInRect(point, node.getClientRect())) {
              const shape = node.attrs.element as Shape;
              const { x, y, scaleX: width, scaleY: height, rotate } = shape;
              const subtype = shape.subtype;
              const data = subtype ? (ShapesData as any)[subtype] : undefined;
              const offset = {
                x: data.viewbox[0] + data.viewbox[2] / 2,
                y: data.viewbox[1] + data.viewbox[3] / 2,
              };
              const outline = new Path2D(data.outline);
              const p = new Path2D();
              p.addPath(outline, new DOMMatrix().translate(-offset.x, -offset.y));
              // building this object is so cumbersome
              const box = {
                x,
                y,
                width,
                height,
                rotate: rotate as Degrees,
              };
              const lpoint = Transform.InvPoint(box, pointInStage);
              let canvas = document.querySelector<HTMLCanvasElement>("#measureCanvas");
              if (!canvas) {
                canvas = document.createElement("canvas");
                canvas.id = "measureCanvas";
              }
              const ctx = canvas.getContext("2d")!;
              return (
                ctx.isPointInPath(p, lpoint.x, lpoint.y) ||
                ((ctx.lineWidth = node.attrs.element.strokeWidth), ctx.isPointInStroke(p, lpoint.x, lpoint.y))
              );
            }
            return false;
          } else {
            const p2 = canvasPointToNormalizedPoint(pointInStage, "shape", node.attrs.element);
            return isNormalizedPointInsideElement(p2, "shape", node.attrs.element);
          }
        }

        case consts.CANVAS_ELEMENTS.MINDMAP: {
          return true;
        }
        case consts.CANVAS_ELEMENTS.MINDMAP_ORG_CHART: {
          return true;
        }

        case consts.CANVAS_ELEMENTS.FILE:
        case consts.CANVAS_ELEMENTS.PDF:
        case consts.CANVAS_ELEMENTS.TEXT_BLOCK: {
          const p2 = canvasPointToNormalizedPoint(pointInStage, type, node.attrs.element);
          return p2.x >= 0 && p2.x <= 1 && p2.y >= 0 && p2.y <= 1;
        }

        case consts.CANVAS_ELEMENTS.STICKY_NOTE:
        case consts.CANVAS_ELEMENTS.TASK_CARD:
        case consts.CANVAS_ELEMENTS.INTEGRATION: {
          return MathUtils.isPointInRect(point, node.getClientRect());
        }

        case consts.CANVAS_ELEMENTS.CONNECTOR: {
          const textElement = (node as any).findOne(".connector-text-label");
          if (textElement) {
            const touchedText = MathUtils.isPointInRect(point, textElement.getClientRect());
            if (touchedText) return true;
          }

          const shapeNode = (node as any).findOne("Shape");
          if (!shapeNode) {
            console.error("no shape found for connector");
            return false;
          }
          const pointElementSpace = shapeNode.getAbsoluteTransform().copy().invert().point(point);
          const context = node.getLayer()?.getContext()?._context;
          if (!context) {
            console.error("Can't find canvas context in a konva node ?!?!?!");
            return false;
          }
          context.save();
          context.resetTransform();
          context.beginPath();
          context.lineWidth = Math.max(DistanceToSelectLines_pixels / posScale.scale, shapeNode.attrs.strokeWidth);

          if (shapeNode.attrs.connectorData) {
            const data = shapeNode.attrs.connectorData;
            data.drawOnCanvas(context);
          } else {
            console.warn("Found connector with no data attribute");
            context.restore();
            return false;
          }
          const hit = context.isPointInStroke(pointElementSpace.x, pointElementSpace.y);
          context.restore();
          return hit;
        }

        case consts.CANVAS_ELEMENTS.DRAWING: {
          const line = (node as any).findOne("Line");
          console.assert(line, "drawing should have a line child");
          const points = line.getTensionPoints();
          const pointElementSpace = line.getAbsoluteTransform().copy().invert().point(point);
          // TODO: use line.sceneFunc to draw on a custom context object, that instead of stroke/fill the shape
          // checks isPointInStroke, which is really the best way to check !
          // the sceneFunc of the node is in local space (without the scale,transform and rotation)
          return MathUtils.isPointCloseToLines(
            pointElementSpace,
            points,
            DistanceToSelectLines_pixels / posScale.scale
          );
        }

        case consts.CANVAS_ELEMENTS.FRAME: {
          const rect = node.getClientRect();
          const largerRect = MathUtils.padRect(rect, 10);
          const smallerRect = MathUtils.padRect(rect, -17);
          return MathUtils.isPointInRect(point, largerRect) && !MathUtils.isPointInRect(point, smallerRect);
        }

        case consts.CANVAS_ELEMENTS.ORG_CHART:
        case consts.CANVAS_ELEMENTS.ORG_CHART_NODE: {
          if (node instanceof Konva.Group) {
            const personPhoto = node.findOne(".orgchart-person-photo");
            const personPhotoRect = personPhoto?.getClientRect();
            const isInPersonPhoto = personPhotoRect && MathUtils.isPointInRect(point, personPhotoRect);
            if (isInPersonPhoto) return false;

            const background = node.findOne(".orgchart-node-background");
            const backgroundRect = background?.getClientRect();
            const isInBackground = backgroundRect && MathUtils.isPointInRect(point, backgroundRect);
            return !!isInBackground;
          }
          return false;
        }

        case consts.CANVAS_ELEMENTS.CARD_STACK:
        case consts.CANVAS_ELEMENTS.LIVE_INTEGRATION: {
          const rect = node.getClientRect();
          return MathUtils.isPointInRect(point, rect);
        }

        case consts.CANVAS_ELEMENTS.TIMELINE: {
          const rect = node.getClientRect();
          const scale = node.scaleX();
          const stageScale = stageRef.current?.getStage().scaleX() ?? 1;
          const height = 120 * scale * stageScale;
          const bottomPart = { x: rect.x, y: rect.y + rect.height - height, width: rect.width, height };
          return MathUtils.isPointInRect(point, bottomPart);
        }

        case consts.CANVAS_ELEMENTS.TABLE: {
          if (node.attrs.isTableTitle) {
            return MathUtils.isPointInRect(point, node.getClientRect());
          }
          const p2 = canvasPointToNormalizedPoint(pointInStage, type, node.attrs.element);
          return p2.x >= 0 && p2.x <= 1 && p2.y >= 0 && p2.y <= 1;
        }

        case consts.CANVAS_ELEMENTS.TABLE_CELL: {
          return false; // not selectable by the stage; table manages its internal selection
        }
        case consts.CANVAS_ELEMENTS.NOUN_ICON: {
          return true;
        }

        // from here on all elements should be implemented as providers so this is just default
        case consts.CANVAS_ELEMENTS.GANTT:
        case consts.CANVAS_ELEMENTS.ICON:
        case consts.CANVAS_ELEMENTS.CHART:
        case consts.CANVAS_ELEMENTS.TASK:
        case consts.CANVAS_ELEMENTS.MONDAY_TASK: {
          return true;
        }

        default: {
          // We should never get here!
          // Will throw build error if all cases were not covered
          const __unhandled: never = type;
          break;
        }
      }
    };
  }

  // Elements calculations
  function getCanvasElementsInRect(rect: IRect): Array<Konva.Node> {
    const inRect = bboxIntersection(rect);
    return getSelectableNodes(inRect).filter(refineRectSelection(rect));
  }
  /**
   * Returns all elements that are in the point
   * @param point Viewport coordinates
   */
  function getCanvasElementsInPoint(point: MathUtils.PointInViewport): Array<Konva.Node> {
    const N = DistanceToSelectLines_pixels * 0.5 * posScale.scale;
    const inRect = bboxIntersection({ x: point.x - N, y: point.y - N, width: N * 2, height: N * 2 });
    return getSelectableNodes(inRect).filter(refinePointSelection(point));
  }
  /**
   * Returns all elements that are in the point
   * @param point Absolute coordinates
   */
  function getCanvasElementsInAbsolutePoint(point: MathUtils.PointInCanvas): Array<Konva.Node> {
    const rect = { x: point.x, y: point.y, width: 1, height: 1 };
    const inRect = (i: Konva.Node) =>
      Konva.Util.haveIntersection(rect, i.getClientRect({ relativeTo: layerRef.current as any }));
    return getSelectableNodes(inRect);
  }

  //We accommodate for 2 types of main flows here:
  //1) This is being called after a mutation to a canvas element (and possibly its attached connectors)
  //2) This is being called after a mutation to an attached connector (and possibly its connected elements)
  //Examples: 1) We are moving a shape, if the shape has attached connectors, they move with it (and should be able to undo/redo to same positions)
  //2) We are attaching a connector, the element now has 1 attached connector and the connector has one connected shape (both need to undo/redo)
  function onMutationEnd() {
    recordMutationForUndo(transformerRef.current?.nodes() ?? []);
    transformerRef.current?.forceUpdate();
  }

  function recordMutationForUndo(nodes: Konva.Node[]) {
    const mutationInfo = new Map<string, ElementDrawProps>();
    const undoMutationInfo = new Map<string, ElementDrawProps>();
    for (const node of nodes) {
      if (node.name().startsWith(transformerAnchorId())) {
        const id = node.attrs.masterId;
        mutationInfo.set(id, { x: node.x(), y: node.y(), scaleX: node.scaleX(), scaleY: node.scaleY() });
        undoMutationInfo.set(id, node.attrs.undoProperties);
      } else {
        const id = node.attrs.id;
        mutationInfo.set(id, drawPropsForNode(node));
        undoMutationInfo.set(id, drawUndoPropsForNode(node));
      }
    }

    mutateWithWorkaroundForTexts(mutationInfo, undoMutationInfo);
    mutationAffectedNodes.current = {};
  }

  // Pointer dragging
  function getSelectionRect() {
    if (stageState?.ghostElement?.kind == "SelectionRect") {
      return selectionRef.current?.getClientRect() ?? null;
    }
    return null;
  }

  function selectElementAtPoint(pointer: Point) {
    const nodes = getCanvasElementsInPoint(pointer as MathUtils.PointInViewport).filter(
      (node) => node.attrs.isSelectable
    );
    if (nodes.length == 0) return null;

    let frames = [],
      cardStacks = [],
      opaqueNodes = [],
      timelines = [],
      transparentNodes = [];
    for (const node of nodes) {
      if (node.attrs.isFrame && !node.attrs.isFrameTitle) {
        frames.push(node);
      } else if (node.attrs.type == consts.CANVAS_ELEMENTS.CARD_STACK) {
        cardStacks.push(node);
      } else if (node.attrs.type == consts.CANVAS_ELEMENTS.TIMELINE) {
        timelines.push(node);
      } else {
        const alpha = node.attrs.element.fill ? getOpacity(node.attrs.element.fill) : 1;
        // we distinguish between opaque and semi-opaque to fully transparent nodes
        if (alpha <= 0.0001) {
          transparentNodes.push(node);
        } else {
          opaqueNodes.push(node);
        }
      }
    }

    function getSize(node: any) {
      const measure = measureNode(node);
      return measure.width * measure.height;
    }

    const frontMostNode = (a: Node, b: Node) => {
      if (a.attrs.element.containerId === b.attrs.id) {
        return a;
      }
      if (b.attrs.element.containerId === a.attrs.id) {
        return b;
      }
      return a.attrs.element.zIndexLastChangeTime < b.attrs.element.zIndexLastChangeTime ? b : a;
    };

    if (opaqueNodes.length == 0 && transparentNodes.length == 0) {
      console.assert(cardStacks.length + frames.length + timelines.length > 0, "unknown nodes found");
      if (cardStacks.length) {
        return reduce(frontMostNode, cardStacks[0], cardStacks);
      } else if (frames.length) {
        // should find the smallest frame surrounding the point,
        // or maybe the one with largest area around the point...
        return reduce(frontMostNode, frames[0], frames);
      } else if (timelines.length) {
        return reduce(frontMostNode, timelines[0], timelines);
      } else {
        return null;
      }
    }

    // Precendence rules for opaque elements are simple: higher z-index wins
    // some old templates have identical z-index for some elements, and then
    // we go by last-modified-timestamp
    const i = opaqueNodes.reduce((prev, cur, curIndex, arr) => {
      const prevEl = arr[prev].attrs.element;
      const curEl = cur.attrs.element;
      if (curEl.containerId === arr[prev].attrs.id) return curIndex;
      if (prevEl.containerId === cur.attrs.id) return prev;
      const prevZ = prevEl.zIndexLastChangeTime ?? Number.MIN_SAFE_INTEGER;
      const curZ = curEl.zIndexLastChangeTime ?? Number.MIN_SAFE_INTEGER;
      if (prevZ != curZ) return curZ > prevZ ? curIndex : prev;
      return curEl.lastModifiedTimestamp > prevEl.lastModifiedTimestamp ? curIndex : prev;
    }, 0);

    const topmost = opaqueNodes[i];

    if (topmost && transparentNodes.length == 0) {
      return topmost;
    }

    // If we have transparent nodes things can get more complicated
    // Precedence rules for transparent nodes:
    // smaller is picked before larger, and if they have the same size, z-index wins
    transparentNodes.sort((a, b) => {
      const sizeA = getSize(a);
      const sizeB = getSize(b);
      if (Math.abs(sizeA - sizeB) > 1e-5) return sizeA - sizeB;
      const z1 = (a.attrs.element.zIndexLastChangeTime ??= a.attrs.element.lastModifiedTimestamp);
      const z2 = (b.attrs.element.zIndexLastChangeTime ??= b.attrs.element.lastModifiedTimestamp);
      return z2 - z1;
    });

    if (!topmost && transparentNodes.length > 0) {
      return transparentNodes[0];
    }

    const topMostZ = topmost!.attrs.element.zIndexLastChangeTime ?? Number.MIN_SAFE_INTEGER;
    // in case of opaque and transparent, we might select a transparent node
    // that's above an opaque one
    transparentNodes = transparentNodes.filter((node) => {
      const zIndex = node.attrs.element.zIndexLastChangeTime ?? Number.MIN_SAFE_INTEGER;
      if (zIndex != topMostZ) {
        return zIndex > topMostZ;
      } else {
        const lastModified = node.attrs.element.lastModifiedTimestamp;
        const topLastModified = topmost!.attrs.element.lastModifiedTimestamp;
        return lastModified > topLastModified;
      }
    });

    const candidate = transparentNodes[minIndex(transparentNodes, getSize)];
    if (candidate && getSize(candidate) < getSize(topmost)) return candidate;
    else return topmost;
  }

  // Used in pointerDragEnd.
  // TODO: Can this be moved out of canvas stage?
  function arraysEqual<T>(a: T[], b: T[]) {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (a.length != b.length) return false;

    for (let i = 0; i < a.length; ++i) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }

  function handlePointerDragEnd(e: KonvaEventObject<MouseEvent> | boolean) {
    const selectionRect = getSelectionRect();
    const { clickPositionScreen } = mouseState.current;
    const mouseDelta = clickPositionScreen
      ? PointUtils.manhattanDistane(stageRef.current?.getPointerPosition() ?? { x: 0, y: 0 }, clickPositionScreen)
      : 0;
    const draggedMouse = mouseDelta > consts.MOUSE_MOVE_THRESHOLD;

    let isShift = false;
    let target = null;
    if (typeof e === "boolean") {
      isShift = e;
    } else if (e) {
      isShift = e.evt.shiftKey;
      target = e.target;
    }

    let editElementId: string | null = null;
    let newSelectionIds: string[] | null = null;
    let expandSelection = true;

    if (target) {
      const name = target.name();
      if (name.includes("anchor")) {
        return;
      }

      if (name.includes("annotation")) {
        return;
      }
      if (name == "edit-trigger") {
        setEditingElementId(target.attrs.editId ?? null);
        return;
      }

      if (target.getLayer()?.name() == "Editing") {
        return;
      }
    }

    // On the following cases we don't change the selection at all:
    // - shift+click on empty space
    // - drag a shape
    // - drag a transformer handle
    let node = !draggedMouse ? selectElementAtPoint(stageRef.current?.getPointerPosition() ?? { x: 0, y: 0 }) : null;

    // This fixes old images and other nodes which are not detected by selectElementAtPoint
    if (
      (node == null && typeof e !== "boolean" && e.target) ||
      (typeof e !== "boolean" && e.target && e.target.name().includes("connector-line")) // If clicked exactly on a certain connector, select it.
    ) {
      let t = e.target;
      while (t && !t.attrs.id && !t.attrs.isSelectable) t = t.getParent();
      if (t && t.attrs.isSelectable) node = t;
    }

    if (isShift && !selectionRect && node && node instanceof Konva.Stage) {
      setEditingElementId(null);
      return;
    }

    if (!draggedMouse) {
      if (node) {
        if (isShift) {
          // shift clicks on table are handled by the table itself
          if (
            isTable(node.attrs.id) &&
            selectedElementIds.length == 1 &&
            selectedElementIds[0].includes(node.attrs.id)
          ) {
            return;
          }

          // shift-click is adding/removing from selection.
          const grpId = getGroupForElement(node.id());
          const addToSelection = !selectedElementIds.includes(node.attrs.id);
          // if we're adding a new table we reset the cell-selection of any existing tables
          if (isTable(node.attrs.id) && selectedElementIds.some(isTable)) {
            setTableSelection(null);
          }
          if (addToSelection) {
            if (grpId) {
              newSelectionIds = selectedElementIds.concat(groups[grpId]);
            } else {
              newSelectionIds = selectedElementIds.concat([node.id()]);
            }
          } else {
            const toRemove = grpId ? new Set(groups[grpId]) : new Set([node.id()]);
            newSelectionIds = selectedElementIds.filter((id) => !toRemove.has(id));
          }
        } else {
          // here we either:
          // - start editing it (if it was the only one preselected)
          // - if its group is selected, focus on it (even if it's locked)
          // - if it's group and others selected, focus on it, and it's group alone
          // - select it for any other case (with its group, even if it's locked)
          if (selectedElementIds.length == 1 && selectedElementIds[0] === node.attrs.id) {
            const type = getElementTypeForId(selectedElementIds[0]);
            if (
              type == consts.CANVAS_ELEMENTS.TEXT_BLOCK ||
              type == consts.CANVAS_ELEMENTS.STICKY_NOTE ||
              type == consts.CANVAS_ELEMENTS.SHAPE ||
              type == consts.CANVAS_ELEMENTS.MINDMAP ||
              type == consts.CANVAS_ELEMENTS.MINDMAP_ORG_CHART ||
              type == consts.CANVAS_ELEMENTS.CONNECTOR ||
              type == consts.CANVAS_ELEMENTS.TASK_CARD ||
              type == consts.CANVAS_ELEMENTS.FRAME ||
              type == consts.CANVAS_ELEMENTS.CARD_STACK ||
              type == consts.CANVAS_ELEMENTS.LIVE_INTEGRATION ||
              type == consts.CANVAS_ELEMENTS.ICON ||
              (type == consts.CANVAS_ELEMENTS.TABLE && node.attrs.isTableTitle)
            ) {
              const locked = (node.attrs.element.lock ?? 0) != LockType.None;
              if (!locked) {
                editElementId = selectedElementIds[0];
              }
            }
          } else if (selectedElementIds.length > 1 && selectedElementIds.includes(node.attrs.id)) {
            const grpId = getGroupForElement(node.attrs.id);
            if (!grpId || arraysEqual(groups[grpId], selectedElementIds)) {
              newSelectionIds = [node.attrs.id];
              expandSelection = false;
            } else {
              newSelectionIds = [node.attrs.id];
              expandSelection = true;
            }
          } else {
            newSelectionIds = [node.attrs.id];
            expandSelection = true;
          }
        }
      } else if (!isShift) {
        newSelectionIds = [];
        selectedIdsRef.current = [];
        disableElementBounds();
        disableGroupsBounds();
      }
    } else if (ghostElementRef.current?.kind == "SelectionRect" && selectionRect != null) {
      // define a function we'll need to filter out frames
      const rectContainedInSelection = (rect: IRect) => {
        return (
          rect.x >= selectionRect.x &&
          rect.x + rect.width <= selectionRect.x + selectionRect.width &&
          rect.y >= selectionRect.y &&
          rect.y + rect.height <= selectionRect.y + selectionRect.height
        );
      };

      // get the elements, and remove locked elements (which are only selectable by clicks)
      let nodes = getCanvasElementsInRect(selectionRect);
      // When selecting elements inside a frame, make sure the frame itself won't be selected unless it's fully contained in the selection rect
      nodes = nodes.filter((x) => {
        if (x.attrs.type == consts.CANVAS_ELEMENTS.TIMELINE) {
          return rectContainedInSelection(x.getClientRect());
        }
        if (x.attrs.isTableTitle) {
          return false;
        }
        if (x.attrs.isSelectable) {
          return true;
        }

        return false;
      });
      const newSelection = addGroupsToSet(nodes);

      if (isShift) {
        const selectedNodes = getNodesByIds(selectedElementIds);
        for (const node of selectedNodes) {
          newSelection.add(node);
        }
      }
      let newSelectionArray = newSelection ? [...newSelection] : [];
      newSelectionIds = newSelectionArray.map((x) => x.id());

      newSelectionArray = newSelectionArray.filter((x) => {
        if (x.attrs.element?.lock) {
          return false;
        }
        return true;
      });
      const boundingBoxes: IRect[] = [];
      newSelectionArray.forEach((node) => {
        if (
          !node.attrs?.element ||
          node.attrs?.element?.containerId?.startsWith("gantt") ||
          node.attrs?.element?.containerId?.startsWith("timeline")
        ) {
          if (newSelectionIds) {
            newSelectionIds = newSelectionIds.filter((id) => id !== node.id());
          }
          return;
        }
        const bbox = getBoundingBoxWithFallback(node);
        if (!bbox) return;
        boundingBoxes.push(bbox);
      });
      const expandedBoundingBox = BoundingBox.expandOnAll(boundingBoxes.filter((box): box is IRect => box !== null));
      enableSelectionBounds(expandedBoundingBox);
      disableTransformer();
    }
    if (newSelectionIds !== null) {
      const ids = new Set(newSelectionIds);
      // Added false on the call to setSelectedIds to avoid updating the bounds. This is temporary fix to make orgchart transformer to work.
      setSelectedIds([...ids], expandSelection, false);
    }
    if (editElementId == editingElementId) {
      if (stageRef.current?.container()?.style.cursor != "text" && editingElementId !== null) {
        setEditingElementId(null);
      }
    } else {
      setEditingElementId(editElementId);
    }
  }

  function moveStagePosition(dx: number, dy: number) {
    const dragDistanceScale = 0.75;
    const x = posScale.x - dragDistanceScale * dx;
    const y = posScale.y - dragDistanceScale * dy;

    // Use pan performance optimization only if top element doesn't have a link
    // (link badges scale inversely, making ghost transformer inaccurate)
    if (zoomAndPanPerformance && !topElementHasLinkRef.current) {
      hideUIElementsDuringPosScaleChange();
      throttledOnChangePosScale({ x, y, scale: posScale.scale });
    } else {
      onChangePosScale({ x, y, scale: posScale.scale });
    }
  }

  function zoomStage(stage: any, dy: number, mode: "mouse" | "viewport-center") {
    const oldScale = stage.scaleX();
    const newScale = MathUtils.clamp(oldScale - dy * 0.01, consts.STAGE_ZOOM.MIN, consts.STAGE_ZOOM.MAX);
    // This should be canvas element center, but since we stretch the canvas over the window
    // I just take window center
    const point =
      mode == "mouse"
        ? stage.getPointerPosition()
        : {
            x: 0.5 * window.innerWidth,
            y: 0.5 * window.innerHeight,
          };
    const centerX = (point.x - posScale.x) / posScale.scale;
    const centerY = (point.y - posScale.y) / posScale.scale;
    const offsetX = centerX * (newScale - posScale.scale);
    const offsetY = centerY * (newScale - posScale.scale);
    const newPosition = {
      x: posScale.x - offsetX,
      y: posScale.y - offsetY,
    };
    // Use zoom performance optimization only if top element doesn't have a link
    // (link badges scale inversely, making ghost transformer inaccurate)
    if (zoomAndPanPerformance && !topElementHasLinkRef.current) {
      hideUIElementsDuringPosScaleChange();
      throttledOnChangePosScale({ x: newPosition.x, y: newPosition.y, scale: newScale });
    } else {
      onChangePosScale({ x: newPosition.x, y: newPosition.y, scale: newScale });
    }
  }

  // Render
  function renderGhostElement(element: GhostElement | undefined, drawConnectors: boolean): any {
    let point1, point2;
    if (element == null) return;
    // If you want to debug shape drawing, add this snippet before GhostCanvasElement:
    // <Rect
    //   x={point1.x}
    //   y={point1.y}
    //   width={point2.x - point1.x}
    //   height={point2.y - point1.y}
    //   fillEnabled={false}
    //   stroke={"blue"}
    //   strokeWidth={1}
    // />
    switch (element?.kind) {
      case "Shape": {
        [point1, point2] = MathUtils.computeBoundingBox(
          element.point1,
          element.point2,
          element.isCentered,
          element.isEquiSided
        );
        return (
          <GhostCanvasElement
            elementType={consts.CANVAS_ELEMENTS.SHAPE}
            point1={point1}
            point2={point2}
            data={element.data ?? newElementData}
          />
        );
      }
      case "Note": {
        [point1, point2] = MathUtils.computeBoundingBox(element.point1, element.point2, false, false);
        return (
          <GhostCanvasElement
            elementType={consts.CANVAS_ELEMENTS.STICKY_NOTE}
            point1={point1}
            point2={point2}
            data={element.data ?? newElementData}
          />
        );
      }
      case "Connector": {
        if (!drawConnectors) {
          return null;
        }

        const { color, opacity } = parseColor(
          newElementData.connector.stroke ?? (isCIM ? "#A6AAAD" : consts.COLOR_PALETTE[0])
        );
        const newOpacity = (opacity / 255) * 0.33 * 255;
        const newGhostColor = combineColorOpacity(color, newOpacity);

        return (
          <GhostCanvasElement
            elementType={consts.CANVAS_ELEMENTS.CONNECTOR}
            point1={element.point1 ?? element.start.pos}
            point2={element.point2 ?? element.end.pos}
            data={{
              connector: {
                ...newElementData.connector,
                start: element.start,
                end: element.end,
                stroke: newGhostColor,
              },
            }}
          />
        );
      }
      case "Frame": {
        return (
          <GhostCanvasElement
            elementType={consts.CANVAS_ELEMENTS.FRAME}
            point1={element.point1}
            point2={element.point2}
            data={newElementData}
          />
        );
      }
      case "Template":
      case "MindMapOrgChart": {
        if (!element.template) {
          return null;
        }
        if (element.template.thumbnailImageURL) {
          return (
            <AsyncImage
              x={element.position.x}
              y={element.position.y}
              size={500}
              src={element.template.thumbnailImageURL}
            />
          );
        }
        return null;
      }

      case "TaskCard": {
        return (
          <GhostCanvasElement
            elementType={consts.CANVAS_ELEMENTS.TASK_CARD}
            point1={element.point1}
            point2={element.point1}
          />
        );
      }
      case "Task": {
        return (
          <GhostCanvasElement
            elementType={consts.CANVAS_ELEMENTS.TASK}
            point1={element.point1}
            point2={element.point1}
            data={element.data}
          />
        );
      }
      case "CardStack": {
        return (
          <GhostCanvasElement
            elementType={consts.CANVAS_ELEMENTS.CARD_STACK}
            point1={element.point1}
            point2={element.point1}
          />
        );
      }
      case "LiveIntegration": {
        return (
          <GhostCanvasElement
            elementType={consts.CANVAS_ELEMENTS.LIVE_INTEGRATION}
            point1={element.point1}
            point2={element.point1}
          />
        );
      }
      case "MindMap": {
        return (
          <GhostCanvasElement
            elementType={consts.CANVAS_ELEMENTS.MINDMAP}
            point1={element.point}
            point2={element.point}
          />
        );
      }
      case "Timeline": {
        return (
          <GhostCanvasElement
            elementType={consts.CANVAS_ELEMENTS.TIMELINE}
            point1={element.point}
            point2={{ x: element.point.x + 100, y: element.point.y }}
          />
        );
      }
      case "Gantt": {
        return (
          <GhostCanvasElement
            elementType={consts.CANVAS_ELEMENTS.GANTT}
            point1={element.point}
            point2={element.point}
          />
        );
      }
      case "Icon": {
        const { text, href, width = sizes.icon.size, height = sizes.icon.size, scaleX = 1, scaleY = 1 } = element.data;
        const center = {
          x: element.point.x - width / 2,
          y: element.point.y - height / 2,
        };

        return (
          <GhostCanvasElement
            elementType={consts.CANVAS_ELEMENTS.ICON}
            point1={center}
            point2={center}
            data={{
              icon: {
                ...newElementData.icon,
                text,
                href,
                width,
                height,
                scaleY,
                scaleX,
              },
            }}
          />
        );
      }
      case "Table": {
        let { point1, point2 } = element;
        if (distance(point1, point2) < 10) {
          point2 = point1;
        } else {
          [point1, point2] = MathUtils.computeBoundingBox(
            element.point1,
            element.point2,
            element.isCentered,
            element.isEquiSided,
            { x: sizes.table.cellWidth / 2, y: sizes.table.cellHeight / 2 }
          );
        }
        return (
          <GhostCanvasElement
            elementType={consts.CANVAS_ELEMENTS.TABLE}
            point1={point1}
            point2={point2}
            data={newElementData}
          />
        );
      }
      case "IconOrImage": {
        [point1, point2] = MathUtils.computeBoundingBox(element.point1, element.point2, false, true);
        return (
          newElementData.nounIcon && (
            <GhostCanvasElement
              elementType={consts.CANVAS_ELEMENTS.NOUN_ICON}
              point1={point1}
              point2={point2}
              data={newElementData}
            />
          )
        );
      }
      case "KonvaNode": {
        return <KonvaNodeElement {...element} />;
      }
      case "MultipleGhosts": {
        return element.ghosts.map((ghost) => renderGhostElement(ghost, drawConnectors));
      }
      case "Chart": {
        return (
          <GhostCanvasElement
            elementType={consts.CANVAS_ELEMENTS.CHART}
            point1={element.point}
            point2={element.point}
            data={{ chartType: element.chartType }}
          />
        );
      }
      default: {
        return null;
      }
    }
  }
  //callbacks used as props to memoed components
  const onMutationEndCb = useCallback(onMutationEnd, [transformerRef, mutationAffectedNodes, layerRef]);

  const onTransformerAnchorDoubleClick: AnchorDblClickCb = (e) => {
    const allText = e.nodes.every((node: Konva.Node) => node.attrs.type == consts.CANVAS_ELEMENTS.TEXT_BLOCK);

    function fitContent(node: Konva.Node) {
      const element = node.attrs.element;
      const isDerived = !!getDerivedGroupId(node);

      let text = element.text ?? consts.DEFAULTS.TEXT;
      if (isDerived) {
        const innerTextNode = node.children.toArray().find((child) => !!child.attrs.text);
        if (innerTextNode) {
          text = innerTextNode.attrs.text;
        } else {
          text = "";
        }
      }

      const config = TextUtils.calcKonvaTextConfig({ element: { ...element, text }, scaleFont: element.scaleY ?? 1 });
      const { longestLine, numLines } = TextUtils.computeFitWidth(text, config);
      const newWidth = longestLine + 1; //computeFitWidth is too tight sometimes, so add 1
      const currentWidth = element.width * (element.scaleX ?? 1);
      let dx = 0;
      if (element.align == "right") {
        dx = currentWidth - newWidth;
      } else if (element.align == "center") {
        dx = (currentWidth - newWidth) / 2;
      }
      const totalHeight = config.lineHeight * config.fontSize * numLines;
      return { node, scaleX: 1, width: newWidth, height: totalHeight / node.scaleY(), x: element.x + dx } as const;
    }

    if (allText) {
      const nodesWithTextConfig = e.nodes.map(fitContent);
      const currentProps = new Map<string, any>(),
        newProps = new Map<string, any>();
      for (const item of nodesWithTextConfig) {
        const id = item.node.id();
        const element = item.node.attrs.element;
        currentProps.set(id, {
          scaleX: element.scaleX ?? 1,
          width: element.width,
          height: element.height,
          x: element.x,
        });
        newProps.set(id, {
          scaleX: item.scaleX,
          width: item.width,
          height: item.height,
          x: item.x,
        });
      }
      mutateWithWorkaroundForTexts(newProps, currentProps);
    }
  };

  type CanvasElementFromReflect = {
    id: string;
    type: TypeCanvasElement;
    value: any;
  };
  function renderElements() {
    let filteredElements = elements as CanvasElementFromReflect[];
    if (isPreviewExportMode) {
      filteredElements = filteredElements.filter(
        (element) => !elementsInThumbnail.includes(`${element.type}-${element.id}`)
      );
    }

    const context: BoardContext = {
      boardId: board!.id,
      documentId: board!.documentId,
      reflect: syncService?.getReplicache() ?? null,
      isReadOnly,
      isLoggedIn: !(!user || user.isAnonymous),
      selectedElementIds,
      setSelectedIds,
      setStageState: setStageState as React.Dispatch<React.SetStateAction<Partial<CanvasState>>>,
      hiddenElementIds,
      editingElementId,
      editingElementLinkId,
      undoRedoStack,
      showUpgradeModal,
      pusherChannel,
      showErrorToast: onError,
      allFeatureFlags: allFeatureFlags,
      dataSource: dataSource,
    };
    return (
      <>
        {filteredElements.map((element) => {
          const elementId = `${element.type}-${element.id}`;
          const provider = getElementProvider(element.type);
          if (provider) {
            if (provider.isFFEnabled()) {
              // if provider exists it means it support the new infra
              return (
                <ElementComponent
                  key={elementId}
                  id={elementId}
                  type={element.type}
                  elementData={element.value}
                  context={context}
                  onResize={onResizeElement}
                />
              );
            }
            return null;
          }
          const isSelected = selectedIdsRef.current.includes(elementId);
          const isSelectable = !hiddenElementIds.includes(elementId);
          return (
            <CanvasElement
              key={elementId}
              syncService={syncService}
              uniqueId={elementId}
              elementData={element.value}
              documentId={board?.documentId}
              onResize={onResizeElement}
              onChangeElement={onChangeElement}
              patchCanvasElement={patchCanvasElement}
              patchAnything={patchAnything}
              isSelected={isSelected}
              isSelectable={isSelectable}
              drawOutlineAroundElements={multipleElementsSelected && isSelected}
              isEditing={editingElementId === elementId}
              isEditingLink={editingElementLinkId === elementId}
              isFrameHighlighted={false} // TODO: DELETE ALL LOGIC RELATED TO THIS
              onElementsMutationEnded={onElementsMutationEnded}
              isThumbnail={thumbnailId === elementId}
              isReadOnly={isReadOnly}
              ghostElementRef={ghostElementRef}
              needToRefreshBoundsRef={needToRefreshBoundsRef}
              selectedIdsRef={selectedIdsRef}
            />
          );
        })}
      </>
    );
  }

  function renderFramesBackground() {
    return frameIds.map((id: string) => <FrameBackgroundElement key={id} syncService={syncService} uniqueId={id} />);
  }

  function addCordCommentIfNeeded(thread: ActiveCommentThread, messageCount?: number) {
    if (!thread || !messageCount || thread.kind == "Existing") return;
    if (!(thread.newElementData.element as any).attachedTo) {
      const x = thread.newElementData.element!.x * posScale.scale + posScale.x;
      const y = thread.newElementData.element!.y * posScale.scale + posScale.y;
      const nodeUnderComment = selectElementAtPoint({ x, y } as MathUtils.PointInViewport);

      if (nodeUnderComment) {
        const elementData = thread.newElementData.element;
        const attachedToId = nodeUnderComment.attrs.id;

        const normalized = canvasPointToNormalizedPoint(
          elementData,
          getElementTypeForId(nodeUnderComment.attrs.id),
          nodeUnderComment.attrs.element
        );
        (thread.newElementData.element as any).attachedTo = attachedToId;
        (thread.newElementData.element as any).attachedLocation = normalized;
      }
    }
    onAddElements([thread.newElementData], true, "cord");
  }

  function renderCordComments(ids: string[], active: ActiveCommentThread) {
    if (!areCommentsVisableOnCanvas) {
      return null;
    }
    const allowInteraction = cursorType === CursorType.pointer;
    const visitingFromAnotherAccount = board?.accountId != appState.account?.id;
    const openCommentId = active?.kind == "Existing" ? active.elementId : undefined;
    return (
      <>
        {ids.map((id) => (
          <OverlayElement
            key={id}
            syncService={syncService}
            uniqueId={id}
            listening={allowInteraction}
            onDragStarted={(node) => {
              // detach from the attachTo element
              // so a remote user moving the attachedTo shape won't move the comment
              const props = new Map();
              // eslint-disable-next-line react/prop-types
              props.set("attachedTo", null);
              onChangeElement(id, props, props, false);
              mutationAffectedNodes.current = { [node.attrs.id]: node };
            }}
            onDrag={(node, forcePosition) => {
              if (forcePosition) {
                onDragElement(id, forcePosition);
              } else {
                setHighlightRect(null);
                const { canAttach, nodeUnderMouse: attachableNode } = calculateCommentAttachment(
                  node,
                  node.getAbsolutePosition()
                );

                if (canAttach && attachableNode) {
                  setHighlightRect(measureNode(attachableNode));
                }
                onDragElement(id, node.getPosition());
              }
            }}
            onDragEnded={(node) => {
              const mutationInfo: any = {
                x: node.x(),
                y: node.y(),
              };

              const { canAttach, nodeUnderMouse, attachedLocation } = calculateCommentAttachment(
                node,
                node.getAbsolutePosition()
              );

              if (canAttach && nodeUnderMouse && attachedLocation) {
                mutationInfo.attachedTo = nodeUnderMouse.attrs.id;
                mutationInfo.attachedLocation = attachedLocation;
              }

              const mutationInfoMap = new Map();
              mutationInfoMap.set(node.attrs.id, mutationInfo);

              const undoMutationInfo = new Map();
              undoMutationInfo.set(node.attrs.id, {
                ...node.attrs.undoProperties,
                attachedTo: node.attrs.element.attachedTo,
                attachedLocation: node.attrs.element.attachedLocation,
              });
              onElementsMutationEnded(mutationInfoMap, undoMutationInfo);
              setHighlightRect(null);
              mutationAffectedNodes.current = {};
            }}
            onClick={(e: KonvaEventObject<MouseEvent>) => {
              if (cursorType == CursorType.pointer) {
                if (visitingFromAnotherAccount) {
                  onError && onError(NoCordMsg);
                  return;
                }
                const group = e.currentTarget;
                setActiveCommentThread({
                  kind: "Existing",
                  storeId: group.attrs.element.storeId,
                  elementId: group.attrs.id,
                });
                setFocusedElementId(group.attrs.id);
                tracking.trackEvent(
                  consts.TRACKING_CATEGORY.CANVAS_ACTION,
                  "cord_comment_clicked",
                  board?.documentId,
                  board?.accountId,
                  user?.id
                );
              }
            }}
            stageScale={posScale.scale}
            renderElementComponent={(id, element) => {
              return (
                <>
                  <AnnotationMarker photoURL={(element as any)?.userPhotoUrl} />
                  {openCommentId == id && (
                    <CordThreadView
                      x={element!.x * posScale.scale + posScale.x}
                      y={element!.y * posScale.scale + posScale.y}
                      storeId={active!.storeId}
                      location={{ documentId: board!.documentId }}
                      onClose={(numMsgs?: number) => {
                        if (typeof numMsgs != "undefined") {
                          if (numMsgs == 0) {
                            onDeleteElements([openCommentId!]);
                          }
                        }
                        setActiveCommentThread(null);
                        setFocusedElementId(RESET);
                      }}
                    />
                  )}
                </>
              );
            }}
          />
        ))}
        {active?.kind == "New" && (
          <>
            <AnnotationMarker position={active.newElementData.element} scale={1 / posScale.scale} />
            <CordThreadView
              x={active.newElementData.element!.x * posScale.scale + posScale.x}
              y={active.newElementData.element!.y * posScale.scale + posScale.y}
              storeId={active.storeId}
              location={{ documentId: board!.documentId }}
              onMessageCountChange={(messageCount) => {
                addCordCommentIfNeeded(active, messageCount);
              }}
              onClose={() => {
                setActiveCommentThread(null);
                setFocusedElementId(RESET);
              }}
            />
          </>
        )}
      </>
    );
  }

  function stageStyle(): CSSProperties {
    const backgroundColor = isCIM ? "#F8F9FC" : "white";
    const baseSize = 25;
    const scale = posScale.scale;
    const position = posScale;
    if (scale >= 1) {
      const size = baseSize * scale;
      const x = (position.x % size) + size / 2;
      const y = (position.y % size) + size / 2;
      const dotSize = 1.1 * scale;
      return {
        backgroundColor,
        backgroundImage: `radial-gradient(#E5E8EA ${dotSize}px, transparent ${dotSize}px)`,
        backgroundSize: `${size}px ${size}px`,
        backgroundPosition: `${x}px ${y}px`,
      };
    } else if (scale >= 0.5) {
      const size = baseSize * scale * 2;
      const x = (position.x % size) + size / 2;
      const y = (position.y % size) + size / 2;
      return {
        backgroundColor,
        backgroundImage: "radial-gradient(#E5E8EA 1.1px, transparent 1.1px)",
        backgroundSize: `${size}px ${size}px`,
        backgroundPosition: `${x}px ${y}px`,
      };
    } else if (scale >= 0.25) {
      const size = baseSize * scale * 4;
      const x = (position.x % size) + size / 2;
      const y = (position.y % size) + size / 2;
      return {
        backgroundColor,
        backgroundImage: "radial-gradient(#E5E8EA 1.1px, transparent 1.1px)",
        backgroundSize: `${size}px ${size}px`,
        backgroundPosition: `${x}px ${y}px`,
      };
    } else if (scale >= 0.1) {
      const size = baseSize * scale * 8;
      const x = (position.x % size) + size / 2;
      const y = (position.y % size) + size / 2;
      return {
        backgroundColor,
        backgroundImage: "radial-gradient(#E5E8EA 1.1px, transparent 1.1px)",
        backgroundSize: `${size}px ${size}px`,
        backgroundPosition: `${x}px ${y}px`,
      };
    } else {
      return {
        backgroundColor,
      };
    }
  }

  const didDropMondayElement = useCallback(
    (itemData: any, position: MathUtils.PointInViewport) => {
      // monday limitation
      const dummyId = `${consts.CANVAS_ELEMENTS.INTEGRATION}-dummy-id`;
      if (!canAddElementsWithMondayLimitation([dummyId])) {
        showUpgradeModal && showUpgradeModal("monday-integration-limit");
        return;
      }

      const node = selectElementAtPoint(position);

      if (isDataLayerEnabled && node) {
        const isParent = consts.ELEMENTS_SUPPORTING_DATA_LAYER.includes(node.attrs.type);
        if (isParent) {
          handleDropMondayItemOnElement(node, itemData);
          return;
        }

        // Support dropping on a derived text within a parent element
        const derivedGroupId = getDerivedGroupId(node);
        if (derivedGroupId) {
          const parentId = node.attrs.element.derivedElementIds?.[0] ?? "";
          const parentNode = getNodeById(parentId);
          if (parentNode) {
            handleDropMondayItemOnElement(parentNode, itemData);
            return;
          }
        }
      }

      // Handle as if dropped on empty space (default case)
      handleDropMondayItemOnCanvas(itemData, position);
    },
    [canAddElementsWithMondayLimitation, isDataLayerEnabled]
  );
  useSetDidDropMondayElement(didDropMondayElement);

  const [, drop] = useDrop(
    () => ({
      accept: ["monday-item", "import-monday-item"],
      drop: (item, monitor) => {
        const pos = (monitor.getClientOffset() ?? { x: 0, y: 0 }) as MathUtils.PointInViewport;
        switch (monitor.getItemType()) {
          case "monday-item": {
            setStageState((previous) => ({
              ...previous,
              ghostElement: previous.ghostElement?.kind === "KonvaNode" ? null : previous.ghostElement,
            }));
            didDropMondayElement(item, pos);
            break;
          }
          case "import-monday-item": {
            // @ts-expect-error item is not typed
            if (item.integrationId) {
              // we need this for the position
              const pos = (monitor.getClientOffset() ?? { x: 0, y: 0 }) as MathUtils.PointInViewport;
              didDropMondayElement(item, pos);
            }
            return { position: pos };
          }
          default: {
            break;
          }
        }
      },
      hover: (_, monitor) => {
        const pos = (monitor.getClientOffset() ?? { x: 0, y: 0 }) as MathUtils.PointInViewport;
        const node = selectElementAtPoint(pos);

        let rect: Konva.Rect | null = null;
        if (node && consts.ELEMENTS_SUPPORTING_DATA_LAYER.includes(node.attrs.type)) {
          // Known Konva type incompatibility - Layer should be compatible with Container<Node<NodeConfig>>
          const bbox = node.getClientRect({
            relativeTo: layerRef.current as unknown as Konva.Container,
            skipShadow: true,
          });
          rect = new Konva.Rect({
            x: bbox.x,
            y: bbox.y,
            width: bbox.width,
            height: bbox.height,
            stroke: "#00A1FF",
            strokeWidth: 2,
          });
        }

        setStageState((previous) => ({
          ...previous,
          ghostElement: rect ? { kind: "KonvaNode", nodes: [rect], x: 0, y: 0, opacity: 1 } : null,
        }));
      },
    }),
    [didDropMondayElement]
  );

  function map<T, E>(prop: T, mapper: (x: T) => E): E {
    return mapper(prop);
  }

  function parseDerivedDataId(derivedDataId: string) {
    const regex = /\${(.*?):(.*?)}/g;
    const matches = derivedDataId.matchAll(regex);
    for (const match of matches) {
      // This loop should only run once
      const derivedElementId = match[1];
      const derivedElementPropertyKeyPath = match[2];
      return { id: derivedElementId, keyPath: derivedElementPropertyKeyPath };
    }
    return null;
  }

  function getPosScale() {
    return {
      ...(stageRef.current?.position() ?? { x: 0, y: 0 }),
      scale: stageRef.current?.scaleX() ?? 1,
    };
  }

  function handleDropMondayItemOnCanvas(
    itemData: { item: any; integrationType: IntegrationType; integrationId: string },
    position: Point
  ) {
    const posScale = getPosScale();
    const canvasPos = map(position, ({ x, y }) => ({
      x: (x - posScale.x) / posScale.scale - 200,
      y: (y - posScale.y) / posScale.scale - 70,
    }));

    tracking.trackEvent(
      consts.TRACKING_CATEGORY.INTEGRATIONS,
      "monday-item-dropped",
      itemData.item.type,
      board?.documentId
    );

    const { item, integrationType, integrationId } = itemData;

    let data: NewElementData;
    if (newMondayTaskElementEnabled) {
      const element = placeMondayTask(canvasPos, { mondayItemId: item.id, integrationId });
      data = {
        id: element.id,
        type: element.mondayTask.type,
        element: element.mondayTask as MondayTaskElement,
      };
    } else {
      const element = placeIntegrationElement(canvasPos, integrationId, integrationType, { itemId: item.id });
      data = {
        id: element.id,
        type: element.item.type,
        element: element.item as IntegrationItem,
      };
    }

    onAddElements([data], true, "drop-monday-item").then((ids) =>
      window.dispatchEvent(new CustomEvent(EVT_ELEMENT_DROP, { detail: { x: canvasPos.x, y: canvasPos.y, ids } }))
    );
  }

  async function handleDropMondayItemOnElement(
    node: Konva.Node,
    itemData: { item: any; integrationType: IntegrationType; integrationId: string }
  ) {
    const sequenceId = `drop-monday-item-on-element-${node.attrs.id}`;

    const dataLayer = node.attrs.element.dataLayer;
    const existingIntegration = dataLayer?.integrations?.[itemData.integrationId];
    const identicalIntegration =
      Object.keys(existingIntegration ?? {}).length === 1 && existingIntegration?.[itemData.item.id];

    const applyIntegration = async (replace = false, addField = true) => {
      await addIntegrationToElement(node.attrs.id, itemData.integrationId, itemData.item.id, {
        replace,
        sequenceId,
        clearText: !replace && addField,
      });
      if (replace || !addField) return; // We don't need to add the field if we're replacing an existing field.

      await addFieldToElement(node.attrs.id, node.attrs.element, itemData.integrationId, itemData.item.id, "name", {
        sequenceId,
        fieldValue: itemData.item.name,
        textProperties: newElementData?.textBlock,
      });
    };

    if (existingIntegration && !identicalIntegration) {
      showModal(ReplaceIntegrationItemModal, async ({ replace }) => {
        await applyIntegration(replace);
      });
    } else {
      tracking.trackAnalyticsEvent("monday-item-dropped-on-element", {
        elementId: node.attrs.id,
        elementType: node.attrs.element.type,
        itemId: itemData.item.id,
        integrationId: itemData.integrationId,
      });
      await applyIntegration(false, !identicalIntegration);
    }
  }

  function onAnchorClick(mode: "click" | "drag", node: Konva.Node, side: string, pos: Point) {
    if (mode === "drag") {
      startConnectorBuilder(pos, side, node);
      return;
    }

    const nearbyAnchors = getNearbyAnchorPoints(pos, side, node);
    if (nearbyAnchors.length > 0) {
      const [, index] = MathUtils.findClosestPoint(pos, nearbyAnchors);
      const endAnchor = nearbyAnchors[index];
      const onAddConnectors = (elements: NewElementData[]) => onAddElements(elements, true, "anchor-click");
      const connectorData = {
        strokeWidth: sizes.connector.strokeWidth.default,
        stroke: isCIM ? "#A6AAAD" : consts.COLOR_PALETTE[0],
        ...newElementData.connector,
      };

      ConnectorUtils.createConnector(
        { pos, shapeId: node.id(), side },
        { pos: { x: endAnchor.x, y: endAnchor.y }, shapeId: endAnchor.id, side: endAnchor.side },
        syncService,
        createElements,
        onAddConnectors,
        connectorData
      );
      setTimeout(() => setStageState((previous) => ({ ...previous, ghostElement: null })), 10);
      return;
    }

    if (!consts.FLOWCHART_SUPPORTED_ELEMENTS.includes(node.attrs.element.type)) {
      startConnectorBuilder(pos, side, node);
      return;
    }

    // For shapes and sticky notes create next elements for easy flowcharting
    const nextLocation = getNextElementLocation(node, side);
    if (nextLocation === null) {
      startConnectorBuilder(pos, side, node);
      return;
    }

    const origin = node.getClientRect({ relativeTo: layerRef.current as any, skipShadow: true });
    const frameId = node.attrs.element.frameId;
    const offsetX = nextLocation.x - origin.x;
    const offsetY = nextLocation.y - origin.y;
    const endElement = copyElement(node.id(), node.attrs.element, offsetX, offsetY);
    const endElementId = dbKey(endElement.id, endElement.type);
    endElement.element.flags = off(endElement.element.flags, Flags.CreatedBySystem); // Do not copy system flags
    endElement.element.attachedConnectors = {}; // Do not remember previous shape's connectors
    if (endElement.element.type !== consts.CANVAS_ELEMENTS.ICON) endElement.element.text = "";
    endElement.element.containerId = null;
    endElement.element.groupId = null;
    endElement.element.groupHistory = [];
    endElement.element.frameId = null;
    const connectorData = {
      strokeWidth: sizes.connector.strokeWidth.default,
      stroke: isCIM ? "#A6AAAD" : consts.COLOR_PALETTE[0],
      ...newElementData.connector,
    };

    const newConnector = ConnectorUtils.getNewConnector(
      { pos, shapeId: node.id(), side },
      { pos, shapeId: endElementId, side: MathUtils.getOppositeSide(side) },
      createElements,
      connectorData
    );

    const elementsToAdd = [newConnector, endElement]; // Order is important

    // If the element is derived, copy all elements in the derived group
    const derivedId = getDerivedGroupId(node);
    if (derivedId) {
      const _newGroupId: string = `derived:${newGroupId()}`;
      endElement.element.groupId = _newGroupId;
      const groupElements = getSelectableNodes((n) => getDerivedGroupId(n) === derivedId && n.id() !== node.id());
      const newIdsMap: Record<string, string> = {};

      for (const groupElement of groupElements) {
        const copy = copyElement(groupElement.id(), groupElement.attrs.element, offsetX, offsetY);
        copy.element.flags = off(copy.element.flags, Flags.CreatedBySystem); // Do not copy system flags
        copy.element.groupId = _newGroupId;
        copy.element.groupHistory = [];
        copy.element.derivedElementIds = [endElementId];

        const parsedDataId = parseDerivedDataId(copy.element.text);
        if (parsedDataId) copy.element.text = `$\{${endElementId}:${parsedDataId.keyPath}}`;

        elementsToAdd.push(copy);
        newIdsMap[groupElement.id()] = `${copy.element.type}-${copy.id}`;
      }

      const newElementMapping = Object.entries(endElement.element.dataLayer?.elementMapping ?? {}).reduce(
        (acc, [key, value]) => {
          if (newIdsMap[value as string]) {
            acc[key] = newIdsMap[value as string];
          }
          return acc;
        },
        {} as Record<string, string>
      );
      endElement.element.dataLayer = { ...endElement.element.dataLayer, elementMapping: newElementMapping };
    }

    onAddElements(elementsToAdd, true, "anchor-click").then((newElementIds) => {
      if (newElementIds.length > 0) {
        const attachedConnector = { lineId: newElementIds[0] };
        ConnectorUtils.attachConnector(syncService, node.id(), "", newElementIds[0], attachedConnector, 0);
        ConnectorUtils.attachConnector(syncService, newElementIds[1], "", newElementIds[0], attachedConnector, 1);
        if (!derivedId) selectNewElements([newElementIds[1]]);

        // Only attach to the frame if at least partially inside it
        if (frameId) {
          const { x, y } = endElement.element;
          const centerRect = { x, y, width: 1, height: 1 };

          const frameNodes = getSelectableNodes((n) => n.attrs.isFrame && !n.attrs.isFrameTitle && n.id() == frameId);
          if (frameNodes.length === 0) return;

          const frameRect = frameNodes[0].getClientRect({ relativeTo: layerRef.current as any, skipShadow: true });
          if (frameRect && Konva.Util.haveIntersection(centerRect, frameRect)) {
            syncService?.mutate.attachToFrame({ ids: [newElementIds[1]], frameId });
          }
        }
      }
    });

    setTimeout(() => setStageState((previous) => ({ ...previous, ghostElement: null })), 10);
  }

  function onAnchorHover(node: Konva.Node, side: string, pos: Point) {
    if (stageState?.ghostElement !== null) {
      return;
    }

    const nearbyAnchors = getNearbyAnchorPoints(pos, side, node);
    if (nearbyAnchors.length > 0) {
      const [, index] = MathUtils.findClosestPoint(pos, nearbyAnchors);
      const endAnchor = nearbyAnchors[index];

      setStageState((previous) => ({
        ...previous,
        ghostElement: {
          kind: "Connector",
          point1: pos,
          point2: endAnchor,
          start: { pos, shapeId: node.id(), side },
          end: { pos: { x: endAnchor.x, y: endAnchor.y }, shapeId: endAnchor.id, side: endAnchor.side },
          mouseDownTime: Date.now(),
        },
      }));
      return;
    }

    if (!consts.FLOWCHART_SUPPORTED_ELEMENTS.includes(node.attrs.element.type)) {
      return;
    }

    const nextLocation = getNextElementLocation(node, side);
    if (nextLocation === null) {
      return;
    }

    drawConnectedGhostElement(node, pos, side, nextLocation);
  }

  function startConnectorBuilder(pos: Point, side: string, node: Konva.Node) {
    setStageState((previous) => ({
      ...previous,
      ghostElement: {
        kind: "Connector",
        start: { pos, shapeId: node.id(), side },
        end: { pos },
        mouseDownTime: Date.now(),
      },
    }));
    mouseState.current.capture = true;
    setTimeout(() => setCursorType(CursorType.connector));
  }

  function drawConnectedGhostElement(node: Konva.Node, pos: Point, side: string, nextLocation: IRect) {
    if (!consts.FLOWCHART_SUPPORTED_ELEMENTS.includes(node.attrs.element.type)) {
      return;
    }

    const { x: originX, y: originY } = node.getClientRect({ relativeTo: layerRef.current as any, skipShadow: true });
    const { x: centerX, y: centerY, width, height, scaleX, scaleY } = node.attrs.element;
    const offsetX = nextLocation.x - originX;
    const offsetY = nextLocation.y - originY;

    let newGhostElement: GhostElement;
    switch (node.attrs.element.type) {
      case consts.CANVAS_ELEMENTS.STICKY_NOTE: {
        const { fill, width, height, scaleX, scaleY } = node.attrs.element;
        newGhostElement = {
          kind: "Note",
          point1: { x: nextLocation.x + (width * scaleX) / 2, y: nextLocation.y + (height * scaleY) / 2 },
          point2: { x: nextLocation.x + (width * scaleX) / 2, y: nextLocation.y + (height * scaleY) / 2 },
          data: { ...newElementData, stickyNote: { color: fill, width, height, scaleX, scaleY } },
        };
        break;
      }
      case consts.CANVAS_ELEMENTS.ICON: {
        const { width, height, scaleX, scaleY, href, text } = node.attrs.element;
        newGhostElement = {
          kind: "Icon",
          point: { x: nextLocation.x + width / 2, y: nextLocation.y + height / 2 },
          data: { href, width, height, scaleX, scaleY, text },
        };
        break;
      }
      default: {
        // We should only get here for Shapes! All other cases need to be handled above

        const isSvgShape = node.attrs.element.type === consts.CANVAS_ELEMENTS.SHAPE;
        const oldShapeType = isSvgShape ? null : node.attrs.element.type;
        const { border, fill, dash, subtype, type, strokeWidth, rotate } = node.attrs.element;

        // HACK: Ghost element cannot draw rotated shapes so using the params
        // from getClientRect will draw the bounding box instead of the actual shape.
        // Instead, we compute the 2 corners of the shape using the center X and Y
        let topLeft: Point, bottomRight: Point;

        if (!isSvgShape && (oldShapeType == consts.SHAPES.RECT || oldShapeType == consts.SHAPES.RECT_ROUNDED)) {
          // For some odd reason old rects are calculated differently.
          topLeft = { x: centerX + offsetX, y: centerY + offsetY };
          bottomRight = { x: centerX + width * scaleX + offsetX, y: centerY + height * scaleY + offsetY };
        } else {
          topLeft = { x: centerX - (width * scaleX) / 2 + offsetX, y: centerY - (height * scaleY) / 2 + offsetY };
          bottomRight = { x: centerX + (width * scaleX) / 2 + offsetX, y: centerY + (height * scaleY) / 2 + offsetY };
        }

        newGhostElement = {
          kind: "Shape",
          point1: topLeft,
          point2: bottomRight,
          isCentered: false,
          isEquiSided: false,
          data: {
            ...newElementData,
            shape: { border, dash, subtype, type, strokeWidth, color: fill, override: { rotate } },
          },
        };
      }
    }

    const endSide = MathUtils.getOppositeSide(side);
    const endPoint = AnchorUtils.getAnchorPointsFromNode(node)[endSide as keyof AnchorUtils.StandardAnchorPoints];
    endPoint.x += offsetX;
    endPoint.y += offsetY;

    const newConnector: GhostConnector = {
      kind: "Connector",
      point1: pos,
      point2: endPoint,
      start: { pos, shapeId: node.id(), side },
      end: { pos: endPoint, side: endSide },
      mouseDownTime: Date.now(),
    };

    setStageState((previous) => ({
      ...previous,
      ghostElement: {
        kind: "MultipleGhosts",
        ghosts: [newConnector, newGhostElement!],
      },
    }));
  }

  function getNearbyAnchorPoints(pos: Point, side: string, node: Konva.Node) {
    // Some elements (like tasks) don't have width or height.
    // In that case we fallback to the bounding box dimensions.
    const { width: clientWidth, height: clientHeight } = node.getClientRect();
    const { width = clientWidth, height = clientHeight, scaleX = 1, scaleY = 1 } = node.attrs.element;
    const searchFactor = 3.25;
    let searchHeight = Math.max(height * scaleY, 100);
    let searchWidth = Math.max(width * scaleX, 100);

    let searchOrigin = { x: 0, y: 0 };
    switch (side) {
      case "left": {
        searchOrigin = { x: pos.x - width * scaleX * searchFactor, y: pos.y - searchHeight / 2 };
        searchWidth *= searchFactor;
        break;
      }
      case "right": {
        searchOrigin = { x: pos.x, y: pos.y - searchHeight / 2 };
        searchWidth *= searchFactor;
        break;
      }
      case "top": {
        searchOrigin = { x: pos.x - searchWidth / 2, y: pos.y - height * scaleY * searchFactor };
        searchHeight *= searchFactor;
        break;
      }
      case "buttom": {
        searchOrigin = { x: pos.x - searchWidth / 2, y: pos.y };
        searchHeight *= searchFactor;
        break;
      }
    }
    const searchBox: IRect = { ...searchOrigin, width: searchWidth, height: searchHeight };

    // Get connectables nodes
    // 1 - Within search box
    // 2 - That do not intersect origin element
    // 3 - That are not already connected to origin element (except by hidden connectors)
    let connectables = getConnectableNodes(
      (i: Konva.Node) =>
        Konva.Util.haveIntersection(searchBox, i.getClientRect({ relativeTo: layerRef.current as any })) &&
        !Konva.Util.haveIntersection(
          node.getClientRect({ relativeTo: layerRef.current as any }),
          i.getClientRect({ relativeTo: layerRef.current as any })
        ) &&
        !ConnectorUtils.areNodesAttached(
          node,
          i,
          (id) => layerRef.current?.find((n: any) => n.id() === id && !n.hidden).toArray().length === 1
        )
    );

    // Filter out derived values, we don't want to connect to them.
    connectables = connectables.filter(
      (connectable) => (connectable.attrs.element.derivedElementIds?.length ?? 0) === 0
    );

    //Get anchor points from nearby elements within search box
    const anchorPoints = connectables.flatMap((element) => {
      const anchors = AnchorUtils.getAnchorPointsFromNode(element);
      return Object.entries(anchors)
        .map(([key, value]) => ({
          id: element.id(),
          side: key,
          inRange: MathUtils.isPointInRect(value, searchBox),
          ...value,
        }))
        .filter((anchor) => anchor.inRange);
    });

    return anchorPoints;
  }

  function getNextElementLocation(node: Konva.Node, side: string) {
    const { width, height, scaleX = 1, scaleY = 1 } = node.attrs.element;
    const origin = node.getClientRect({ relativeTo: layerRef.current as any, skipShadow: true });
    let nextLocation: IRect | null = { ...origin };

    switch (side) {
      case "left": {
        nextLocation.x -= width * scaleX * 2;
        nextLocation = getNextVerticalPosition(nextLocation, node);
        break;
      }
      case "right": {
        nextLocation.x += width * scaleX * 2;
        nextLocation = getNextVerticalPosition(nextLocation, node);
        break;
      }
      case "top": {
        nextLocation.y -= height * scaleY * 2;
        nextLocation = getNextHorizontalPosition(nextLocation, node);
        break;
      }
      case "buttom": {
        nextLocation.y += height * scaleY * 2;
        nextLocation = getNextHorizontalPosition(nextLocation, node);
        break;
      }
    }

    return nextLocation;
  }

  function getNextVerticalPosition(origin: IRect, node: Konva.Node, maxPositions = 5): IRect | null {
    const { height, scaleY = 1 } = node.attrs.element;
    let lowerY, upperY;
    let spotIsAvailable = false;

    for (let index = 0; index < maxPositions; index++) {
      lowerY = origin.y + height * scaleY * 2 * index;
      upperY = origin.y - height * scaleY * 2 * index;

      for (const y of [lowerY, upperY]) {
        spotIsAvailable =
          getSelectableNodes(
            (n: Konva.Node) =>
              Konva.Util.haveIntersection({ ...origin, y }, n.getClientRect({ relativeTo: layerRef.current as any })) &&
              n.attrs.element.type !== consts.CANVAS_ELEMENTS.FRAME &&
              n.attrs.element.type !== consts.CANVAS_ELEMENTS.CONNECTOR
          ).length === 0;
        if (spotIsAvailable) return { ...origin, y };
      }
    }

    return null;
  }

  function getNextHorizontalPosition(origin: IRect, node: Konva.Node, maxPositions = 5): IRect | null {
    const { width, scaleX = 1 } = node.attrs.element;
    let leftX, rightX;
    let spotIsAvailable = false;

    for (let index = 0; index < maxPositions; index++) {
      rightX = origin.x + width * scaleX * 2 * index;
      leftX = origin.x - width * scaleX * 2 * index;

      for (const x of [rightX, leftX]) {
        spotIsAvailable =
          getSelectableNodes(
            (n: Konva.Node) =>
              Konva.Util.haveIntersection({ ...origin, x }, n.getClientRect({ relativeTo: layerRef.current as any })) &&
              n.attrs.element.type !== consts.CANVAS_ELEMENTS.FRAME &&
              n.attrs.element.type !== consts.CANVAS_ELEMENTS.CONNECTOR
          ).length === 0;
        if (spotIsAvailable) return { ...origin, x };
      }
    }

    return null;
  }

  function onConvertToMondayItem(itemType: "item" | "subitem", nodes: Konva.Node[], anchor: DOMRect) {
    setCreateIntegrationItemMenuState({
      anchor,
      nodes: nodes,
      isSubitem: itemType === "subitem",
    });
  }

  return (
    <div ref={drop}>
      <Stage
        ref={stageRef}
        width={window.innerWidth}
        height={window.innerHeight}
        x={posScale.x}
        y={posScale.y}
        scaleX={posScale.scale || 1}
        scaleY={posScale.scale || 1}
        onWheel={handleWheelScroll}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        style={stageStyle()}
      >
        <GuiSystemProvider widgetsMap={widgetsMap}>
          <HotkeysProvider>
            <CanvasProvider appState={appState} documentId={board?.documentId} pusherChannel={pusherChannel}>
              <OrgchartFileUploaderProvider openModal={openModal}>
                <IntegrationsProvider documentId={board!.documentId} pusherChannel={pusherChannel}>
                  <MondayItemManagerProvider documentId={board!.documentId} pusherChannel={pusherChannel}>
                    <Layer name={CANVAS_STAGE_LAYERS.ELEMENTS} ref={layerRef}>
                      {renderFramesBackground()}
                      {renderElements()}
                      {isCIM && <TemplateGhostPreview />}
                      {cursorType == CursorType.connector && (
                        <ConnectorBuilder
                          stage={stageRef.current}
                          layer={layerRef.current}
                          initialState={
                            stageState?.ghostElement?.kind == "Connector" ? stageState.ghostElement.start : null
                          }
                          mouseDownTime={
                            stageState?.ghostElement?.kind == "Connector" ? stageState.ghostElement.mouseDownTime : 0
                          }
                          newElementData={{
                            strokeWidth: sizes.connector.strokeWidth.default,
                            stroke: isCIM ? "#A6AAAD" : consts.COLOR_PALETTE[0],
                            ...newElementData.connector,
                          }}
                          onCreate={(newElementData, ghost) => {
                            const onAddConnectors = (elements: NewElementData[]) =>
                              onAddElements(elements, true, "connector-builder");

                            ConnectorUtils.createConnector(
                              ghost.start,
                              ghost.end,
                              syncService,
                              createElements,
                              onAddConnectors,
                              newElementData
                            )?.then((newElementIds) => {
                              selectNewElements(newElementIds);

                              // Allow selecting the next shape if the connector ends on empty space
                              const onElements = getCanvasElementsInAbsolutePoint(ghost.end.pos).filter(
                                (n) =>
                                  n.attrs.type !== consts.CANVAS_ELEMENTS.FRAME &&
                                  n.attrs.type !== consts.CANVAS_ELEMENTS.CONNECTOR
                              );
                              if (ghost.start.shapeId && !ghost.end.shapeId && onElements.length === 0) {
                                const pointerPosition = stageRef.current?.getPointerPosition() ?? { x: 0, y: 0 };
                                // Without the setTimeout it hides the menu instantly
                                setTimeout(() =>
                                  setNextElementMenuState({
                                    anchor: new DOMRect(pointerPosition.x, pointerPosition.y),
                                    originNode: getNodeById(ghost.start.shapeId),
                                    connectorId: newElementIds[0],
                                    connectorPoints: { start: ghost.start.pos, end: ghost.end.pos },
                                  })
                                );
                              }
                            });
                            setCursorType(CursorType.pointer);
                          }}
                        />
                      )}

                      {showFileUploadModal && (
                        <Html transform={false}>
                          <FileUploader
                            uppy={uppy}
                            onUpload={(files) => {
                              // Track the upload events
                              trackUploadFiles({ files, boardDocumentId: board?.documentId });

                              // At this point the files to upload have been added to uppy internal state.
                              // We'll get them out and pipe them through our regular flow
                              // That requires us to remove them from uppy first
                              const filesData = files.map((x) => x.data as File);
                              for (const file of files) uppy.removeFile(file.id);
                              performUploadFromHook(filesData);
                              setShowFileUploadModal(false);
                              setCursorType(CursorType.pointer);
                            }}
                            onClose={() => {
                              setShowFileUploadModal(false);
                              uppy.reset();
                              setCursorType(CursorType.pointer);
                              setShowSubmenu(false);
                            }}
                          />
                        </Html>
                      )}
                    </Layer>
                    <Layer
                      name={CANVAS_STAGE_LAYERS.OVERLAY_ELEMENT_BOUNDS}
                      ref={elementBoundsLayerRef}
                      listening={false}
                      style={{ pointerEvents: "none" }}
                      opacity={1}
                    ></Layer>
                    <Layer name={CANVAS_STAGE_LAYERS.OVERLAY} ref={overlayRef}>
                      {cursorType != CursorType.move &&
                        renderGhostElement(stageState?.ghostElement, cursorType != CursorType.connector)}
                      <Rect ref={selectionRef} fill="#00A1FF1A" stroke="#00A1FF" strokeWidth={1} listening={false} />
                      <Rect
                        ref={selectionBoundsRef}
                        x={selectionBoundsRef.current?.x}
                        y={selectionBoundsRef.current?.y}
                        width={selectionBoundsRef.current?.width}
                        height={selectionBoundsRef.current?.height}
                        stroke="dodgerblue"
                        strokeWidth={1 / (stageRef.current?.scaleX() ?? 1)} // Adjust stroke width based on zoom
                        listening={false} // Don't interfere with mouse events
                        perfectDrawEnabled={false} // Optimize rendering
                      />
                      {stageState?.connectorAnchorPoints &&
                        stageState.connectorAnchorPoints.points.map((anchorPoint, index) => {
                          //TODO: move this to a new file
                          return (
                            <Circle
                              x={anchorPoint.x}
                              y={anchorPoint.y}
                              key={index}
                              radius={connectorConsts.ConnectorSnapPointRadius / posScale.scale}
                              fill={index == stageState.connectorAnchorPoints?.highlight ? "#1973FF" : "white"}
                              stroke={index == stageState.connectorAnchorPoints?.highlight ? "white" : "#1973FF"}
                              strokeWidth={2 / posScale.scale}
                            />
                          );
                        })}
                      <SnappingTransformer
                        ref={transformerRef}
                        visible={internalSelectionValue.length == 0 && !tableSelection?.cells?.length}
                        onTransformStart={(e) => {
                          const theTransformer = e.currentTarget as Konva.Transformer;
                          const nodes = theTransformer.nodes();
                          const nodesWithoutContainer = nodes.filter((node) => !node.attrs.element.containerId);
                          const add = new Set<string>();
                          for (const node of nodesWithoutContainer) {
                            if (node.attrs.type == "table" && node.attrs.containedIds?.length) {
                              node.attrs.containedIds.forEach((i: string) => add.add(i));
                            }
                          }
                          for (const node of nodesWithoutContainer) {
                            add.delete(node.id());
                          }
                          const extras = layerRef.current?.find((node: any) => add.has(node.id())).toArray() || [];
                          // this should happen only when using the corner anchors, and aspect-ratio
                          // should be kept for nodes and table
                          // when transforming the table from the side-anchors, Miro's table actualy
                          // resizes columns and rows, are stops at child-element bounding-boxes
                          theTransformer.nodes(extras.concat(nodesWithoutContainer));
                        }}
                        newTransformerDesign={newTransformerDesign}
                        onTransformEnd={onMutationEndCb}
                        showSnapGuidelines={true}
                        snapToGuidelines={true}
                        anchorDblClick={onTransformerAnchorDoubleClick}
                        setResizingElements={setResizingElements}
                      />

                      {showShapeSocketsOnElement !== null && (
                        <ShapeSockets
                          id={showShapeSocketsOnElement}
                          elementsLayer={layerRef.current}
                          scale={posScale.scale}
                          onCreateConnector={onAnchorClick}
                          onAnchorHoverStart={onAnchorHover}
                          onAnchorHoverEnd={() =>
                            setStageState((previous) => {
                              if (previous?.ghostElement?.kind == "SelectionRect") return previous;
                              return { ...previous, ghostElement: null };
                            })
                          }
                        />
                      )}
                      {showElementToolbar && (
                        <ElementsToolbar
                          customColors={customColors}
                          elements={elements}
                          tableCells={tableCells}
                          documentId={board?.documentId}
                          elementsLayer={layerRef.current}
                          setNodePosition={(id, pos) => {
                            needToRefreshBoundsRef.current = boundsRefreshOperation.selectNewElements;
                            onDragElement(id, pos);
                          }}
                          mutateElementsProperties={mutateWithWorkaroundForTexts}
                          onMutationEnd={onMutationEnd}
                          doGroup={allowGrouping ? handleGroup : undefined}
                          doUngroup={singleGroupIsSelected ? handleUngroup : undefined}
                          doLock={lockElements}
                          doUnlock={unlockElements}
                          onContextMenu={(rect: any, nodes: Konva.Node[], lockedElements: boolean) => {
                            setContextMenuState({
                              anchor: rect,
                              nodes: nodes,
                              enablePaste: false,
                              enableGroup: allowGrouping && !lockedElements,
                              enableUngroup: singleGroupIsSelected && !lockedElements,
                              enableComments: !!cordAuthToken,
                              enableRelativeMove: nodes.length == 1 && hasIntersects(nodes[0]),
                              theme: "dark",
                              margin: 15,
                              openedFrom: "toolbar",
                            });
                          }}
                          selectedIdsRef={selectedIdsRef}
                          onSelectionChanged={() => {
                            enableTransformer();
                            enableGroupsBounds();
                            enableElementBounds();
                          }}
                          onCommentCreate={cordAuthToken ? attachCommentToElement : null}
                          reflect={syncService?.getReplicache()}
                          undoRedoStack={undoRedoStack}
                          showUpgradeModal={showUpgradeModal}
                          onError={onError}
                          onOpenDataLayerMenu={setDataLayerMenuState}
                          onOpenRulesMenu={setRulesEditorState}
                          onConvertToMondayItem={onConvertToMondayItem}
                        />
                      )}

                      {highlightRect && (
                        <Rect
                          {...highlightRect}
                          perfectDrawEnabled={false}
                          stroke="blue"
                          strokeWidth={2}
                          cornerRadius={5}
                          strokeScaleEnabled={false}
                        />
                      )}
                      {contextMenuState && (
                        <ContextMenuElement
                          {...contextMenuState}
                          onItemSelection={handleContextMenuOperation}
                          thumbnailId={thumbnailId}
                          allowPdfExport
                          newStyle={isCIM && newFloatersStyle}
                        />
                      )}
                      {nextElementMenuState && (
                        <NextElementMenu
                          {...nextElementMenuState}
                          onAddElement={(nextElement, connectorId, start, end, mode = "add") => {
                            // This function assumes the connector originated from an anchor point of an existing element.
                            setNextElementMenuState(null);
                            if (!nextElement) return;

                            const direction = MathUtils.inferMoveDirection(start, end);
                            const endAnchorSide = MathUtils.getOppositeSide(direction);

                            let newElement: NewElementData;
                            let newElementType: TypeCanvasElement;
                            let offsetX = 0,
                              offsetY = 0;
                            if (mode === "add") {
                              // Add a new element based on selection.
                              nextElement = nextElement as NextElementMenuItem;
                              const { library, group, item } = nextElement.elementInfo;
                              const libraryPath = `${library}:${group}:${item}`;
                              switch (nextElement.elementType) {
                                case "shape": {
                                  newElement = createShape(
                                    {
                                      ...newElementData.shape,
                                      ...nextElement.elementData,
                                      libraryPath,
                                    },
                                    end
                                  )[0];
                                  newElementType = consts.CANVAS_ELEMENTS.SHAPE;
                                  offsetX = newElement.element.x;
                                  offsetY = newElement.element.y;
                                  break;
                                }

                                case "icon": {
                                  newElement = createIcon(
                                    {
                                      ...newElementData.icon,
                                      ...nextElement.elementData,
                                      libraryPath,
                                    },
                                    end
                                  )[0];
                                  newElementType = consts.CANVAS_ELEMENTS.ICON;
                                  offsetX = newElement.element.x + newElement.element.width / 2;
                                  offsetY = newElement.element.y + newElement.element.height / 2;
                                  break;
                                }

                                // TODO: These cases are old. Do we want to support them in the new menu?
                                case "textBlock": {
                                  newElement = createTextBlock(newElementData.textBlock, end)[0];
                                  newElementType = consts.CANVAS_ELEMENTS.TEXT_BLOCK;
                                  offsetX = newElement.element.x;
                                  offsetY = newElement.element.y;
                                  break;
                                }
                                case "stickyNote": {
                                  newElement = createStickyNote(newElementData.stickyNote, end)[0];
                                  newElementType = consts.CANVAS_ELEMENTS.STICKY_NOTE;
                                  offsetX = newElement.element.x + newElement.element.width / 2;
                                  offsetY = newElement.element.y + newElement.element.height / 2;
                                  break;
                                }
                                default: {
                                  console.warn("Selected unsupported element type on next element menu.");
                                  return;
                                }
                              }
                              const anchorPoints = AnchorUtils.getAnchorPoints(newElementType, newElement.element);
                              const anchor = (anchorPoints as any)[endAnchorSide];
                              newElement.element.x += offsetX - anchor.x;
                              newElement.element.y += offsetY - anchor.y;
                            } else {
                              // Copy origin element.
                              nextElement = nextElement as Konva.Node;
                              const anchor = (AnchorUtils.getAnchorPointsFromNode(nextElement) as any)[endAnchorSide];
                              const offsetX = end.x - anchor.x;
                              const offsetY = end.y - anchor.y;
                              newElement = copyElement(nextElement.id(), nextElement.attrs.element, offsetX, offsetY);
                            }

                            const connector = getSelectableNodes((i) => i.id() == connectorId)[0];
                            onAddElements([newElement], true, "next-element-menu").then((newElementIds) => {
                              if (newElementIds.length > 0) {
                                const attachedConnector = { lineId: connectorId };
                                ConnectorUtils.attachConnector(
                                  syncService,
                                  newElementIds[0],
                                  "",
                                  connectorId,
                                  attachedConnector,
                                  1
                                );

                                // Attach connector only modifies the shape, we need to modify the connector separately
                                const connectedShapes = [
                                  connector.attrs.element.connectedShapes[0],
                                  { id: newElementIds[0], type: "" },
                                ];
                                const anchorsMode = [connector.attrs.element.anchorsMode[0], endAnchorSide];
                                const props = new Map();
                                // eslint-disable-next-line react/prop-types
                                props.set(connectorId, { connectedShapes, anchorsMode });
                                onChangeElement(connectorId, props, props, false);
                                onElementsMutationEnded(props, props, undefined, false);
                                selectNewElements([newElementIds[0]]);
                                setEditingElementId(newElementIds[0]);
                              }
                            });
                          }}
                        />
                      )}
                      {!modifierKeys.ctrlOrCmd &&
                        stageState?.guidelines &&
                        Object.entries(stageState.guidelines).map(([key, value]) => (
                          <Line
                            key={key}
                            points={value}
                            strokeWidth={1 / posScale.scale}
                            stroke="#00A1FF"
                            dash={[3, 3]}
                          />
                        ))}
                      {!modifierKeys.ctrlOrCmd &&
                        stageState?.distanceGuidelines &&
                        stageState.distanceGuidelines.map((line, index) => (
                          <DistanceGuideline
                            key={index}
                            scale={posScale.scale}
                            coord={line.coord}
                            segment={line.segment}
                            direction={line.direction}
                          />
                        ))}
                      {!modifierKeys.ctrlOrCmd &&
                        stageState?.sizeGuidelines &&
                        stageState.sizeGuidelines.map((line, index) => (
                          <DistanceGuideline
                            key={index}
                            scale={posScale.scale}
                            coord={line.coord}
                            segment={line.segment}
                            direction={line.direction}
                            margin={0}
                            offset={15}
                          />
                        ))}
                      <CordProvider
                        clientAuthToken={cordAuthToken}
                        enableTasks={false}
                        cordScriptUrl={process.env.CORD_SDK_URL}
                      >
                        {renderCordComments(commentIds, activeCommentThread)}
                      </CordProvider>
                    </Layer>
                  </MondayItemManagerProvider>
                </IntegrationsProvider>
              </OrgchartFileUploaderProvider>
            </CanvasProvider>
          </HotkeysProvider>
        </GuiSystemProvider>
      </Stage>
      {createIntegrationItemMenuState && (
        <CreateMondayItemMenu
          {...createIntegrationItemMenuState}
          documentId={board?.documentId}
          onCreatedItems={() => {
            setCreateIntegrationItemMenuState(null);
          }}
          onDismiss={() => setCreateIntegrationItemMenuState(null)}
        />
      )}
      {dataLayerMenuState && (
        <DataEditorMenu
          {...dataLayerMenuState}
          pusherChannel={pusherChannel}
          documentId={board?.documentId}
          onDismiss={() => setDataLayerMenuState(null)}
        />
      )}
      {rulesEditorState && (
        <RulesEditorMenu
          {...rulesEditorState}
          documentId={board?.documentId}
          pusherChannel={pusherChannel}
          onDismiss={() => setRulesEditorState(null)}
        />
      )}
      <div
        style={{
          position: "fixed",
          top: "0",
          left: "0",
          width: "1px",
          height: "1px",
          overflow: "visible",
          transform: `translate(${posScale.x}px,${posScale.y}px) scale(${posScale.scale}) `,
          pointerEvents: "none",
          backgroundColor: "rgba(250,0,0,0.1)",
        }}
      >
        {!isReadOnly && (
          <VotingButtons syncService={syncService} elementsIds={elementsIds} movingOffsetRef={votingButtonsOffsetRef} />
        )}
      </div>
      {!!focusedElementId && getElementTypeForId(focusedElementId) == consts.CANVAS_ELEMENTS.TASK_CARD && (
        <TaskCardOpen
          id={focusedElementId}
          syncService={syncService}
          onDismiss={() => {
            setFocusedElementId(RESET);
          }}
          onDeleteElement={() => {
            onDeleteElements([focusedElementId]);
            if (selectedIdsRef.current.includes(focusedElementId)) {
              setSelectedIds(selectedIdsRef.current.filter((i) => i != focusedElementId));
              enableTransformer(true);
            }
            setFocusedElementId(RESET);
            enableTransformer(true); //recalculate transformer if this card is selected
          }}
          onChangeElement={(props, undoConfig) => {
            const previousMapProps = new Map<string, any>(Object.entries(undoConfig.previousProps ?? {}));
            const mapProps = new Map<string, any>(Object.entries(props));
            onChangeElement(focusedElementId, mapProps, previousMapProps, undoConfig.shouldAdd);
          }}
        />
      )}
      {dashboard}
      {showTemplateCreationLoaderModal && <CreatingTemplateIntegrationModal />}
    </div>
  );
}
